import React from "react";
import { push } from "connected-react-router";

import { toast } from "react-toastify";
import PptxGenJS from "pptxgenjs";
import numeral from "numeral";
import isEqual from "lodash/isEqual";

import Logo from "images/Aible_Logo/aible-light-bg.png";
import aibleHelix from "images/aibleHelix.png";
import aibleTransparentLogo from "images/Aible_Logo/aibleTransparentLogo.png";
import aibleCenterTransparentLogo from "images/Aible_Logo/aibleCenterTransparentLogo.png";
import aibleSmallTransparentLogo from "images/Aible_Logo/smallAibleTransparentLogo.png";

import * as ProjectAPI from "api/ProjectAPI";
import * as ScoreAPI from "api/ScoreAPI";
import * as DatasetAPI from "api/DatasetAPI";

import * as globalReducer from "containers/App/reducer";
import * as runReducer from "containers/Run/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import * as monitoringReducer from "containers/Monitoring/reducer";
import * as deployReducer from "containers/Deployments/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as paginationReducer from "containers/Pagination/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as errorsReducer from "containers/Errors/reducer";
import * as unifiedProjectReducer from "containers/UnifiedProject/reducer";
import * as consumerReducer from "containers/Consumer/reducer";
import * as transformReducer from "containers/Dataset/reducer";
import * as tasksReducer from "containers/Tasks/TasksReducer";
import * as viewReducer from "containers/View/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as reportReducer from "containers/Report/ReportReducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";

import { init as initMonitoring } from "containers/Monitoring/reducer";

import { isEnabled } from "utils/featureFlags";

import {
  ConfusionMatrix,
  CostBenefitMatrix,
  falseNegativeRate,
  falsePositiveRate,
  trueNegativeRate
} from "utils/stats";
import {
  AllocationMetric,
  ProjectOptimizer,
  ObjectiveMetric,
  SegmentMetric,
  FairnessStrategy
} from "utils/optimizer/ProjectOptimizer";
import * as Utils from "utils/misc";
import moment from "moment";
import {
  getBusinessObjective,
  getFairnessObjective,
  getBiasReduction,
  getFairnessToleranceBound
} from "containers/Setup/reducer";
import { ConditionUtils, ConditionType } from "types/condition";

import { AibleClientFactory } from "client/aible/factory";
import { fetchComments } from "containers/Comments/reducer";
import { loadJobMetadata } from "api/ModelAPI";
import CurrentState from "../CurrentState/CurrentState";

const PROJECT_CLIENT = AibleClientFactory.getInstance().getProjectClient();

export const INIT_OPTIMIZER = "project/INIT_OPTIMIZER";
export const REPLACE_PROJECTS = "project/REPLACE_PROJECTS";
export const UPDATE_KEY = "project/UPDATE_KEY";
export const UPDATE_SUB_KEY = "project/UPDATE_SUB_KEY";
export const UPDATE_SETTINGS = "project/UPDATE_SETTINGS";
export const UDPATE_DRILLDOWN = "project/UDPATE_DRILLDOWN";
export const UPDATE_SETTING_SCENARIO = "project/UPDATE_SETTING_SCENARIO";
export const RESET = "project/RESET";

export const PROJECT_MODES = {
  OVERALL: "Overall",
  DRILLDOWN: "Drilldown",
  VIEW_SEGMENTS: "View Subgroups"
};

export const AIBLE_VIEW_TYPE = {
  ADVANCED: "Aible Advanced",
  BUSINESS: "Aible Business",
  MONITORING: "Monitoring",
  DEPLOYMENT: "Deployment"
};

export const SCENARIO_STATUS = {
  DRAFT: "DRAFT",
  PENDING: "PENDING",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED"
};

export const BUSINESS_OBJECTIVE = {
  REVENUE: "Revenue",
  PROFIT: "Profit",
  MINIMIZE_RESOURCING: "MinimizeResourcing"
};

export const FAIRNESS_OBJECTIVE = {
  NONE: "None",
  ALLOCATION: "Equal Share",
  ACCURACY: "Equal Accuracy",
  TRUE_POSITIVE_RATE: "Equal Success Odds",
  FALSE_POSITIVE_RATE: "Equal Failure Odds",
  ODDS: "Equal Odds"
};

export const BIAS_REDUCTION = {
  IMPACT: "Impact",
  COUNT: "Count",
  EFFORT: "Effort"
};

export const BALANCER_OUTPUT = {
  TRUE_POSITIVE: "TRUE POSITIVE",
  ALL_POSITIVE: "ALL POSITIVE"
};

export const MIN_SEGMENT_ROWS = 50;
export const MAX_NUM_SEGMENTS = 5;
export const MAX_NUM_SEGMENTS_CONTINUOUS = 10;

const CURRENCY_UNIT_TYPES = {
  $: true,
  "£": true,
  "€": true,
  "": true
};

const initialState = {};

function projectReducer(state = initialState, action) {
  switch (action.type) {
    case INIT_OPTIMIZER:
      return {
        ...state,
        ...action.data
      };
    case REPLACE_PROJECTS:
      return {
        ...action.data
      };
    case UPDATE_KEY:
      return {
        ...state,
        [action.key]: action.value
      };
    case UPDATE_SUB_KEY:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          [action.subKey]: action.value
        }
      };
    case UPDATE_SETTINGS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          settings: {
            ...state[action.key].settings,
            [action.subKey]: action.value
          }
        }
      };
    case UDPATE_DRILLDOWN:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          settings: {
            ...state[action.key].settings,
            ["innerId"]: action.innerId,
            ["runId"]: action.runId,
            ["drilldownValue"]: action.drilldownValue
          }
        }
      };
    case UPDATE_SETTING_SCENARIO:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          settings: {
            ...state[action.key].settings,
            ["scenarios"]: action.scenarios,
            ["originalScenarios"]: action.originalScenarios,
            ["scenarioStats"]: action.scenarioStats,
            ["viewingIndex"]: action.viewingIndex
          }
        }
      };
    case RESET:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

/* Selectors */
export function isShowSharingAndAssumptions() {
  return isEnabled("show-sharing-and-assumptions");
}

export function getBase(state) {
  return state.project;
}

export function getProject(state, id) {
  return getBase(state)[id];
}

export function getProjectDatasetSuccess(state, id) {
  const runIds = getProjectSegmentRunIds(state, id, false);

  for (let i = 0; i < runIds.length; i++) {
    const currRunId = runIds[i];
    const datasetOutcome = setupReducer.getOutcomeFromDataset(state, currRunId);
    const successValue = setupReducer.getSuccessValue(state, currRunId);

    if (datasetOutcome && successValue) {
      return {
        outcome: datasetOutcome,
        success: successValue
      };
    }
  }
}

export function getProjectSegments(state, id, includeOverall = true) {
  const segments = (getProject(state, id) || {}).segments || [];

  if (includeOverall || segments.length == 1) {
    return segments;
  }

  return segments.filter(({ conditions }) => conditions.length > 0);
}

export function getProjectSegmentTasks(state, id, projectSegmentId) {
  const project = Object.values(getBase(state)).find(
    (project) => project?.segments?.find((s) => s.id == projectSegmentId) !== undefined
  );

  return project?.segments?.find((s) => s.id == projectSegmentId)?.tasks ?? [];
}

export function getTaskProjects(state, taskId) {
  return Object.values(getBase(state)).filter((project) =>
    project?.segments?.flatMap((s) => s.tasks)?.find((t) => t.id == taskId)
  );
}

export function getProjectScenario(state, scenarioId) {
  const project = Object.values(getBase(state)).find(
    (project) => project?.scenarios?.find((s) => s.id == scenarioId) !== undefined
  );

  return project?.scenarios?.find((s) => s.id == scenarioId);
}

export function getIsForecastProject(state, id) {
  const runIds = id === "new" ? ["new"] : getAllJobIdsFromOptimizer(state, id) || [];
  return runIds.some((runId) => setupReducer.getIsForecast(state, runId));
}

export function getProjectAccountId(state, id) {
  return (getProject(state, id) || {}).account_id;
}

export function getPermissions(state, id) {
  const project = getProject(state, id);
  return project && project.permissions;
}

export function getUserDeletePermission(state, id) {
  const permissions = getPermissions(state, id);
  return permissions && permissions.can_delete;
}

export function getUserEditPermission(state, id) {
  const permissions = getPermissions(state, id);
  return permissions && permissions.can_edit;
}

export function getUserManagePermissions(state, id) {
  const permissions = getPermissions(state, id);
  return permissions && permissions.can_manage_permissions;
}

export function getSegmentEditPermissions(state, id) {
  const project = getProject(state, id);
  if (!project || !getUserEditPermission(state, id)) {
    return false;
  }
  const rawSegments = project.segments || [];
  if (rawSegments.length < project.segment_count) {
    return false;
  }
  for (let i; i < rawSegments.length; i++) {
    const currSegment = rawSegments[i];
    if (!currSegment || !currSegment.permissions || !currSegment.permissions.can_edit) {
      return false;
    }
  }
  return true;
}

export function getSegmentManagePermissions(state, id) {
  const project = getProject(state, id);
  if (!project || !getUserManagePermissions(state, id)) {
    return false;
  }
  const rawSegments = project.segments || [];
  if (rawSegments.length < project.segment_count) {
    return false;
  }
  for (let i; i < rawSegments.length; i++) {
    const currSegment = rawSegments[i];
    if (!currSegment || !currSegment.permissions || !currSegment.permissions.can_manage_permissions) {
      return false;
    }
  }
  return true;
}

export function getProjectName(state, id) {
  const project = getProject(state, id) || {};
  return project.name;
}

export function getCreatedDate(state, id) {
  const project = getProject(state, id) || {};
  return project.created_date;
}

export function getProjectsArr(state, id) {
  return Object.keys(getBase(state)).map((k) => getBase(state)[k]);
}

export function getProjectMonitor(state, id) {
  const project = getProject(state, id) || {};

  return (project.monitors || [])[0];
}

export function getUniqueIdName(state, id) {
  const monitor = getProjectMonitor(state, id) || {};

  return monitor.unique_id_name;
}

export function getBusinessPredictionName(state, id) {
  const monitor = getProjectMonitor(state, id) || {};

  return monitor.business_prediction_name;
}

export function getTerminalConditions(state, id) {
  const monitor = getProjectMonitor(state, id) || {};

  return monitor.terminal_conditions || [];
}

export function getActivityConditions(state, id) {
  const monitor = getProjectMonitor(state, id) || {};

  return monitor.activity_conditions || [];
}

export function getOptimizer(state, id) {
  return getBase(state)[id] ? getBase(state)[id].settings : undefined;
}

export function getRunId(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).runId : undefined;
}

export function getDatasetId(state, id) {
  const runId = getRunId(state, id);
  const dataset = setupReducer.getDatasetFromSetup(state, runId);

  return dataset?.id;
}

export function getInnerId(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).innerId : undefined;
}

export function getProjectMode(state, id) {
  return getOptimizer(state, id) && getOptimizer(state, id).projectMode
    ? getOptimizer(state, id).projectMode
    : PROJECT_MODES.OVERALL;
}

export function getIsMonitorFeedback(state, id) {
  return getOptimizer(state, id) && getOptimizer(state, id).isMonitorFeedback
    ? getOptimizer(state, id).isMonitorFeedback
    : false;
}

export function getIsMonitorRetrain(state, id) {
  return getOptimizer(state, id) && getOptimizer(state, id).isMonitorRetrain
    ? getOptimizer(state, id).isMonitorRetrain
    : false;
}

export function getIsAFO(state, id) {
  return getOptimizer(state, id) && getOptimizer(state, id).isAFO ? getOptimizer(state, id).isAFO : false;
}

export function getIsSharing(state, id) {
  return getOptimizer(state, id) && getOptimizer(state, id).isSharing ? getOptimizer(state, id).isSharing : false;
}

export function getRunPlotType(state, id) {
  return getOptimizer(state, id) && getOptimizer(state, id).runPlotType
    ? getOptimizer(state, id).runPlotType
    : runReducer.GRAPH_TYPES.COUNTS;
}

export function getHasSplits(state, id) {
  if (id === undefined) {
    id = globalReducer.getCurrentProjectId(state);
  }
  const project = getProject(state, id);
  return project && project.segment_count > 1;
}

export function getEnableFairnessMetrics(state, id) {
  if (!isEnabled("fairness")) {
    return false;
  }
  const overallJobId = isNaN(id) ? id : getOverallRunId(state, id);
  return setupReducer.getEnableFairnessMetrics(state, overallJobId);
}

export function getRetrainDatasetId(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).retrainDatasetId : undefined;
}

export function getRetrainDataset(state, id) {
  const retrainDatasetId = getRetrainDatasetId(state, id);

  return retrainDatasetId !== undefined ? homeReducer.getDataset(state, retrainDatasetId) : undefined;
}

export function getIsNewFrontier(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).isNewFrontier : undefined;
}

export function getSegmentCount(state, id) {
  return (getProject(state, id) || {}).segment_count;
}

export function getIsRefreshing(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).isRefreshing : undefined;
}

export function getIsRetraining(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).isRetraining : undefined;
}

export function getIsLoadingLink(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).isLoadingLink : undefined;
}

export function getIsLoadingCompare(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).isLoadingCompare : undefined;
}

export function getProjectSegmentRunIds(state, projectId, includeOverall = true) {
  const segments = getProjectSegments(state, projectId, includeOverall);

  return segments && segments.map((s) => getSegmentRunId(state, projectId, s.id));
}

export function getProjectRuns(state, id) {
  return getNonOverallJobsFromOptimizer(state, id).map((jobId) => runReducer.getJob(state, jobId));
}

export function getIsTraining(state, id) {
  const runs = getProjectRuns(state, id);
  return runs ? Boolean(runs.find((elem) => elem.status !== "DONE" && elem.status !== "ERROR")) : true;
}

export function getIsError(state, id, runId) {
  const project = getProject(state, id);
  if (isNaN(id) || (runId && isNaN(runId))) {
    return false;
  }
  if (!project || project.status === "ERROR") {
    return true;
  }
  if (!isNaN(runId)) {
    if (getIsOverallSegment(state, id, runId)) {
      return false;
    }
    const run = runReducer.getJob(state, runId);
    const models = run && run.models;
    if (!run || run.status === "ERROR") {
      return true;
    }
    const errors = runReducer.getErrors(state, runId);
    const isForecast = setupReducer.getIsForecast(state, id);
    // Forecast
    // Show results even if only 1 model returns;
    const MIN_MODELS = isForecast ? 1 : 20;
    if (run.status === "DONE" && errors && errors.length && (!models || Object.keys(models).length < MIN_MODELS)) {
      return true;
    }
  }
  return false;
}

export function getHasErrors(state, id, runId) {
  if (getIsOverallSegment(state, id, runId)) {
    const runIds = getAllJobIdsFromOptimizer(state, id, false);
    const runIdErrors = runIds.map((rId) => getIsError(state, id, rId));
    const hasErrors = !runIdErrors.every((e) => !e);
    return hasErrors;
  }
  return getIsError(state, id, runId);
}

export function getProjectOptimizeBy(state, id) {
  const runIds = getNonOverallJobsFromOptimizer(state, id, false);

  const retOptimizedBy = {};

  runIds.forEach((runId) => {
    retOptimizedBy[runId] = {
      optimizeBy: setupReducer.getOptimizeBy(state, runId),
      optimizeByOptions: setupReducer.getOptimizeByOptions(state, runId),
      optimizeGoal: setupReducer.getOptimizeGoal(state, runId),
      optimizeGoalOptions: setupReducer.getOptimizeGoalOptions(state, runId),
      optimizeByCount: setupReducer.getNOpts(state, runId),
      optimizeByCountOptions: setupReducer.getNOptsOptions(state, runId)
    };
  });

  return retOptimizedBy;
}

export function updateProjectOptimizeBy(id, runId, optimizeBy) {
  return (dispatch, getState) => {
    const runIds = runId ? [runId] : getProjectSegmentRunIds(getState(), id, true);
    runIds.forEach((currRunId) => {
      if (setupReducer.getOptimizeBy(getState(), currRunId) !== optimizeBy) {
        dispatch(setupReducer.updateOptimizeBy(currRunId, optimizeBy));
      }
    });
  };
}

export function updateProjectOptimizeGoal(id, runId, optimizeGoal) {
  return (dispatch, getState) => {
    const runIds = runId ? [runId] : getProjectSegmentRunIds(getState(), id, true);
    runIds.forEach((currRunId) => {
      if (setupReducer.getOptimizeGoal(getState(), currRunId) !== optimizeGoal) {
        dispatch(setupReducer.updateOptimizeGoal(currRunId, optimizeGoal));
      }
    });
  };
}

export function updateProjectOptimizeByCount(id, runId, optimizedByCount) {
  return (dispatch, getState) => {
    const runIds = runId ? [runId] : getProjectSegmentRunIds(getState(), id, true);
    runIds.forEach((currRunId) => {
      if (setupReducer.getNOpts(getState(), currRunId) !== optimizedByCount) {
        dispatch(setupReducer.updateNOpts(currRunId, optimizedByCount));
      }
    });
  };
}

export function getShowShortListed(state, id) {
  return (getOptimizer(state, id) || {}).showShortListed;
}

export function getShowPending(state, id) {
  return (getOptimizer(state, id) || {}).showPending;
}

export function getSliderView(state, id) {
  return (getOptimizer(state, id) || {}).sliderView;
}

export function getHasInit(state, id) {
  return (getOptimizer(state, id) || {}).hasInit;
}

export function getIsLoadingProject(state, id) {
  return (getOptimizer(state, id) || {}).isLoading;
}

export function getIsLoadingSharing(state, id) {
  return (getOptimizer(state, id) || {}).isLoadingSharing;
}

export function getIsScoringFile(state, id) {
  return (getOptimizer(state, id) || {}).isScoringFile;
}

export function getIsSlowMode(state, id) {
  return (getOptimizer(state, id) || {}).isSlowMode;
}

export function getActiveSegment(state, id) {
  const project = getProject(state, id);

  const retSegments =
    project && project.segments
      ? project.segments.filter(
          (s) =>
            (s.job && project && project.settings && s.job.job_id === project.settings.runId) ||
            (s.history &&
              s.history.jobs &&
              s.history.jobs.reduce(
                (a, b) => a + (project.settings && b.job_id === project.settings.runId ? 1 : 0),
                0
              ) > 0)
        )
      : [];

  return retSegments.length > 0 ? retSegments[0] : undefined;
}

export function getInnerSegment(state, id) {
  const project = getProject(state, id);

  return project &&
    project.segments &&
    project.segments.filter((s) => s.job && s.job.job_id === project.settings.innerId).length > 0
    ? project.segments.filter((s) => s.job && s.job.job_id === project.settings.innerId)[0]
    : undefined;
}

export function getSegmentId(state, id, runIdOverride) {
  let retSegmentId = undefined;

  const runId = isFinite(runIdOverride) ? runIdOverride : getRunId(state, id);

  if (runId !== undefined) {
    const project = getProject(state, id);

    const retSegments =
      project && project.segments
        ? project.segments.filter(
            (s) =>
              (s.job && s.job.job_id === runId) ||
              (s.history && s.history.jobs && s.history.jobs.reduce((a, b) => a + (b.job_id === runId ? 1 : 0), 0) > 0)
          )
        : [];

    if (retSegments.length > 0) {
      retSegmentId = retSegments[0].id;
    }
  }

  return retSegmentId;
}

export function getOverallScale(state, id) {
  return getOptimizer(state, id).overallScale;
}

export function getOptimizers(state) {
  return Object.keys(state.optimizer).map((o) => state.optimizer[o]);
}

export function getBaselineUnitCost(state, id) {
  const aCost = setupReducer.getBaselineAdvancedCost(state, id);

  const baselineConversionCost = setupReducer.getBaselineConversionCost(state, id);

  return isFinite(aCost) && aCost !== 0 ? Math.abs(aCost) : Math.abs(baselineConversionCost);
}

export function getCurrentUnitCost(state, id) {
  const aCost = setupReducer.getCurrentAdvancedCost(state, id);

  const baselineConversionCost = setupReducer.getCurrentConversionCost(state, id);

  return isFinite(aCost) && aCost !== 0 ? Math.abs(aCost) : Math.abs(baselineConversionCost);
}

export function getUnitCost(state, id, setupOverride) {
  const aCost = setupReducer.getAdvancedCost(state, id, setupOverride);

  return isFinite(aCost) && aCost !== 0
    ? Math.abs(aCost)
    : Math.abs(getOptimizedConversionCost(state, id, setupOverride));
}

export function getBaselineUnitRevenue(state, id) {
  const baselineConversionGain = setupReducer.getBaselineConversionGain(state, id);

  const baselineConversionCost = setupReducer.getBaselineConversionCost(state, id);

  const aRev = setupReducer.getBaselineAdvancedRevenue(state, id);

  return isFinite(aRev) && aRev !== 0 ? aRev : baselineConversionGain + Math.abs(baselineConversionCost);
}

export function getCurrentUnitRevenue(state, id) {
  const baselineConversionGain = setupReducer.getCurrentConversionGain(state, id);

  const baselineConversionCost = setupReducer.getCurrentConversionCost(state, id);

  const aRev = setupReducer.getAdvancedRevenue(state, id);

  return isFinite(aRev) && aRev !== 0 ? aRev : baselineConversionGain + Math.abs(baselineConversionCost);
}

export function getUnitRevenue(state, id, setupOverride) {
  const aRev = setupReducer.getAdvancedRevenue(state, id, undefined, setupOverride);

  return isFinite(aRev) && aRev !== 0
    ? aRev
    : getOptimizedConversionGain(state, id, setupOverride) +
        Math.abs(getOptimizedConversionCost(state, id, setupOverride));
}

export function getOptimizedConversionGain(state, runId, setupOverride) {
  return Number(setupReducer.getConversionGain(state, runId, true, undefined, setupOverride));
}

export function getOptimizedConversionCost(state, runId, setupOverride) {
  return Number(setupReducer.getConversionCost(state, runId, true, undefined, setupOverride));
}

export function getOptimizedConversionSaved(state, runId, setupOverride) {
  return Number(setupReducer.getConversionSaved(state, runId, true, undefined, setupOverride));
}

export function getOptimizedConversionMissed(state, runId, setupOverride) {
  return Number(setupReducer.getConversionMissed(state, runId, true, undefined, setupOverride));
}

export function getSegmentConfusionMatrix(state, id, segmentId) {
  return (getSegmentConfusionMatrices(state, id) || {})[segmentId];
}

export function getSegmentConfusionMatrices(state, id) {
  return (getOptimizer(state, id) || {}).segmentConfusionMatrices;
}

export function getOptimizerWithinBounds(state, id) {
  return (getOptimizer(state, id) || {}).optimizerWithinBounds;
}

export function getOptimizerIsRunning(state, id) {
  return (getOptimizer(state, id) || {}).optimizerIsRunning;
}

export function getRejectedModels(state, projectId, segmentId) {
  const project = getProject(state, projectId) || {};

  const segments = project.segments !== undefined ? project.segments : [];

  for (let segment of segments) {
    if (segment.id == segmentId && segment.job && segment.job.data && segment.job.data.rejected !== undefined) {
      return segment.job.data.rejected;
    }
  }

  return [];
}

export function getIsAllowedInFrontier(state, runId, modelName) {
  const projectId = getProjectIdFromRunId(state, runId);
  const segment = getSegmentByRunId(state, projectId, runId);

  const rejected = getRejectedModels(state, projectId, segment.id);

  for (let rejectedModelName of rejected) {
    if (rejectedModelName == modelName) {
      return false;
    }
  }

  return true;
}

export function getBudget(state, id) {
  return getOptimizer(state, id) ? Number(getOptimizer(state, id).budget) : 0;
}

export function getSegmentBudgets(state, id) {
  return (getOptimizer(state, id) || {}).segmentBudgets;
}

export function getSegmentBudget(state, id, segmentId) {
  const runId = getSegmentRunId(state, id, segmentId);

  return getCapacity(state, id, runId) * getUnitCost(state, runId);
}

export function getSegmentBudgetByRunId(state, id, runId) {
  if (runId == undefined) {
    return getBudget(state, id);
  }

  const segmentId = getSegmentIdByRunId(state, id, runId);

  const segmentBudget = getSegmentBudget(state, id, segmentId);

  if (segmentBudget === undefined) {
    return getDefaultSegmentBudget(state, id, segmentId);
  }

  return segmentBudget;
}

export function getDefaultSegmentBudget(state, id, segmentId) {
  const runId = getSegmentRunId(state, id, segmentId);

  return getCurrentSpend(state, id, runId);
}

export function getSegmentMinBudget(state, id, segmentId) {
  return (getSegmentMinBudgets(state, id) || {})[segmentId];
}

export function getSegmentMinBudgets(state, id) {
  return (getOptimizer(state, id) || {}).segmentMinBudgets;
}

export function getAibleViewType(state, id) {
  return (getOptimizer(state, id) || {}).aibleViewType;
}

export function getSegmentMinBudgetByRunId(state, id, runId) {
  const segmentId = getSegmentIdByRunId(state, id, runId);

  return getSegmentMinBudget(state, id, segmentId);
}

export function getSegmentMaxBudget(state, id, segmentId) {
  return (getSegmentMaxBudgets(state, id) || {})[segmentId];
}

export function getSegmentMaxBudgets(state, id) {
  return (getOptimizer(state, id) || {}).segmentMaxBudgets;
}

export function getSegmentMaxBudgetByRunId(state, id, runId) {
  const segmentId = getSegmentIdByRunId(state, id, runId);

  return getSegmentMaxBudget(state, id, segmentId);
}

export function getSegment(state, id, segmentId) {
  const project = getProject(state, id) || {};

  const segments = project.segments !== undefined ? project.segments : [];

  for (let segment of segments) {
    if (segment.id == segmentId) {
      return segment;
    }
  }

  return undefined;
}

export function getSegmentRunId(state, id, segmentId) {
  const segment = getSegment(state, id, segmentId) || {};

  return (segment.job || {}).job_id;
}

export function getSegmentIdByRunId(state, id, runId) {
  const project = getProject(state, id) || {};

  const segments = project.segments !== undefined ? project.segments : [];

  for (let segment of segments) {
    if (Utils.get(segment, ["job", "job_id"]) == runId) {
      return segment.id;
    }
  }

  return undefined;
}

export function getSegmentByRunId(state, id, runId) {
  const project = getProject(state, id);

  if (project !== undefined) {
    for (let segment of project.segments) {
      if (Utils.get(segment, ["job", "job_id"]) == runId) {
        return segment;
      }
    }
  }

  return {};
}

export function getChildRunIds(state, id, runId) {
  const segmentId = getSegmentIdByRunId(state, id, runId);

  return getChildSegments(state, id, segmentId)
    .filter((segment) => segment.job)
    .map((segment) => segment.job.job_id);
}

export function getChildSegments(state, id, segmentId, column) {
  const childSegmentsByColumn = getChildSegmentsByColumn(state, id, segmentId);

  if (column == undefined) {
    for (let value of Object.values(childSegmentsByColumn)) {
      return value;
    }
  }

  return childSegmentsByColumn[column] || [];
}

export function getChildSegmentsByColumn(state, id, segmentId) {
  const project = getProject(state, id);
  const baseSegment = getSegment(state, id, segmentId);

  if (baseSegment === undefined || project === undefined) {
    return {};
  }

  const baseConditions = baseSegment.conditions;

  const childSegmentsByColumn = {};

  const matchingSegments = project.segments.filter((segment) =>
    baseConditions.every((baseCondition) =>
      segment.conditions.some(
        (condition) => condition.column == baseCondition.column && condition.value == baseCondition.value
      )
    )
  );

  for (let segment of matchingSegments) {
    const conditions = segment.conditions.filter(
      (condition) =>
        baseConditions.length == 0 ||
        baseConditions.some(
          (baseCondition) => condition.column != baseCondition.column && condition.value != baseCondition.value
        )
    );

    // child segments only have one additional condition
    // those with more are children of the children
    if (conditions.length == 1) {
      const { column } = conditions[0];

      if (childSegmentsByColumn[column] === undefined) {
        childSegmentsByColumn[column] = [];
      }

      childSegmentsByColumn[column].push(segment);
    }
  }

  return childSegmentsByColumn;
}

export function getCapacity(state, optimizerId, runId) {
  const segmentId = getSegmentIdByRunId(state, optimizerId, runId);

  const cm = getSegmentConfusionMatrix(state, optimizerId, segmentId);

  if (cm === undefined) {
    return 0;
  }

  return cm.getAllPositive();
}

export function getSegments(state, id) {
  const project = getProject(state, id);

  const segments =
    project && project.segments
      ? project.segments.map((s) => ({
          ...s,
          runId: s && s.job && s.job.job_id !== undefined ? s.job.job_id : undefined,
          model: s && s.job && s.job.job_id !== undefined ? runReducer.getFirstModel(state, s.job.job_id) : undefined,
          runCost: s && s.job && s.job.job_id !== undefined ? runReducer.getRunCost(state, s.job.job_id) : undefined
        }))
      : [];

  return segments;
}

function createChildSumFunction(func, skipLoadCheck = false) {
  return (state, optimizerId, runId) => {
    // Use overall run for everything unless all sub jobs have actual models
    if (!skipLoadCheck && hasLoadedAllJobs(state, optimizerId) === false) {
      return undefined;
    }

    const children = getChildRunIds(state, optimizerId, runId);

    if (children && children.length > 0) {
      const childValues = children.map((childRunId) => func(state, optimizerId, childRunId));

      return childValues.reduce((x, y) => x + y, 0);
    }

    return undefined;
  };
}

export function getTotalRevenue(state, optimizerId, runId) {
  const childSum = createChildSumFunction(getTotalRevenue)(state, optimizerId, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  return getTotalWon(state, optimizerId, runId) * getUnitRevenue(state, runId);
}

export function getTotalCost(state, optimizerId, runId) {
  const childSum = createChildSumFunction(getTotalCost)(state, optimizerId, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  return getTotalPursued(state, optimizerId, runId) * getUnitCost(state, runId);
}

export function getProfit(state, optimizerId, runId, isRaw) {
  const childSum = createChildSumFunction(getProfit)(state, optimizerId, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  const ccm = getConstrainedConfusionMatrix(state, optimizerId, runId);

  return ccm === undefined
    ? isRaw
      ? NaN
      : 0
    : ccm.getTruePositive() * setupReducer.getConversionGain(state, runId) +
        ccm.getFalsePositive() * setupReducer.getConversionCost(state, runId) +
        ccm.getTrueNegative() * setupReducer.getConversionSaved(state, runId) +
        ccm.getFalseNegative() * setupReducer.getConversionMissed(state, runId);
}

export function getProfitFromMatrix(state, matrix, runId, setupOverride) {
  const retSetupOverride = setupOverride && setupOverride.data ? setupOverride.data : setupOverride;
  const conversionGain =
    (retSetupOverride ? retSetupOverride.conversionGain : getOptimizedConversionGain(state, runId, retSetupOverride)) ||
    0;
  const conversionCost =
    (retSetupOverride ? retSetupOverride.conversionCost : getOptimizedConversionCost(state, runId, retSetupOverride)) ||
    0;
  const conversionSaved =
    (retSetupOverride
      ? retSetupOverride.conversionSaved
      : getOptimizedConversionSaved(state, runId, retSetupOverride)) || 0;
  const conversionMissed =
    (retSetupOverride
      ? retSetupOverride.conversionMissed
      : getOptimizedConversionMissed(state, runId, retSetupOverride)) || 0;

  return matrix === undefined || (retSetupOverride === undefined && runId === undefined)
    ? 0
    : matrix.tp * conversionGain +
        matrix.fp * conversionCost +
        matrix.tn * conversionSaved +
        matrix.fn * conversionMissed;
}

export function getRevenueFromMatrix(state, matrix, runId, setupOverride) {
  const retSetupOverride = setupOverride && setupOverride.data ? setupOverride.data : setupOverride;
  return matrix === undefined ? 0 : matrix.tp * getUnitRevenue(state, runId, retSetupOverride);
}

export function getCostFromMatrix(state, matrix, runId, setupOverride) {
  const retSetupOverride = setupOverride && setupOverride.data ? setupOverride.data : setupOverride;
  return matrix === undefined ? 0 : (matrix.tp + matrix.fp) * getUnitCost(state, runId, retSetupOverride);
}

export function getConfusionMatrix(state, runId, capacity = undefined) {
  const model = runReducer.getFirstModel(state, runId);

  if (model === undefined) {
    return new ConfusionMatrix(0, 0, 0, 0);
  }

  return (
    model.maxIteration &&
    model.maxIteration.confusionMatrixFactory &&
    model.maxIteration.confusionMatrixFactory.getConfusionMatrix({
      capacity
    })
  );
}

export function getOriginalCountConfusionMatrix(state, id, runId) {
  let retRunId = runId || getRunId(state, id);
  const run = runReducer.getJob(state, retRunId);
  if (Object.keys(run).length === 0) {
    return undefined;
  }
  const originalSetup =
    run.attributes && run.attributes.extra && run.attributes.extra.setupState
      ? JSON.parse(run.attributes.extra.setupState)
      : setupReducer.getSetup(state, retRunId);

  const tp = parseInt(originalSetup.truePositive);
  const fp = parseInt(originalSetup.falsePositive);
  const tn = parseInt(originalSetup.trueNegative);
  const fn = parseInt(originalSetup.falseNegative);
  const cm = new ConfusionMatrix(tp, tn, fp, fn);

  return cm;
}

export function getCountConfusionMatrix(state, id, runId) {
  let retRunId = runId || getRunId(state, id);
  if (getOptimizer(state, id) === undefined || Object.keys(runReducer.getJob(state, retRunId)).length === 0) {
    return undefined;
  }
  const tp = setupReducer.getTruePositive(state, retRunId);
  const fp = setupReducer.getFalsePositive(state, retRunId);
  const tn = setupReducer.getTrueNegative(state, retRunId);
  const fn = setupReducer.getFalseNegative(state, retRunId);
  const cm = new ConfusionMatrix(tp, tn, fp, fn);

  return cm;
}

function getCurrentStateConfusionMatrix(state, id, runId, fromSetup = false) {
  let retRunId = runId || getRunId(state, id);
  if (getOptimizer(state, id) === undefined || Object.keys(runReducer.getJob(state, retRunId)).length === 0) {
    return undefined;
  }

  const segment = getSegmentByRunId(state, id, retRunId);
  let tp, fp, tn, fn;
  if (segment && segment.assumption) {
    const assumptionData = segment.assumption.data;
    tp = parseInt(assumptionData.truePositive);
    fp = parseInt(assumptionData.falsePositive);
    tn = parseInt(assumptionData.trueNegative);
    fn = parseInt(assumptionData.falseNegative);
  } else {
    const setup = setupReducer.getSetup(state, retRunId);

    tp = parseInt(setup.truePositive);
    fp = parseInt(setup.falsePositive);
    tn = parseInt(setup.trueNegative);
    fn = parseInt(setup.falseNegative);
  }

  const cm = new ConfusionMatrix(tp, tn, fp, fn);

  return cm;
}

export function getOriginalOutcomePercentage(state, id, runId) {
  const cm = getOriginalCountConfusionMatrix(state, id, runId);
  return cm && cm.getActualPositive() / cm.getTotal();
}

export function getOutcomePercentage(state, id, runId) {
  const cm = getCountConfusionMatrix(state, id, runId);
  return cm && cm.getActualPositive() / cm.getTotal();
}

export function getCurrentStateOutcomePercentage(state, id, runId) {
  const cm = getCurrentStateConfusionMatrix(state, id, runId);
  return cm && cm.getActualPositive() / cm.getTotal();
}

export function updateProjectName(id, name) {
  return (dispatch, getState) => {
    return ProjectAPI.updateProject(id, {
      name: name
    }).then((response) => {
      dispatch({
        type: UPDATE_SUB_KEY,
        key: id,
        subKey: "name",
        value: name
      });
      return response;
    });
  };
}

export function updateOutcomePercentage(id, runId, percentage) {
  return (dispatch, getState) => {
    const state = getState();
    const setup = setupReducer.getSetup(state, runId);

    const tp = Math.round(setup.truePositive);
    const fp = Math.round(setup.falsePositive);
    const tn = Math.round(setup.trueNegative);
    const fn = Math.round(setup.falseNegative);

    const cm = new ConfusionMatrix(tp, tn, fp, fn);

    if (cm) {
      const newActualPositive = Math.max(0, Math.round(cm.getTotal() * percentage));

      const newCm = cm.copyAndConstrainActualPositives(newActualPositive);

      dispatch(setupReducer.updateCountsFromConfusionMatrix(runId, newCm, true));

      dispatch(setupReducer.updateOutcomePercentage(runId, percentage));

      dispatch(runReducer.updateCapValue(runId, id));
    }
    return id;
  };
}

export function getTotalWon(state, optimizerId, runId) {
  const childSum = createChildSumFunction(getTotalWon)(state, optimizerId, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  const constrainedConfusionMatrix = getConstrainedConfusionMatrix(state, optimizerId, runId);

  return constrainedConfusionMatrix && constrainedConfusionMatrix.getTruePositive();
}

export function getConstrainedConfusionMatrix(state, optimizerId, runId) {
  const segmentId = getSegmentIdByRunId(state, optimizerId, runId);

  /*
  const linkedData = getLinkedDataFromRunId(state, runId, optimizerId);

  //console.log(linkedData);

  const linkScale =
    linkedData !== undefined && linkedData.totalWon !== undefined
      ? linkedData.totalWon
      : undefined;

  */

  const linkScale = undefined;

  const confusionMatrix = getSegmentConfusionMatrix(state, optimizerId, segmentId);

  return linkScale !== undefined && confusionMatrix ? confusionMatrix.copyAndScaleTotal(linkScale) : confusionMatrix;
}

export function getTotalProspects(state, optimizerId, runId) {
  const childSum = createChildSumFunction(getTotalProspects)(state, optimizerId, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  const confusionMatrix = getConfusionMatrix(state, runId);

  return confusionMatrix && confusionMatrix.getTotal();
}

export function getCurrentTotalPursued(state, optimizerId, runId) {
  const childSum = createChildSumFunction(getCurrentTotalPursued)(state, optimizerId, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  const currentConfusionMatrix = getCurrentConfusionMatrix(state, runId);

  return currentConfusionMatrix && currentConfusionMatrix.getAllPositive();
}

export function getTotalPursued(state, optimizerId, runId) {
  const childSum = createChildSumFunction(getTotalPursued)(state, optimizerId, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  const constrainedConfusionMatrix = getConstrainedConfusionMatrix(state, optimizerId, runId);

  return constrainedConfusionMatrix && constrainedConfusionMatrix.getAllPositive();
}

export function getCurrentTotalWon(state, optimizerId, runId) {
  const childSum = createChildSumFunction(getCurrentTotalWon, true)(state, optimizerId, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  const currentConfusionMatrix = getCurrentConfusionMatrix(state, runId);

  return currentConfusionMatrix && currentConfusionMatrix.getTruePositive();
}

export function getDrilldownColumn(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).drilldownColumn : undefined;
}

export function getDrilldownValue(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).drilldownValue : undefined;
}

export function getSalesDrilldown(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).salesDrilldown : undefined;
}

export function getMarketingDrilldown(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).marketingDrilldown : undefined;
}

export function getBreakEvenProbability(state, id) {
  const salesRunId = getSalesRunId(state, id);
  const conversionCost = getOptimizedConversionCost(state, salesRunId);
  const conversionGain = getOptimizedConversionGain(state, salesRunId);
  const processLimitPct = setupReducer.getProcessLimitPct(state, salesRunId);

  const breakEvenProb = -conversionCost / (-conversionCost + conversionGain);

  const retBreakEvenProb = breakEvenProb + (1 - breakEvenProb) * (1 - processLimitPct);

  return retBreakEvenProb;
}

export function getCurrentSpend(state, id, runId) {
  const childSum = createChildSumFunction(getCurrentSpend, true)(state, id, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  return getCurrentConfusionMatrix(state, runId).getAllPositive() * getBaselineUnitCost(state, runId);
}

export function getCurrentSpendExcludingBaselines(state, id, runId) {
  const childSum = createChildSumFunction(getCurrentSpendExcludingBaselines, true)(state, id, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  return getCurrentConfusionMatrix(state, runId).getAllPositive() * getCurrentUnitCost(state, runId);
}

export function getCurrentProfit(state, id, runId) {
  const childSum = createChildSumFunction(getCurrentProfit, true)(state, id, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  const ccm = getCurrentConfusionMatrix(state, runId);

  return (
    ccm.getTruePositive() * setupReducer.getBaselineConversionGain(state, runId) +
    ccm.getFalsePositive() * setupReducer.getBaselineConversionCost(state, runId) +
    ccm.getTrueNegative() * setupReducer.getBaselineConversionSaved(state, runId) +
    ccm.getFalseNegative() * setupReducer.getBaselineConversionMissed(state, runId)
  );
}

export function getCurrentProfitExcludingBaselines(state, id, runId) {
  const childSum = createChildSumFunction(getCurrentProfitExcludingBaselines, true)(state, id, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  const ccm = getCurrentConfusionMatrix(state, runId);

  return (
    ccm.getTruePositive() * setupReducer.getCurrentConversionGain(state, runId) +
    ccm.getFalsePositive() * setupReducer.getCurrentConversionCost(state, runId) +
    ccm.getTrueNegative() * setupReducer.getCurrentConversionSaved(state, runId) +
    ccm.getFalseNegative() * setupReducer.getCurrentConversionMissed(state, runId)
  );
}

export function getCurrentRevenue(state, id, runId) {
  const childSum = createChildSumFunction(getCurrentRevenue, true)(state, id, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  return getCurrentConfusionMatrix(state, runId).getTruePositive() * getBaselineUnitRevenue(state, runId);
}

export function getCurrentRevenueExcludingBaselines(state, id, runId) {
  const childSum = createChildSumFunction(getCurrentRevenueExcludingBaselines, true)(state, id, runId);

  if (childSum !== undefined) {
    return childSum;
  }

  return getCurrentConfusionMatrix(state, runId).getTruePositive() * getCurrentUnitRevenue(state, runId);
}

export function getBaselineMatrixFromSetup(setup) {
  if (!setup) {
    return undefined;
  }
  const isRealityHammer = setup.isRealityHammer;
  const baselineCounts = setup.baselineCounts;

  const baselineMatrix = {
    tp: isRealityHammer && baselineCounts ? baselineCounts.truePositive : setup.truePositive,
    fp: isRealityHammer && baselineCounts ? baselineCounts.falsePositive : setup.falsePositive,
    fn: isRealityHammer && baselineCounts ? baselineCounts.falseNegative : setup.falseNegative,
    tn: isRealityHammer && baselineCounts ? baselineCounts.trueNegative : setup.trueNegative
  };

  return baselineMatrix;
}

export function getCurrentConfusionMatrix(state, runId) {
  const setup = setupReducer.getSetup(state, runId);

  const baselineMatrix = getBaselineMatrixFromSetup(setup);

  return new ConfusionMatrix(baselineMatrix.tp, baselineMatrix.tn, baselineMatrix.fp, baselineMatrix.fn);
}

export function getErrorRuns(state, id) {
  const optimizer = getOptimizer(state, id);

  if (optimizer === undefined) {
    return undefined;
  }

  let errorRuns = {};

  const jobKeys = getAllJobIdsFromOptimizer(state, optimizer.firstRunId, optimizer.secondRunIde);

  jobKeys.forEach((k) => {
    const job = runReducer.getJob(state, k);

    errorRuns[k] = job ? job.status : undefined;
  });

  return errorRuns;
}

export function getGroupInfo(state, id) {
  const optimizer = getOptimizer(state, id);

  if (optimizer === undefined) {
    return undefined;
  }

  let retInfo = {
    marketing: {},
    sales: {}
  };

  return retInfo;
}

export function getSegmentOptions(state, id, projectOverride = null) {
  const project = projectOverride ? projectOverride : getProject(state, id);

  if (!project || !project.segments) {
    return undefined;
  }

  return project.segments
    .filter(
      (s) => s.conditions.length <= 1 && (s.permissions.can_draft || s.permissions.can_edit || s.conditions.length == 0)
    )
    .map((s) => ({
      value: s.conditions && s.conditions.length > 0 ? s.conditions[0].value : "Overall",
      label:
        s.conditions && s.conditions.length > 0 ? s.conditions[0].column + ": " + s.conditions[0].value : "Overall",
      runId: s.job && s.job.job_id ? s.job.job_id : undefined
    }));
}

/* old colors
export const colorsTwo = isEnabled("aible-business-simple")
  ? [
      "#7E69D7",
      "#9DC0FA",
      "#82DAE9",
      "#87DCC0",
      "#C1E58A",
      "#FACE85",
      "#F99FAE",
    ]
  : ["#CCC", "#D8B4FE", "#5EEAD4", "#BBF7D0", "#FEF08A", "#FED7AA", "#FECDD3"];
*/

export const colorsTwo = [
  "#20CAE8",
  "#E879F9",
  "#82D200",
  "#60A5FA",
  "#F87171",
  "#34D399",
  "#52DCEC",
  "#E396F5",
  "#82D200",
  "#6DB9FD",
  "#FF8E8C",
  "#67E2AD"
];

export const colorsSecondary = ["#7E69D7", "#22D3EE", "#E879F9", "#84CC16", "#60A5FA", "#10B981", "#F59E0B"];

export function translateColors(color, indexOverride = undefined) {
  const bluePallete = ["#DBEAFE", "#BFDBFE", "#93C5FD", "#60A5FA", "#3B82F6"];
  const baseIndex = indexOverride !== undefined ? indexOverride : colorsTwo.indexOf(color);
  const retIndex = baseIndex % bluePallete.length;
  return bluePallete[retIndex];
}

export function getColorFromSegment(state, projectId, segmentId) {
  const modelGroups = getModelGroups(state, projectId);

  return (
    (modelGroups &&
      modelGroups.drilldownValues &&
      modelGroups.drilldownValues.find((s) => s.segmentId === Number(segmentId))?.color) ||
    colorsTwo[0]
  );
}

export function getSegmentColor(state, projectId) {
  const segmentId = getSegmentId(state, projectId);

  return getColorFromSegment(state, projectId, segmentId);
}

export function getUserThresholds(state, projectId, userId, modelGroupOverride) {
  const modelGroups = modelGroupOverride || getModelGroups(state, projectId);

  return (
    modelGroups &&
    modelGroups.drilldownValues &&
    modelGroups.drilldownValues
      .map((segment, i, drilldownValues) => {
        if (segment.value === "Overall" && drilldownValues.length > 1) {
          return;
        }

        let retThresholdValue;

        if (isEnabled("monitoring-demo")) {
          const normalizedId = userId % 15;

          const baseThresholdValue =
            userId % 2 == 0
              ? normalizedId * Math.abs(10 - 2 * Math.sqrt(normalizedId))
              : Math.abs(40 - 2 * normalizedId);

          const segmentModifier = i / (((i + 2) / 2) * drilldownValues.length);

          retThresholdValue =
            (i + userId) % 2 === 0
              ? baseThresholdValue * (1 + segmentModifier)
              : baseThresholdValue / (1 + segmentModifier);
        } else {
          const runId = segment?.runId;

          const currSegment = getSegmentByRunId(state, projectId, runId);

          const userRole = currSegment?.roles?.find((r) => r?.role_user?.id === userId);

          retThresholdValue = userRole?.preferences?.percent_processed;
        }

        return {
          runId: segment.runId,
          segmentId: segment.segmentId,
          color: segment.color,
          colorSecondary: segment.colorSecondary,
          column: segment.column,
          value: segment.value,
          label: segment.label,
          threshold: retThresholdValue
        };
      })
      .filter((v) => Boolean(v) && v?.threshold !== undefined)
  );
}

export function getBalanceAlloc(state, projectId) {
  const linkedProject = getLinkedProject(state, projectId);
  const linkedProjectOutputMode = getLinkedProjectOutputMode(state, projectId);
  const balanceData = getBalanceData(state, projectId);

  const retData = {};

  linkedProject &&
    linkedProject.segments &&
    linkedProject.segments
      .filter((s, i, segs) => segs.length === 1 || (s.conditions && s.conditions.length > 0))
      .forEach((s) => {
        const runId = s.job && s.job.job_id !== undefined ? s.job.job_id : undefined;

        let totalWon = 0;
        switch (linkedProjectOutputMode) {
          case BALANCER_OUTPUT.TRUE_POSITIVE:
            totalWon = getTotalWon(state, linkedProject.id, runId);
            break;
          case BALANCER_OUTPUT.ALL_POSITIVE:
          default:
            totalWon = getTotalPursued(state, linkedProject.id, runId);
            break;
        }

        const used = Object.keys(balanceData).reduce(
          (a, b) =>
            a +
            Object.keys(balanceData[b])
              .filter(
                (k) =>
                  balanceData[b][k] && balanceData[b][k].linkedSegment && balanceData[b][k].linkedSegment.id === s.id
              )
              .reduce((c, d) => c + balanceData[b][d].total, 0),
          0
        );

        const nUsed = [];

        Object.keys(balanceData).forEach((b) =>
          Object.keys(balanceData[b])
            .filter((runId) => balanceData[b][runId].linkedSegment !== undefined)
            .forEach((runId) => {
              if (balanceData[b][runId].linkedSegment.id === s.id) {
                nUsed.push({
                  percentage: balanceData[b][runId].percentage,
                  toSegment: getSegment(state, projectId, b),
                  color: getColorFromSegment(state, projectId, b)
                });
              }
            })
        );

        retData[s.id] = {
          total: totalWon,
          segment: s,
          used: used,
          nUsed: nUsed
        };
      });

  return retData;
}

export function getBalanceData(state, projectId) {
  const project = getProject(state, projectId);
  const linkedProject = getLinkedProject(state, projectId);
  const linkedProjectOutputMode = getLinkedProjectOutputMode(state, projectId);

  const retData = {};

  project &&
    project.segments &&
    project.segments.forEach((s) => {
      const runId = s.job && s.job.job_id !== undefined ? s.job.job_id : undefined;

      const setup = setupReducer.getSetup(state, runId);

      const totalWon = {};
      setup &&
        setup.linkedRuns &&
        Object.keys(setup.linkedRuns)
          .filter((k) => setup.linkedRuns[k])
          .forEach((k) => {
            let currTotal = 0;
            switch (linkedProjectOutputMode) {
              case BALANCER_OUTPUT.TRUE_POSITIVE:
                currTotal = getTotalWon(state, setup.linkedProjectId, setup.linkedRuns[k].linkedRunId);
                break;
              case BALANCER_OUTPUT.ALL_POSITIVE:
              default:
                currTotal = getTotalPursued(state, setup.linkedProjectId, setup.linkedRuns[k].linkedRunId);
                break;
            }
            totalWon[setup.linkedRuns[k].linkedRunId] = {
              percentage: setup.linkedRuns[k].percentage,
              total: Math.round((currTotal * setup.linkedRuns[k].percentage) / 100),
              rawTotal: Math.round(currTotal),
              linkedSegment:
                linkedProject &&
                linkedProject.segments &&
                linkedProject.segments.filter((s) => s.job && s.job.job_id === setup.linkedRuns[k].linkedRunId).length >
                  0
                  ? linkedProject.segments.filter((s) => s.job && s.job.job_id === setup.linkedRuns[k].linkedRunId)[0]
                  : undefined
            };
          });

      retData[s.id] = totalWon;
    });

  return retData;
}

export function getLinkedModelGroups(state, projectId) {
  const linkedProject = getLinkedProject(state, projectId);

  return linkedProject && linkedProject.id ? getModelGroups(state, linkedProject.id) : undefined;
}

export function getBalancedModel(state, projectId) {
  const project = getProject(state, projectId);

  let retModel = {
    maxIteration: {
      truePositive: 0,
      falsePositive: 0,
      trueNegative: 0,
      falseNegative: 0
    }
  };

  const childSegments =
    project &&
    project.segments.filter((d) => d.conditions && d.conditions.length !== 0 && d.job && d.job.job_id !== undefined);

  childSegments.forEach((d) => {
    const bModel = runReducer.getFirstModel(state, d.job.job_id);

    if (bModel && bModel.maxIteration) {
      retModel.maxIteration.truePositive += bModel.maxIteration.truePositive;
      retModel.maxIteration.falsePositive += bModel.maxIteration.falsePositive;
      retModel.maxIteration.trueNegative += bModel.maxIteration.trueNegative;
      retModel.maxIteration.falseNegative += bModel.maxIteration.falseNegative;
    }
  });

  if (childSegments.length > 0) {
    retModel.maxIteration.truePositive / childSegments.length;
    retModel.maxIteration.falsePositive / childSegments.length;
    retModel.maxIteration.trueNegative / childSegments.length;
    retModel.maxIteration.falseNegative / childSegments.length;
  }

  return retModel;
}

export function getBalancedCCM(state, projectId) {
  const project = getProject(state, projectId);

  let retCcm = {
    tp: 0,
    fp: 0,
    tn: 0,
    fn: 0
  };

  const childSegments =
    project &&
    project.segments.filter((d) => d.conditions && d.conditions.length !== 0 && d.job && d.job.job_id !== undefined);

  childSegments.forEach((d) => {
    const ccm = getConstrainedConfusionMatrix(state, projectId, d.job.job_id);

    if (ccm) {
      retCcm.tp += ccm.tp;
      retCcm.fp += ccm.fp;
      retCcm.tn += ccm.tn;
      retCcm.fn += ccm.fn;
    }
  });

  if (childSegments.length > 0) {
    retCcm.tp / childSegments.length;
    retCcm.fp / childSegments.length;
    retCcm.tn / childSegments.length;
    retCcm.fn / childSegments.length;
  }

  return retCcm;
}

export function getModelGroups(state, optimizerId, getModelList = false, skipFrontier = true) {
  let modelGroups = { ...getProject(state, optimizerId) };

  const runId = getRunId(state, optimizerId);

  if (runId === undefined) {
    return undefined;
  }

  modelGroups.hasGroup = true;

  modelGroups.drilldownValues = [
    {
      value: "Overall",
      label: "Overall",
      column: "Overall",
      runId:
        modelGroups.segments && modelGroups.segments[0] && modelGroups.segments[0].job
          ? modelGroups.segments[0].job.job_id
          : undefined,
      pctOfRows: 1
    }
  ];

  modelGroups.segments &&
    Object.keys(modelGroups.segments).forEach((m) => {
      const segment = modelGroups.segments[m];

      if (segment.job && segment.job.job_id && Object.keys(runReducer.getJob(state, segment.job.job_id)).length > 0) {
        if (
          segment.conditions.length === 1 &&
          (segment.permissions.can_draft || segment.permissions.can_edit || segment.permissions.can_view)
        ) {
          const condition = segment.conditions[0];
          const runId = segment.job.job_id;

          modelGroups.drilldownValues.push({
            column: condition.column,
            value: condition.value,
            label: condition.value,
            runId: runId,
            segment: segment,
            originalSetup:
              segment && segment.history && segment.history.assumptions && segment.history.assumptions.length > 0
                ? segment.history.assumptions[0].data
                : setupReducer.getSetup(state, runId),
            totalCost: getTotalCost(state, optimizerId, runId),
            currentSpend: getCurrentSpend(state, optimizerId, runId),
            currentProfit: getCurrentProfit(state, optimizerId, runId),
            currentRevenue: getCurrentRevenue(state, optimizerId, runId),
            profit: getProfit(state, optimizerId, runId),
            revenue: getTotalRevenue(state, optimizerId, runId),
            cost: getTotalCost(state, optimizerId, runId),
            isBusinessReality: runReducer.getIsBusinessReality(state, runId),
            segmentId: segment.id,
            originalOutcomePercentage: getOriginalOutcomePercentage(state, optimizerId, runId),
            outcomePercentage: getOutcomePercentage(state, optimizerId, runId),
            currentOutcomePercentage: getCurrentStateOutcomePercentage(state, optimizerId, runId)
          });
        }
      }
    });

  modelGroups.drilldownValues.forEach((d, i) => {
    if (d.profit === undefined) {
      d.profit = getProfit(state, optimizerId, d.runId);
    }
    if (d.revenue === undefined) {
      d.revenue = getTotalRevenue(state, optimizerId, d.runId);
    }
    if (d.cost === undefined) {
      d.cost = getTotalCost(state, optimizerId, d.runId);
    }

    if (d.units === undefined) {
      d.units = getTotalPursued(state, optimizerId, d.runId);
    }

    if (d.currentSpend === undefined) {
      d.currentSpend = getCurrentSpend(state, optimizerId, d.runId);
    }
    if (d.currentProfit === undefined) {
      d.currentProfit = getCurrentProfit(state, optimizerId, d.runId);
    }
    if (d.currentRevenue === undefined) {
      d.currentRevenue = getCurrentRevenue(state, optimizerId, d.runId);
    }

    d.setup = getProjectSetup(state, optimizerId, d.runId);
    d.oSetup = setupReducer.getSetup(state, d.runId);
    d.pctOfRows = runReducer.getPctOfRows(state, d.runId);
    d.bestModel = getIsOverallSegment(state, optimizerId, d.runId)
      ? getBalancedModel(state, optimizerId)
      : runReducer.getFirstModel(state, d.runId);

    /* I'm being super lazy here and hacking onto this horrible function
      getTransformedSortedModels is aj expensive call so I'm only using it when its needed
      on the model list component. Ideally we should rewrite a smaller selector */
    if (getModelList === true) {
      d.modelList = runReducer.getCurrentCapacityModels(state, d.runId, optimizerId);
    }

    if (skipFrontier === false) {
      d.capModels = runReducer.getFrontier(state, d.runId);
    }
    d.scaledTotal = setupReducer.getScaledTotal(state, d.runId);

    d.numModels = runReducer.getModelsArr(state, d.runId) ? runReducer.getModelsArr(state, d.runId).length : 0;

    d.ccm = getIsOverallSegment(state, optimizerId, d.runId)
      ? getBalancedCCM(state, optimizerId)
      : getConstrainedConfusionMatrix(state, optimizerId, d.runId);

    d.runCost = runReducer.getRunCost(state, d.runId);

    d.errorMessages = runReducer.getErrors(state, d.runId);
  });

  const firstOrderSegmentsByColumn = {};

  for (let value of modelGroups.drilldownValues) {
    if (firstOrderSegmentsByColumn[value.column] === undefined) {
      firstOrderSegmentsByColumn[value.column] = [];
    }

    firstOrderSegmentsByColumn[value.column].push(value);
  }

  modelGroups.drilldownValues = Object.values(firstOrderSegmentsByColumn)
    .flatMap((values) => {
      const cost = values.map((value) => value.totalCost).reduce((x, y) => x + y, 0);

      return values.map((value) => ({
        ...value,
        optimalPct: value.totalCost,
        currentCost: value.currentSpend,
        capValue: getCapValue(state, optimizerId, value.runId, true)
        /*
        value.setup && value.setup.truePositive !== undefined
          ? Math.abs((value.setup.truePositive +
              value.setup.falsePositive) *
            value.setup.conversionCost)
          : 0
        */
      }));
    })
    .sort((a, b) => (a.value < b.value ? 1 : -1));

  modelGroups.overspendCount = modelGroups.drilldownValues.filter((v) => v.totalCost < v.currentSpend).length;
  modelGroups.underspendCount = modelGroups.drilldownValues.filter((v) => v.totalCost > v.currentSpend).length;
  modelGroups.noIssueCount = modelGroups.drilldownValues.filter(
    (v) => v.totalCost === v.currentSpend && v.totalCost !== undefined
  ).length;

  modelGroups.drilldownValues = modelGroups.drilldownValues.sort((a, b) =>
    a.column === "Overall" ? -1 : b.column === "Overall" ? 1 : b.column < a.column ? 1 : -1
  );

  modelGroups.drilldownValues.forEach((d, i) => {
    d.color = colorsTwo[i % colorsTwo.length];
    d.colorSecondary = colorsSecondary[i % colorsSecondary.length];
  });

  modelGroups.topDrivers = modelGroups.drilldownValues.map((v) => ({
    drilldownValue: v.value,
    drilldownColumn: v.column,
    driver: getTopDriver(state, v.runId),
    columnDrivers: getColumnDrivers(state, v.runId, optimizerId),
    runId: v.runId,
    bestModel: v.bestModel,
    segment: getSegmentByRunId(state, optimizerId, v.runId),
    color: v.color
  }));

  modelGroups.activeDriver =
    modelGroups.topDrivers.filter((d) => d.runId === runId).length > 0
      ? modelGroups.topDrivers.filter((d) => d.runId === runId)[0]
      : undefined;

  return modelGroups;
}

export function getColumnDrivers(state, runId, projectId) {
  const firstModel = runReducer.getFirstModel(state, runId, true);

  if (firstModel === undefined || firstModel.features === undefined || firstModel.features.percentages === undefined) {
    return undefined;
  }

  let retFeatures = [];

  Object.keys(firstModel.features.percentages).forEach((p) => {
    let maxValue = -Infinity;
    let maxKey = undefined;

    let minValue = Infinity;
    let minKey = undefined;

    for (const val of Object.keys(firstModel.features.percentages[p].values)) {
      if (maxValue === undefined || firstModel.features.percentages[p].values[val] > maxValue) {
        maxValue = firstModel.features.percentages[p].values[val];
        maxKey = val;
      }

      if (minValue === undefined || firstModel.features.percentages[p].values[val] < minValue) {
        minValue = firstModel.features.percentages[p].values[val];
        minKey = val;
      }
    }
    retFeatures.push({
      field: p,
      minName: minKey,
      maxName: maxKey,
      value: firstModel.features.percentages[p].percentage
    });
  });

  return retFeatures.sort((a, b) => {
    return b.value - a.value;
  });
}

export function getTopDriver(state, runId) {
  const firstModel = runReducer.getFirstModel(state, runId, true);

  if (firstModel === undefined || firstModel.features === undefined || firstModel.features.percentages === undefined) {
    return undefined;
  }

  const profit = 1;

  let topFeature = undefined;
  let topFeatureName = undefined;

  let retFeatures = [];

  Object.keys(firstModel.features.percentages).forEach((p) =>
    Object.keys(firstModel.features.percentages[p].values).forEach((v) => {
      retFeatures.push({
        field: p,
        name: p + ": " + v,
        value: firstModel.features.percentages[p].values[v],
        profit: profit,
        score: Math.abs(profit * firstModel.features.percentages[p].values[v])
      });
    })
  );

  let filterHelper = {};

  return retFeatures
    .sort((a, b) => {
      if (isNaN(a.score) && isNaN(b.score)) {
        return Math.abs(b.value) - Math.abs(a.value);
      } else {
        return b.score - a.score;
      }
    })
    .filter((f) => {
      if (f.field) {
        filterHelper[f.field] = filterHelper[f.field] === undefined ? 1 : filterHelper[f.field] + 1;

        if (filterHelper[f.field] < 3) {
          return true;
        }
      }

      return false;
    });
}

export function getTopDriversByModel(model, numDrivers = 5) {
  const firstModel = model;

  if (firstModel === undefined || firstModel.features === undefined || firstModel.features.percentages === undefined) {
    return undefined;
  }

  let retFeatures = [];

  Object.keys(firstModel.features.percentages).forEach((p) => {
    let maxValue = -Infinity;
    let maxKey = undefined;

    let minValue = Infinity;
    let minKey = undefined;

    for (const val of Object.keys(firstModel.features.percentages[p].values)) {
      if (maxValue === undefined || firstModel.features.percentages[p].values[val] > maxValue) {
        maxValue = firstModel.features.percentages[p].values[val];
        maxKey = val;
      }

      if (minValue === undefined || firstModel.features.percentages[p].values[val] < minValue) {
        minValue = firstModel.features.percentages[p].values[val];
        minKey = val;
      }
    }
    retFeatures.push({
      field: p,
      minName: minKey,
      maxName: maxKey,
      value: firstModel.features.percentages[p].percentage
    });
  });

  return retFeatures.sort((a, b) => {
    return b.value - a.value;
  });
}

export function getLinkedDataFromRunId(state, runId, projectId) {
  const setup = setupReducer.getSetup(state, runId);

  if (setup && setup.linkedRuns !== undefined) {
    const linkedRuns = Object.keys(setup.linkedRuns)
      .filter((r) => setup.linkedRuns[r] !== undefined && setup.linkedRuns[r].linkedRunId !== undefined)
      .map((r) => setup.linkedRuns[r]);

    return {
      totalWon: Math.round(
        linkedRuns.reduce(
          (a, b) => a + getTotalWon(state, setup.linkedProjectId, b.linkedRunId) * (b.percentage / 100),
          0
        )
      ),
      totalProspects: Math.round(
        linkedRuns.reduce(
          (a, b) => a + getTotalProspects(state, setup.linkedProjectId, b.linkedRunId) * (b.percentage / 100),
          0
        )
      ),
      totalPursued: Math.round(
        linkedRuns.reduce(
          (a, b) => a + getTotalPursued(state, setup.linkedProjectId, b.linkedRunId) * (b.percentage / 100),
          0
        )
      )
    };
  }
}

export function getLinkedData(state, projectId) {
  const linkedProjectOutputMode = getLinkedProjectOutputMode(state, projectId);

  const runId = getRunId(state, projectId);

  const setup = setupReducer.getSetup(state, runId);

  if (setup && (setup.linkedProjectId !== undefined || setup.linkedRunId !== undefined)) {
    const linkedRunId =
      setup.linkedRunId !== undefined
        ? setup.linkedRunId
        : getOverallRunIdFromProject(getProject(state, setup.linkedProjectId));

    const linkedSetup =
      setup.linkedProjectId === undefined
        ? setup
        : setupReducer.getSetup(state, getOverallRunId(state, setup.linkedProjectId));

    const linkedRuns =
      setup.linkedRuns === undefined
        ? []
        : Object.keys(setup.linkedRuns)
            .filter((r) => setup.linkedRuns[r] !== undefined && setup.linkedRuns[r].linkedRunId !== undefined)
            .map((r) => setup.linkedRuns[r]);

    if (getIsOverallSegment(state, projectId, runId) === true) {
      const project = getProject(state, projectId);

      const totalWon = linkedRuns.reduce((a, b) => a + getTotalWon(state, setup.linkedProjectId, b.linkedRunId), 0);

      const totalProspects = linkedRuns.reduce(
        (a, b) => a + getTotalProspects(state, setup.linkedProjectId, b.linkedRunId),
        0
      );

      const totalPursued = linkedRuns.reduce(
        (a, b) => a + getTotalPursued(state, setup.linkedProjectId, b.linkedRunId),
        0
      );

      const totalAllocated =
        project &&
        project.segments
          .filter((s) => s.conditions.length > 0)
          .reduce((a, b) => {
            const setup = setupReducer.getSetup(state, b.job.job_id);

            const innerLinkedRuns =
              setup.linkedRuns === undefined
                ? []
                : Object.keys(setup.linkedRuns)
                    .filter((r) => setup.linkedRuns[r] !== undefined && setup.linkedRuns[r].linkedRunId !== undefined)
                    .map((r) => setup.linkedRuns[r]);

            return (
              a +
              innerLinkedRuns.reduce((c, d) => {
                let currTotal = 0;
                switch (linkedProjectOutputMode) {
                  case BALANCER_OUTPUT.TRUE_POSITIVE:
                    currTotal = getTotalWon(state, setup.linkedProjectId, d.linkedRunId);
                    break;
                  case BALANCER_OUTPUT.ALL_POSITIVE:
                  default:
                    currTotal = getTotalPursued(state, setup.linkedProjectId, d.linkedRunId);
                    break;
                }
                return c + currTotal * (d.percentage / 100);
              }, 0)
            );
          }, 0);

      return {
        setup: linkedRuns.length > 0 ? setupReducer.getSetup(state, linkedRuns[0].linkedRunId) : linkedSetup,
        totalWon: Math.round(totalWon),
        totalProspects: Math.round(totalProspects),
        totalPursued: Math.round(totalPursued),
        totalAllocated: Math.round(totalAllocated)
      };
    }

    return {
      setup: linkedRuns.length > 0 ? setupReducer.getSetup(state, linkedRuns[0].linkedRunId) : linkedSetup,
      totalWon: Math.round(
        linkedRuns.reduce(
          (a, b) => a + getTotalWon(state, setup.linkedProjectId, b.linkedRunId) * (b.percentage / 100),
          0
        )
      ),
      totalProspects: Math.round(
        linkedRuns.reduce(
          (a, b) => a + getTotalProspects(state, setup.linkedProjectId, b.linkedRunId) * (b.percentage / 100),
          0
        )
      ),
      totalPursued: Math.round(
        linkedRuns.reduce(
          (a, b) => a + getTotalPursued(state, setup.linkedProjectId, b.linkedRunId) * (b.percentage / 100),
          0
        )
      ),
      totalAllocated: Math.round(
        linkedRuns.reduce((a, b) => {
          let currTotal = 0;
          switch (linkedProjectOutputMode) {
            case BALANCER_OUTPUT.TRUE_POSITIVE:
              currTotal = getTotalWon(state, setup.linkedProjectId, b.linkedRunId);
              break;
            case BALANCER_OUTPUT.ALL_POSITIVE:
            default:
              currTotal = getTotalPursued(state, setup.linkedProjectId, b.linkedRunId);
              break;
          }
          return a + currTotal * (b.percentage / 100);
        }, 0)
      )
    };
  } else {
    return undefined;
  }
}

export function getHasLinkedProjectOld(state, projectId) {
  const segment = getActiveSegment(state, projectId);

  const setup = segment !== undefined ? getSetupFromSegment(state, projectId, segment.id) : undefined;

  return setup && setup.linkedProjectId !== undefined ? true : false;
}

export function getIsLinkedProjectLoading(state, projectId) {
  const segment = getActiveSegment(state, projectId);

  const setup = segment !== undefined ? getSetupFromSegment(state, projectId, segment.id) : undefined;

  if (setup && (setup.linkedProjectId !== undefined || setup.linkedRunId !== undefined)) {
    const linkedRunId =
      setup.linkedRunId !== undefined
        ? setup.linkedRunId
        : getOverallRunIdFromProject(getProject(state, setup.linkedProjectId));

    const job = runReducer.getJob(state, linkedRunId);

    return job !== undefined && Object.keys(job).length > 2 ? false : true;
  }

  return undefined;
}

export function getLinkProjectOptions(state, projectId) {
  const reports = viewReducer.getReports(state);

  const retProjects = [];

  reports.forEach((r) => {
    const next = nextReducer.getNext(state, r?.id);
    if (next?.projectId !== undefined && (!projectId || next?.projectId !== projectId)) {
      if (getIsContinuous(state, next?.projectId)) {
        return;
      }
      retProjects.push({
        label: next?.reportLabel !== undefined ? next.reportLabel : r?.name,
        value: next?.projectId
      });
    }
  });

  return retProjects;
}

export function getLinkedProject(state, id) {
  const runId = getOverallRunId(state, id);
  const setup = setupReducer.getSetup(state, runId);

  return setup && setup.linkedProjectId !== undefined ? getProject(state, setup.linkedProjectId) : undefined;
}

export function getLinkedProjectOutputMode(state, projectId) {
  const overallRunId = getOverallRunId(state, projectId);

  const setupOutputMode = setupReducer.getLinkedProjectOutputMode(state, overallRunId);

  return setupOutputMode !== undefined ? setupOutputMode : BALANCER_OUTPUT.ALL_POSITIVE;
}

export function getLinkedProjectOutputModeOption(state, projectId, optionOverride = undefined) {
  const linkedProjectId = getLinkedProjectId(state, projectId);
  const linkedOptimized = getOptimized(state, linkedProjectId);

  const linkedSetup = linkedOptimized !== undefined ? linkedOptimized.setup : undefined;

  const selectedOption = optionOverride !== undefined ? optionOverride : getLinkedProjectOutputMode(state, projectId);

  const tpLabel = linkedSetup && linkedSetup.truePositiveLabel ? linkedSetup.truePositiveLabel : "True Positive";
  const transactionLabel = linkedSetup && linkedSetup.transactionLabel ? linkedSetup.transactionLabel : "Opportunities";
  const trueActionLabel = linkedSetup && linkedSetup.trueAction ? linkedSetup.trueAction : "Pursue";

  switch (selectedOption) {
    case BALANCER_OUTPUT.ALL_POSITIVE:
      return {
        label: transactionLabel + " you " + trueActionLabel,
        value: BALANCER_OUTPUT.ALL_POSITIVE
      };
    case BALANCER_OUTPUT.TRUE_POSITIVE:
      return {
        label: tpLabel,
        value: BALANCER_OUTPUT.TRUE_POSITIVE
      };
  }

  return undefined;
}

export function getTasks(state, projectId) {
  const activeSegment = getActiveSegment(state, projectId);
  const tasks = tasksReducer.getTasksArr(state);
  const project = getProject(state, projectId);

  /*


  return tasks === undefined
    ? []
    : tasks
        .filter(
          (task) =>
            tasksReducer.getProjectIdFromTask(state, task?.id) === projectId &&
            (tasksReducer.getSegmentIdFromTask(state, task?.id) ===
              activeSegment?.id ||
              isOverallSegment)
        )
        .map((task) => ({
          ...task,
          segment: tasksReducer.getSegmentFromTask(state, task),
        }));
  */

  const isOverallSegment = getIsOverallSegment(state, projectId);

  const retTasks =
    isOverallSegment && project?.segments
      ? project.segments.reduce((a, b) => a.concat(b.tasks), [])
      : activeSegment?.tasks || project?.segments?.[0]?.tasks || [];

  return retTasks.map((t) =>
    tasksReducer.getTask(state, t?.id) !== undefined ? tasksReducer.getTask(state, t?.id) : t
  );
}

export function getLinkedProjectOutputModeOptions(state, projectId) {
  return [
    getLinkedProjectOutputModeOption(state, projectId, BALANCER_OUTPUT.ALL_POSITIVE),
    getLinkedProjectOutputModeOption(state, projectId, BALANCER_OUTPUT.TRUE_POSITIVE)
  ];
}

export function getProjectSegmentRoleId(state, projectId) {
  const userName = loginReducer.getUserName(state);

  const project = getProject(state, projectId);

  const segment = getActiveSegment(state, projectId);

  const activeRole =
    segment &&
    segment.roles &&
    segment.roles.filter((r) => r && r.role_user && r.role_user.fullname && r.role_user.fullname === userName).length >
      0
      ? segment.roles.filter((r) => r && r.role_user && r.role_user.fullname && r.role_user.fullname === userName)[0]
      : undefined;

  return activeRole ? activeRole.id : undefined;
}

export function getHasBusinessSetup(state, projectId) {
  const project = getProject(state, projectId);

  let hasBusinessSetup = false;

  project &&
    project.segments &&
    project.segments.forEach(
      (s) =>
        s.history &&
        s.history.assumptions &&
        s.history.assumptions.forEach((a) => {
          if (a && a.data && (a.data.showBusiness !== undefined || a.data.showImpact !== undefined)) {
            hasBusinessSetup = true;
          }
        })
    );

  return hasBusinessSetup;
}

export function updatePercentProcessed(projectId, percent) {
  return (dispatch, getState) => {
    const projectSegmentRoleId = getProjectSegmentRoleId(getState(), projectId);

    return ProjectAPI.patchUserRoleSegmentPercent(projectSegmentRoleId, percent).then((response) => response);
  };
}

export function updateSegmentRoleFeedback(projectId, feedback) {
  return (dispatch, getState) => {
    const projectSegmentRoleId = getProjectSegmentRoleId(getState(), projectId);

    return ProjectAPI.patchUserRoleSegmentFeedback(projectSegmentRoleId, feedback).then((response) => response);
  };
}

export function updateModelFeedback(runId, modelId, isLike) {
  return (dispatch, getState) => {
    const projectId = getProjectIdFromRunId(getState(), runId);

    const segment = getSegmentByRunId(getState(), projectId, runId);

    if (!segment.permissions.can_view) {
      toast("You do not have permission to provide feedback on this project.", {
        type: toast.TYPE.ERROR,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });
      return Promise.resolve();
    }

    const feedback = {
      response: isLike ? "LIKE" : "DISLIKE",
      job_id: runId,
      model_name: modelId
    };

    return ProjectAPI.submitUserFeedback(segment.id, feedback).then((r) => dispatch(fetchProject(projectId)));
  };
}

export function getScenarioLegacyAssumptionId(state, projectId, runId) {
  const scenarios = getSelectedScenario(state, projectId);

  const currSubgroupScenario =
    scenarios && scenarios.scenario && Object.values(scenarios.scenario).find((s) => s.runId === runId);

  const assumptionId = currSubgroupScenario && currSubgroupScenario.assumption_id;
  return assumptionId;
}

export function getScenarioSegmentFeedback(state, projectId, runId) {
  const assumptionId = getScenarioLegacyAssumptionId(state, projectId, runId);

  const currAssumption = getAssumptionById(state, projectId, assumptionId);

  const assumptionFeedback = getAssumptionFeedback(state, projectId, assumptionId);

  const currRating = assumptionFeedback && assumptionFeedback.ratings && assumptionFeedback.ratings.score;

  const retComments = assumptionFeedback && assumptionFeedback.comments ? assumptionFeedback.comments : [];
  const retCommentsCount = retComments.length;

  return {
    dislikes: currAssumption && currAssumption.dislike_count ? currAssumption.dislike_count : 0,
    likes: currAssumption && currAssumption.like_count ? currAssumption.like_count : 0,
    commentsCount: currAssumption && currAssumption.comment_count ? currAssumption.comment_count : retCommentsCount,
    status: currRating,
    comments: retComments
  };
}

export function updateScenarioSegmentFeedback(projectId, runId, vote) {
  return (dispatch, getState) => {
    const assumptionId = getScenarioLegacyAssumptionId(getState(), projectId, runId);

    const feedback = {
      feedback: vote
    };

    return ProjectAPI.submitScenarioFeedback(assumptionId, feedback).then((r) => {
      return dispatch(fetchProject(projectId));
    });
  };
}

export function postScenarioSegmentComment(projectId, runId, comment) {
  return (dispatch, getState) => {
    const assumptionId = getScenarioLegacyAssumptionId(getState(), projectId, runId);

    const feedback = {
      message: comment
    };

    return ProjectAPI.submitScenarioComment(assumptionId, feedback).then((r) => {
      // Shortcut for now
      return dispatch(fetchProject(projectId));
      // Would also need to update the comment count of the assumption
      return ProjectAPI.getScenarioComments(assumptionId).then((comments) => {
        const retFeedback = {
          ...getAssumptionFeedback(getState(), projectId, assumptionId),
          comments: comments
        };
        return dispatch(updateAssumptionFeedback(projectId, assumptionId, retFeedback));
      });
    });
  };
}

function fetchScenariosHelper(ids, fetchFunction) {
  return Promise.all(
    ids.map((aId) =>
      fetchFunction(aId).then((response) => {
        return {
          data: response,
          assumption_id: aId
        };
      })
    )
  );
}

export function fetchScenarioFeedback(projectId) {
  return (dispatch, getState) => {
    const project = getProject(getState(), projectId);
    const commentAssumptionIds = [];
    const feedbackAssumptionIds = [];

    project &&
      project.segments &&
      project.segments.forEach(
        (s) =>
          s.history &&
          s.history.assumptions &&
          s.history.assumptions.forEach((a) => {
            if (a.dislike_count || a.like_count) {
              feedbackAssumptionIds.push(a.id);
            }
            if (a.comment_count) {
              commentAssumptionIds.push(a.id);
            }
          })
      );

    return Promise.all([
      fetchScenariosHelper(commentAssumptionIds, ProjectAPI.getScenarioComments),
      fetchScenariosHelper(feedbackAssumptionIds, ProjectAPI.getScenarioFeedback)
    ]).then((values) => {
      const comments = values[0];
      const ratings = values[1];
      const feedback = {};
      comments.forEach((c) => {
        feedback[c.assumption_id] = {
          ...feedback[c.assumption_id],
          comments: c && c.data
        };
      });
      ratings.forEach((c) => {
        feedback[c.assumption_id] = {
          ...feedback[c.assumption_id],
          ratings: c && c.data && c.data[0]
        };
      });
      dispatch(setProjectFeedback(projectId, feedback));

      return Promise.resolve();
    });
  };
}

export function setProjectFeedback(id, feedback) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "feedback",
    value: feedback
  };
}

export function updateAssumptionFeedback(projectId, assumptionId, aFeedback) {
  return (dispatch, getState) => {
    const currFeedback = { ...getProjectFeedback(getState, projectId) };
    currFeedback[assumptionId] = aFeedback;
    return dispatch(setProjectFeedback(projectId, currFeedback));
  };
}

export function getProjectFeedback(state, projectId) {
  return getOptimizer(state, projectId) ? getOptimizer(state, projectId).feedback : {};
}

export function getAssumptionFeedback(state, projectId, assumptionId) {
  return (getProjectFeedback(state, projectId) || {})[assumptionId];
}

export function setIsAllowedInFrontier(runId, modelId, setIsAllowedInFrontier) {
  return (dispatch, getState) => {
    if (setIsAllowedInFrontier) {
      return dispatch(approveModel(runId, modelId));
    } else {
      return dispatch(rejectModel(runId, modelId));
    }
  };
}

export function reconciliateProjectRejectedModels(id) {
  return (dispatch, getState) => {
    const state = getState();

    const project = { ...getProject(state, id) };

    let hasUpdate = false;

    const segments =
      project.segments === undefined
        ? []
        : project.segments.map((s) => {
            if (s.job && s.job.job_id) {
              let data = Utils.get(s, ["job", "data"]);
              if (data === undefined) {
                return s;
              } else if (data.rejected === undefined) {
                data = {
                  ...data,
                  rejected: []
                };
              }

              const rejected = data.rejected;
              const segmentSetup = setupReducer.getSetup(state, s.job.job_id);
              const setupRejectedModels = Object.keys((segmentSetup && segmentSetup.ignoredModels) || {});
              const equalArrays = isEqual(setupRejectedModels, rejected);

              if (!equalArrays) {
                hasUpdate = true;

                const updatedData = {
                  ...s.job.data,
                  rejected: [...setupRejectedModels]
                };

                ProjectAPI.patchSegmentJob(s.job.id, { data: updatedData });
                return {
                  ...s,
                  job: {
                    ...s.job,
                    data: updatedData
                  }
                };
              }
            }

            return s;
          });
    if (hasUpdate) {
      dispatch(
        updateProject({
          ...project,
          segments: segments
        })
      );
    }
  };
}

// Model Approval (NOT IGNORE HOOKUP)
export function rejectModel(runId, modelId) {
  return (dispatch, getState) => {
    let retModelIds = [];
    if (typeof modelId === "string") {
      retModelIds = [modelId];
    } else if (modelId) {
      retModelIds = modelId;
    }
    const projectId = getProjectIdFromRunId(getState(), runId);

    const project = { ...getProject(getState(), projectId) };

    let hasUpdate = false;

    const segments = project.segments.map((s) => {
      if (s.job && s.job.job_id && s.job.job_id === runId) {
        let data = Utils.get(s, ["job", "data"]);

        if (data === undefined) {
          return s;
        } else if (data.rejected === undefined) {
          data = {
            ...data,
            rejected: []
          };
        }

        const rejected = data.rejected;

        if (rejected) {
          // const isAlreadyRejected = rejected.some(
          //   (rejectedModelId) => modelId == rejectedModelId
          // );

          const newRejected = [...rejected];
          retModelIds.forEach((mId) => {
            if (!rejected.includes(mId)) {
              newRejected.push(mId);
            }
          });

          const isAlreadyRejected = newRejected.length === rejected.length;

          if (!isAlreadyRejected) {
            hasUpdate = true;

            const updatedData = {
              ...s.job.data,
              rejected: newRejected
            };

            // optimistic update
            ProjectAPI.patchSegmentJob(s.job.id, { data: updatedData });

            return {
              ...s,
              job: {
                ...s.job,
                data: updatedData
              }
            };
          }
        }
      }

      return s;
    });

    if (hasUpdate) {
      dispatch(
        updateProject({
          ...project,
          segments: segments
        })
      );
    }
  };
}

// Model Approval (NOT IGNORE HOOKUP)
export function approveModel(runId, modelId) {
  return (dispatch, getState) => {
    let retModelIds = [];
    if (typeof modelId === "string") {
      retModelIds = [modelId];
    } else if (modelId) {
      retModelIds = modelId;
    }
    const projectId = getProjectIdFromRunId(getState(), runId);

    const project = { ...getProject(getState(), projectId) };

    let hasUpdate = false;

    const segments = project.segments.map((s) => {
      if (s.job && s.job.job_id && s.job.job_id === runId) {
        const rejected = Utils.get(s, ["job", "data", "rejected"]);

        if (rejected) {
          const updatedRejected = rejected.filter((rejectedModelId) => !retModelIds.includes(rejectedModelId));

          if (updatedRejected.length < rejected.length) {
            hasUpdate = true;

            const updatedData = {
              ...s.job.data,
              rejected: updatedRejected
            };

            // optimistic update
            ProjectAPI.patchSegmentJob(s.job.id, { data: updatedData });

            return {
              ...s,
              job: {
                ...s.job,
                data: updatedData
              }
            };
          }
        }
      }

      return s;
    });

    if (hasUpdate) {
      dispatch(
        updateProject({
          ...project,
          segments: segments
        })
      );
    }
  };
}

export function handleCreateDrilldown(id, drilldownColumn) {
  return (dispatch, getState) => {
    const state = getState();

    const firstRunId = getMarketingRunId(state, id);
    const secondRunId = getSalesRunId(state, id);

    const firstRun = runReducer.getJob(state, firstRunId);
    const secondRun = runReducer.getJob(state, secondRunId);

    const firstDataset = homeReducer.getDataset(state, firstRun.datasetId);
    const secondDataset = homeReducer.getDataset(state, secondRun.datasetId);

    const retModelGroups = {
      models: firstDataset.fields[drilldownColumn.value].data.map((c) => ({
        job_id: null,
        model_name: null,
        conditions: [{ column: drilldownColumn.value, value: c.value, operand: "=" }]
      }))
    };

    /*
    const retModelGroups = {
      models: [
        {
          job_id: null,
          model_name: null,
          conditions: specialVals
            ? specialVals.map(s => ({ column: s.field, value: s.fieldValue, operand: "=" }))
            : []
        },
        ...modelGroup.models
      ]
    };

    return ModelGroupAPI.patchModelGroup(modelGroup.id, retModelGroups).then(response =>
      dispatch(updateModelGroup(originalId, transformModelGroup(response)))
    );
    */
  };
}

export function getSegmentSetups(state, id) {
  const project = getProject(state, id);

  let segmentSetups = {};

  (project?.segments ?? []).map((s) => {
    const runId = s?.job?.job_id;
    const rSetup = setupReducer.getSetup(state, runId);
    const value = s?.conditions?.[0]?.value ?? "Overall";

    segmentSetups[value] = rSetup;
  });

  return segmentSetups;
}

export function getSetupFromSegment(state, id, segmentId) {
  const segment = getSegment(state, id, segmentId);

  return segment && segment.assumption && segment.assumption.data ? segment.assumption.data : {};
}

export function getSplitColumn(state, id) {
  const overallSetupId = getOverallRunId(state, id);
  return setupReducer.getSplitColumn(state, overallSetupId);
}

export function getBalancedSetup(state, id, isBalancedOriginalSetup) {
  const project = getProject(state, id);
  if (project === undefined) {
    return undefined;
  }
  const runId = getRunId(state, id);
  const isContinuous = getIsContinuous(state, id);

  const segments = project.segments
    .filter((s, i) => s.conditions.length !== 0)
    .map((s) => ({
      conditions: s.conditions,
      job: s.job,
      assumption: {
        data: s.job ? setupReducer.getSetup(state, s.job.job_id) : { customValues: {} }
      }
    }));

  const oSegments = project.segments.map((s) => ({
    conditions: s.conditions,
    job: s.job,
    assumption: {
      data: s.job ? setupReducer.getSetup(state, s.job.job_id) : { customValues: {} }
    }
  }));

  const simpleValues = ["conversionGain", "conversionCost", "conversionMissed", "conversionSaved"];

  const simpleCounts = ["truePositive", "falsePositive", "trueNegative", "falseNegative"];

  let overallSetup = {
    ...setupReducer.getSetup(state, runId),
    ignored: setupReducer.getIgnored(state, runId),
    ignoredValue: setupReducer.getIgnoredValue(state, runId),
    customValues: {
      ...setupReducer.getSetup(state, runId).customValues
    }
  };

  if (segments.length === 0 || oSegments.length === 0) {
    return overallSetup;
  }

  segments.forEach((s) => {
    const setup = s.assumption.data;

    if (setup.showBusiness === true) {
      overallSetup.showBusiness = true;
    }

    if (setup.showImpact === true) {
      overallSetup.showImpact = true;
    }
  });

  if (overallSetup.splitColumn === undefined) {
    overallSetup.splitColumn = segments
      .filter((s) => s.conditions.length > 0)
      .flatMap((s) => s.conditions.map((c) => c.column))
      .find((x) => x !== undefined);
  }

  const totalCount = isContinuous
    ? getOverallExpectedContinuousCount(state, id)
    : segments.reduce((a, b) => {
        return isBalancedOriginalSetup
          ? b.assumption !== null &&
              b.assumption !== undefined &&
              b.assumption.data !== undefined &&
              b.assumption.data.defaultSetup !== undefined &&
              a +
                (Number(b.assumption.data.defaultSetup.data.truePositive) +
                  Number(b.assumption.data.defaultSetup.data.falsePositive) +
                  Number(b.assumption.data.defaultSetup.data.trueNegative) +
                  Number(b.assumption.data.defaultSetup.data.falseNegative))
          : b.assumption !== null &&
              b.assumption !== undefined &&
              a +
                (Number(b.assumption.data.truePositive) +
                  Number(b.assumption.data.falsePositive) +
                  Number(b.assumption.data.trueNegative) +
                  Number(b.assumption.data.falseNegative));
      }, 0);

  simpleCounts.forEach((s) => {
    overallSetup[s] = segments.reduce((a, b) => {
      const countPath =
        b.assumption === null || b.assumption === undefined || b.assumption.data === undefined
          ? {}
          : b.assumption.data.baselineCounts
            ? b.assumption.data.baselineCounts
            : b.assumption.data;

      return (
        b.assumption !== null &&
        b.assumption !== undefined &&
        b.assumption.data !== undefined &&
        b.assumption.data.defaultSetup !== undefined &&
        a + (isBalancedOriginalSetup ? Number(b.assumption.data.defaultSetup.data[s]) : Number(countPath[s]))
      );
    }, 0);
  });

  simpleValues.forEach((s) => {
    let allSame = true;
    segments.forEach((x) => {
      if (x.assumption && x.assumption.data[s] !== oSegments[0].assumption.data[s]) {
        allSame = false;
      }
    });

    if (allSame !== true) {
      overallSetup[s] = segments.reduce((a, b) => {
        if (b.assumption === null || b.assumption === undefined || (isContinuous && !b.job)) {
          return false;
        }

        const segmentCount = isContinuous
          ? setupReducer.getExpectedCountContinuous(state, b.job.job_id) === undefined
            ? runReducer.getContinuousCount(state, b.job.job_id)
            : Number(setupReducer.getExpectedCountContinuous(state, b.job.job_id))
          : Number(b.assumption.data.truePositive) +
            Number(b.assumption.data.falsePositive) +
            Number(b.assumption.data.trueNegative) +
            Number(b.assumption.data.falseNegative);
        return a + (Number(b.assumption.data[s]) * segmentCount) / totalCount;
      }, 0);
    }
  });

  if (overallSetup.customValues) {
    Object.keys(overallSetup.customValues).forEach((c) => {
      let allSame = true;
      segments.forEach((x) => {
        if (
          oSegments &&
          oSegments[0] &&
          oSegments[0].assumption &&
          oSegments[0].assumption.data &&
          oSegments[0].assumption.data.customValues &&
          x.assumption &&
          x.assumption.data &&
          x.assumption.data.customValues &&
          x.assumption.data.customValues[c] !== oSegments[0].assumption.data.customValues[c]
        ) {
          allSame = false;
        }
      });

      if (allSame !== true) {
        overallSetup.customValues[c] = segments.reduce(
          (a, b) =>
            b.assumption !== null &&
            b.assumption !== undefined &&
            b.assumption.data &&
            b.assumption.data.customValues &&
            a +
              (Number(b.assumption.data.customValues[c]) *
                (Number(b.assumption.data.truePositive) +
                  Number(b.assumption.data.falsePositive) +
                  Number(b.assumption.data.trueNegative) +
                  Number(b.assumption.data.falseNegative))) /
                totalCount,
          0
        );
      }
    });
  }

  if (isContinuous) {
    const overallRunId = getOverallRunId(state, id);
    overallSetup.baselineCountContinuous = setupReducer.getBaselineCountContinuous(state, overallRunId, id);
    overallSetup.baselineValueContinuous = setupReducer.getBaselineValueContinuous(state, overallRunId, id);
    overallSetup.expectedCountContinuous = setupReducer.getExpectedCountContinuous(state, overallRunId, id);
    overallSetup.expectedValueContinuous = setupReducer.getExpectedValueContinuous(state, overallRunId, id);
    overallSetup.currentProfit = setupReducer.getCurrentContinuousProfit(state, overallRunId, id);
    overallSetup.continuousPercent = setupReducer.getCurrentContinuousPercent(state, overallRunId, id);
  }

  return overallSetup;
}

export function updateShowShortListed(id, value) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "showShortListed",
    value: value
  };
}

export function updateShowPending(id, value) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "showPending",
    value: value
  };
}

export function updateRunPlotType(id, runPlotType) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "runPlotType",
    value: runPlotType
  };
}

export function updateSliderView(id, value) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "sliderView",
    value: value
  };
}

export function updateHasInit(id, hasInit) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "hasInit",
    value: hasInit
  };
}

export function updateIsLoadingProject(id, isLoading) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "isLoading",
    value: isLoading
  };
}

export function updateUsePercentages(id, value) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "usePercentages",
    value: value
  };
}

export function updateShowBaselines(id, value) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "showBaselines",
    value: value
  };
}

export function updateIsLoadingSharing(id, isLoadingSharing) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "isLoadingSharing",
    value: isLoadingSharing
  };
}

export function updateApplyOverall(id, value) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "applyOverall",
    value: value
  };
}

export function updateIsLoadingCompare(id, isLoadingCompare) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "isLoadingCompare",
    value: isLoadingCompare
  };
}

export function updateLinkedProjectId(id, linkedProjectId) {
  return (dispatch, getState) => {
    const runIds = getAllJobIdsFromOptimizer(getState(), id);

    runIds.forEach((runId) => {
      dispatch(setupReducer.updateLinkedProjectId(runId, linkedProjectId));
      dispatch(setupReducer.updateLinkedScenarioId(runId, undefined));
    });

    if (linkedProjectId !== undefined) {
      dispatch(unifiedProjectReducer.updateIsInit(linkedProjectId, false));
      return dispatch(init(linkedProjectId, undefined, id, true)).then((r) => {
        if (getIsContinuous(getState(), linkedProjectId)) {
          toast("Cannot link a project with a numerical outcome. Please select a different project.", {
            type: toast.TYPE.ERROR,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 7500
          });
          runIds.forEach((runId) => {
            dispatch(setupReducer.updateLinkedProjectId(runId, undefined));
            dispatch(setupReducer.updateLinkedScenarioId(runId, undefined));
          });
          return Promise.resolve();
        }
        const linkedSettings = getOptimizer(getState(), linkedProjectId);
        const scenId =
          linkedSettings && linkedSettings.originalScenarios && linkedSettings.originalScenarios[0]
            ? linkedSettings.originalScenarios[0].id
            : undefined;
        if (scenId !== undefined) {
          dispatch(updateLinkedScenarioId(id, scenId));
        }
        return Promise.resolve();
      });
    }
  };
}

export function applyBoost(projectId, linkedProjectId) {
  return (dispatch, getState) => {
    const jobIds = getAllJobsIdsFromProject(getState(), linkedProjectId);

    const checkBalance = getCheckBalance(getState(), projectId);

    const runId = checkBalance?.highestSubgroup?.runId;

    if (runId !== undefined) {
      let setup = setupReducer.getSetup(getState(), runId);

      setup.conversionGain = setup.conversionGain * 2;

      dispatch(setupReducer.updateSetup(runId, setup));
    } else {
      (jobIds ?? []).forEach((jobId) => {
        let setup = setupReducer.getSetup(getState(), jobId);

        setup.conversionGain = setup.conversionGain * 2;

        dispatch(setupReducer.updateSetup(jobId, setup));
      });
    }

    dispatch(resetBudget(linkedProjectId));

    dispatch(updateSetupFromLinked(projectId));
  };
}

export function updateLinkedScenarioId(id, linkedScenarioId) {
  return (dispatch, getState) => {
    const linkedProject = getLinkedProject(getState(), id);

    let linkedIndex = -1;

    linkedProject &&
      linkedProject.settings &&
      linkedProject.settings.originalScenarios &&
      linkedProject.settings.originalScenarios.forEach((s, i) => {
        if (s.id === linkedScenarioId) {
          linkedIndex = i;
        }
      });

    if (linkedIndex !== -1) {
      const linkedScenario = linkedProject.settings.scenarios[linkedIndex];

      dispatch(viewScenario(linkedProject.id, linkedScenario, linkedIndex));
    }

    const runIds = getAllJobIdsFromOptimizer(getState(), id);

    runIds.forEach((runId) => dispatch(setupReducer.updateLinkedScenarioId(runId, linkedScenarioId)));

    return dispatch(updateSetupFromLinked(id));
  };
}

export function updateLinkedProjectOutputMode(id, mode = BALANCER_OUTPUT.ALL_POSITIVE) {
  return (dispatch, getState) => {
    const runIds = getAllJobIdsFromOptimizer(getState(), id);

    runIds.forEach((runId) => {
      dispatch(setupReducer.updateLinkedProjectOutputMode(runId, mode));
    });

    dispatch(updateSetupFromLinked(id));
  };
}

export function updateSetupFromLinked(id) {
  return (dispatch, getState) => {
    const project = getProject(getState(), id);

    const linkedProject = getLinkedProject(getState(), id);

    const linkedProjectOutputMode = getLinkedProjectOutputMode(getState(), id);

    project &&
      project.segments &&
      project.segments.forEach((s, i) => {
        const runId = s && s.job && s.job.job_id !== undefined ? s.job.job_id : undefined;

        if (runId !== undefined) {
          const outcomePercentage = setupReducer.getOutcomePercentage(getState(), runId);

          const setup = JSON.parse(JSON.stringify(setupReducer.getSetup(getState(), runId)));

          if (
            setup &&
            setup.linkedProjectId &&
            setup.linkedRuns &&
            Object.keys(setup.linkedRuns).filter((r) => setup.linkedRuns[r] !== undefined).length > 0
          ) {
            const scalar = Object.keys(setup.linkedRuns)
              .filter((r) => setup.linkedRuns[r] !== undefined)
              .reduce((a, b) => {
                if (
                  linkedProject &&
                  linkedProject.settings &&
                  linkedProject.settings.segmentConfusionMatrices &&
                  setup.linkedRuns[b] &&
                  linkedProject.settings.segmentConfusionMatrices[setup.linkedRuns[b].linkedSegmentId]
                ) {
                  let currValue = 0;
                  switch (linkedProjectOutputMode) {
                    case BALANCER_OUTPUT.TRUE_POSITIVE:
                      currValue =
                        linkedProject.settings.segmentConfusionMatrices[setup.linkedRuns[b].linkedSegmentId].tp;
                      break;
                    case BALANCER_OUTPUT.ALL_POSITIVE:
                    default:
                      currValue =
                        linkedProject.settings.segmentConfusionMatrices[setup.linkedRuns[b].linkedSegmentId].tp +
                        linkedProject.settings.segmentConfusionMatrices[setup.linkedRuns[b].linkedSegmentId].fp;
                      break;
                  }

                  return a + (currValue * setup.linkedRuns[b].percentage) / 100;
                } else {
                  return a;
                }
              }, 0);

            const total = setupReducer.getDefaultTotal(getState(), runId);

            if (isNaN(scalar) || isNaN(total) || total === 0) {
              setup.truePositive = 0;
              setup.falsePositive = 0;
              setup.trueNegative = 0;
              setup.falseNegative = 0;
            } else {
              setup.truePositive = Math.round(
                setup.defaultSetup
                  ? (setup.defaultSetup.data.truePositive * scalar) / total
                  : (setup.truePositive * scalar) / total
              );
              setup.falsePositive = Math.round(
                setup.defaultSetup
                  ? (setup.defaultSetup.data.falsePositive * scalar) / total
                  : (setup.falsePositive * scalar) / total
              );
              setup.trueNegative = Math.round(
                setup.defaultSetup
                  ? (setup.defaultSetup.data.trueNegative * scalar) / total
                  : (setup.trueNegative * scalar) / total
              );
              setup.falseNegative = Math.round(
                setup.defaultSetup
                  ? (setup.defaultSetup.data.falseNegative * scalar) / total
                  : (setup.falseNegative * scalar) / total
              );
            }

            dispatch(setupReducer.updateSetup(runId, setup));

            if (outcomePercentage !== undefined) {
              dispatch(updateOutcomePercentage(id, runId, outcomePercentage));
            }

            dispatch(resetBudget(id, false, true));
          }
        }
      });
  };
}

export function updateFirstLinkSetup(id, linkedProjectId) {
  return (dispatch, getState) => {
    const runIds = getAllJobIdsFromOptimizer(getState(), id);

    const linkedProject = getProject(getState(), linkedProjectId);

    runIds.forEach((runId) => {
      const setup = setupReducer.getSetup(getState(), runId);

      if (linkedProject && linkedProject.segments && setup && setup.linkedRuns === undefined) {
        const linkedRuns = {};

        const segments = linkedProject.segments.filter(
          (s) => linkedProject.segments.length === 1 || (s.conditions && s.conditions.length > 0)
        );

        segments.forEach((s) => {
          linkedRuns[s.id] = {
            linkedSegmentId: s.id,
            percentage: 0,
            name: s.name,
            linkedRunId: s.job.job_id
          };
        });

        dispatch(setupReducer.updateLinkedRuns(runId, linkedRuns));
      }
    });

    return dispatch(updateSetupFromLinked(id));
  };
}

export function updateHasLinkedProject(id, hasLinkedProject) {
  return (dispatch, getState) => {
    const state = getState();
    const overallRunId = getOverallRunId(state, id);
    let needsBudgetReset = false;

    const pRuns = getProjectRuns(state, id);
    pRuns.forEach((run) => {
      const runId = run.id;
      if (hasLinkedProject) {
        if (!setupReducer.getIsRealityHammer(state, runId)) {
          dispatch(setupReducer.updateIsRealityHammer(runId, true));
          needsBudgetReset = true;
        }
      } else {
        const currTotal = setupReducer.getTotal(state, runId);
        dispatch(setupReducer.updateScaledTotal(undefined, runId, currTotal, true));
      }
    });

    if (needsBudgetReset) {
      dispatch(resetBudget(id, false, true));
    }

    dispatch(setupReducer.updateHasLinkedProject(overallRunId, hasLinkedProject));
  };
}

export function updateProjectSetting(id, key, value) {
  return (dispatch, getState) => {
    const runId = getRunId(getState(), id);
    const panelMode = runReducer.getPanelMode(getState(), runId);

    if (value === true && panelMode !== runReducer.PANEL_MODES.CLOSED) {
      dispatch(runReducer.updatePanelMode(runId, null, runReducer.PANEL_MODES.CLOSED));
    }

    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: key,
      value: value
    });
  };
}

export function updateIsScoringFile(id, isScoringFile) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "isScoringFile",
    value: isScoringFile
  };
}

export function updateIsSlowMode(id, isSlowMode) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "isSlowMode",
    value: isSlowMode
  };
}

export function updateDrilldownValue(id, runId, value) {
  return (dispatch, getState) => {
    const state = getState();
    let retRunId = runId;
    if (!retRunId && value === "Overall") {
      const segments = getProject(state, id) && getProject(state, id).segments;
      for (let i = 0; segments && i < segments.length; i++) {
        if (segments[i] && (!segments[i].conditions || segments[i].conditions.length === 0)) {
          retRunId = segments[i].job && segments[i].job.job_id;
          break;
        }
      }
    }

    const optimizeSetup = chatGPTReducer.getOptimizeSetup(getState());

    if (optimizeSetup !== undefined) {
      dispatch(chatGPTReducer.updateOptimizeSetup({ ...optimizeSetup, runId: retRunId }));
    }

    return dispatch({
      type: UDPATE_DRILLDOWN,
      key: id,
      innerId: retRunId,
      runId: runId,
      drilldownValue: value
    });

    /*
    dispatch(updateInnerId(id, retRunId));
    dispatch(setRunId(id, retRunId));
    dispatch(setDrilldownValue(id, value));
    */
  };
}

export function updateToOverall(id) {
  return (dispatch, getState) => {
    const modelGroups = getModelGroups(getState(), id);

    if (modelGroups && modelGroups.drilldownValues && modelGroups.drilldownValues[0]) {
      const overall = modelGroups.drilldownValues[0];
      dispatch(updateDrilldownValue(id, overall.runId, overall.value));
    }

    return Promise.resolve();
  };
}

export function getModelCount(state, id, runId) {
  if (!runId || getIsOverallSegment(state, id, runId)) {
    const jobKeys = getAllJobIdsFromOptimizer(state, id, false);
    if (!jobKeys || jobKeys.length < 1) {
      return 0;
    }
    return jobKeys.reduce((acc, currRunId) => {
      const modelArr = runReducer.getModelsArr(state, currRunId).filter((model) => model.id !== "Current State");
      const numModels = modelArr ? modelArr.length : 0;

      return acc + numModels;
    }, 0);
  } else {
    const modelArr = runReducer.getModelsArr(state, runId).filter((model) => model.id !== "Current State");
    return modelArr ? modelArr.length : 0;
  }
}

export function getTotalModelCount(state, id) {
  const jobKeys = getAllJobIdsFromOptimizer(state, id, false);

  return jobKeys.reduce(
    (a, b) => a + runReducer.getModelsArr(state, b).filter((model) => model.id !== "Current State").length,
    0
  );
}

export function getOptimalProfit(state, id) {
  return undefined;
}

export function getOptimalCost(state, id) {
  return undefined;
}

export function getIsScenarioDeployed(state, id, indexOverride = undefined) {
  const selectedScenario = getSelectedScenario(state, id, indexOverride);

  return selectedScenario?.isApproved ?? false;
}

export function getHasSameScenario(state, id, onlyCheckViewingScenario = false, onlyCheckDeployedScenario = false) {
  const project = getProject(state, id);

  if (project?.settings?.scenarios === undefined) {
    return false;
  }

  if (project.settings.scenarios.length === 0) {
    return true;
  }

  const modelGroups = getModelGroups(state, id);

  if (
    project === undefined ||
    project.settings === undefined ||
    project.settings.scenarios === undefined ||
    modelGroups === undefined ||
    modelGroups.drilldownValues === undefined
  ) {
    return false;
  }

  let hasSameScenario = project.settings.scenarios.length === 0 ? true : false;

  let scenariosToCheck = project.settings.scenarios;

  const viewingScenarioIndex = getViewingScenarioIndex(state, id);
  if (onlyCheckViewingScenario && !isNaN(viewingScenarioIndex)) {
    scenariosToCheck = [project.settings.scenarios[viewingScenarioIndex]];
  }
  if (onlyCheckDeployedScenario) {
    const deployedScenarioIndex = getDeployedScenarioIndex(state, id);
    if (!isNaN(deployedScenarioIndex)) {
      const deployedScenario = project.settings.scenarios[deployedScenarioIndex];
      // if (onlyCheckViewingScenario && !isNaN(viewingScenarioIndex)) {
      //   scenariosToCheck.push(deployedScenario)
      // } else
      scenariosToCheck = [deployedScenario];
    }
  }

  scenariosToCheck &&
    scenariosToCheck.forEach((s) => {
      let isSameScenario = true;
      Object.keys(s).forEach((k, i) => {
        let targetDrilldownValue = modelGroups.drilldownValues.find((dv) => dv?.value === k);
        if (
          !targetDrilldownValue ||
          isSameSetup(s[k]?.setup?.data, setupReducer.getSetup(state, targetDrilldownValue?.runId)) === false
        ) {
          isSameScenario = false;
        }
      });

      if (isSameScenario === true) {
        hasSameScenario = true;
      }
    });

  return hasSameScenario;
}

export function getAreJobsInSync(state, id, runId) {
  const modelGroups = getModelGroups(state, id);

  const currentJobIds =
    modelGroups &&
    modelGroups.drilldownValues &&
    modelGroups.drilldownValues.length > 0 &&
    modelGroups.drilldownValues.map((group) => {
      const isOverallSegment = getIsOverallSegment(state, id, group.runId);
      const frontierJobId = isOverallSegment
        ? group.runId
        : group.setup && group.setup.frontier && group.setup.frontier.length > 0 && group.setup.frontier[0].job_id;
      return { currentRunId: group.runId, frontierJobId };
    });
  let jobsInSync = true;

  const currentScenarioIndex = getViewingScenarioIndex(state, id);

  if (currentScenarioIndex !== undefined && currentJobIds) {
    currentJobIds.forEach((segment) => {
      if (segment.currentRunId !== segment.frontierJobId) {
        jobsInSync = false;
      }
    });

    return jobsInSync;
  }

  return true;
}

export function getIsContinuous(state, id) {
  const runId = getRunId(state, id);
  const isContinuous = runReducer.getIsContinuous(state, runId);
  return isContinuous;
}

export function getIsLoading(state, id) {
  const runId = getRunId(state, id);
  return getOptimizer(state, id) === undefined || Object.keys(runReducer.getJob(state, runId)).length === 0;
}

export function getBlueprintId(state, id) {
  const innerId = getInnerId(state, id);

  const setup = setupReducer.getSetup(state, innerId);

  return setup ? setup.blueprintId : undefined;
}

export function getHasLinkedProject(state, id) {
  const overallRunId = getOverallRunId(state, id);

  return setupReducer.getHasLinkedProject(state, overallRunId);
}

export function getLinkedProjectId(state, id) {
  const overallRunId = getOverallRunId(state, id);

  return setupReducer.getLinkedProjectId(state, overallRunId);
}

export function getBestModel(state, id, runIdOverride = false) {
  const runId = runIdOverride ? runIdOverride : getRunId(state, id);
  return getIsOverallSegment(state, id, runId) ? getBalancedModel(state, id) : runReducer.getFirstModel(state, runId);
}

export function getProjectSetup(state, id, runIdOverride = false) {
  const innerId = runIdOverride ? runIdOverride : getInnerId(state, id);

  const isOverallSegment = getIsOverallSegment(state, id, innerId, true);

  if (isOverallSegment && isFinite(id)) {
    return getBalancedSetup(state, id);
  }

  const isContinuous = setupReducer.getIsContinuous(state, innerId);

  if (isContinuous) {
    return {
      ...setupReducer.getSetup(state, innerId),
      baselineCountContinuous: setupReducer.getBaselineCountContinuous(state, innerId, id),
      baselineValueContinuous: setupReducer.getBaselineValueContinuous(state, innerId, id),
      expectedCountContinuous: setupReducer.getExpectedCountContinuous(state, innerId, id),
      expectedValueContinuous: setupReducer.getExpectedValueContinuous(state, innerId, id),
      currentProfit: setupReducer.getCurrentContinuousProfit(state, innerId, id),
      continuousPercent: setupReducer.getCurrentContinuousPercent(state, innerId, id)
    };
  }

  const retSetup = {
    ...setupReducer.getSetup(state, innerId),
    ignored: setupReducer.getIgnored(state, innerId),
    ignoredValue: setupReducer.getIgnoredValue(state, innerId)
  };

  return retSetup;
}

export function getOptimized(state, id, runIdOverride = false) {
  const runId = runIdOverride ? runIdOverride : getRunId(state, id);
  const innerId = runIdOverride ? runIdOverride : getInnerId(state, id);

  if (getOptimizer(state, id) === undefined || Object.keys(runReducer.getJob(state, runId)).length === 0) {
    return undefined;
  }

  const setup = setupReducer.getSetup(state, runId);

  const project = getProject(state, id);

  const isLoading =
    project &&
    project.settings &&
    project.settings.scenarios &&
    project.settings.scenarioStats &&
    project.settings.scenarios.length <= Object.keys(project.settings.scenarioStats).length
      ? false
      : true;

  const isContinuous = runReducer.getIsContinuous(state, runId);

  const innerSegment = getInnerSegment(state, id);

  const unitLabel = setup && setup.unitType !== undefined ? setup.unitType : "$";
  const isCurrencyUnitType = CURRENCY_UNIT_TYPES[unitLabel] || false;

  const cSegment = getSegmentByRunId(state, id, runId);

  const first = getLinkedData(state, id);
  const isOverallSegment = getIsOverallSegment(state, id, innerId, true);

  const runIdsToCheckForCreation = isOverallSegment ? getAllJobsIdsFromProject(state, id) : [innerId];

  const isCreating = runIdsToCheckForCreation.some(
    (segmentRunId) =>
      Object.keys(runReducer.getJob(state, segmentRunId)).length > 0 &&
      runReducer.hasModels(state, segmentRunId) === false
  );

  return {
    label: "Marketing",
    projectId: id,
    id: runId,
    innerId: innerId,
    segmentId: getSegmentIdByRunId(state, id, runId),
    isLoading: project && project.settings && project.settings.isLoading == false ? false : true,
    isCreating: isCreating,
    errors: runReducer.getErrors(state, runId),
    unitCost: getUnitCost(state, runId),
    budget: getSegmentBudgetByRunId(state, id, runId),
    minBudget: getSegmentMinBudgetByRunId(state, id, runId),
    maxBudget: getSegmentMaxBudgetByRunId(state, id, runId),
    capacity: getCapacity(state, id, runId),
    confusionMatrix: getConfusionMatrix(state, runId),
    constrainedConfusionMatrix: getIsOverallSegment(state, id, runId)
      ? getBalancedCCM(state, id)
      : getConstrainedConfusionMatrix(state, id, runId),
    totalWon: getTotalWon(state, id, runId),
    currentTotalPursued: getCurrentTotalPursued(state, id, runId),
    currentTotalWon: getCurrentTotalWon(state, id, runId),
    totalPursued: getTotalPursued(state, id, runId),
    totalProspects: getTotalProspects(state, id, runId),
    totalCost: getTotalCost(state, id, runId),
    bestModel: getBestModel(state, id, runId),
    currentSpend: getCurrentSpend(state, id, runId),
    setup: getProjectSetup(state, id, innerId),
    blueprintId: setup ? setup.blueprintId : undefined,
    blueprint: setup ? blueprintReducer.getBlueprint(state, setup.blueprintId) : undefined,
    originalSetup:
      isOverallSegment && project && project.segments && project.segments.length > 1
        ? getBalancedSetup(state, id, true)
        : innerSegment &&
            innerSegment.history &&
            innerSegment.history.assumptions &&
            innerSegment.history.assumptions.length > 0
          ? innerSegment.history.assumptions[0].data
          : setupReducer.getSetup(state, innerId),
    deployedSetup: cSegment && cSegment.assumption && cSegment.assumption.data,

    totalScale: setupReducer.getTotalScale(state, innerId),
    dataset: setupReducer.getDatasetFromSetup(state, innerId),
    profit: getProfit(state, id, runId, true),
    revenue: getTotalRevenue(state, id, runId),

    originalOutcomePercentage: getOriginalOutcomePercentage(state, id, innerId),
    outcomePercentage: getOutcomePercentage(state, id, innerId),
    currentOutcomePercentage: getCurrentStateOutcomePercentage(state, id, innerId),

    originalCapValue: getCapValue(state, id, runId, true),

    currentRevenue: getCurrentRevenue(state, id, runId),
    revenueDelta: getTotalRevenue(state, id, runId) - getCurrentRevenue(state, id, runId),

    currentProfit: getCurrentProfit(state, id, runId),
    profitDelta: getProfit(state, id, runId, true) - getCurrentProfit(state, id, runId),

    revenueLabel: setup && setup.advancedRevenueLabel !== undefined ? setup.advancedRevenueLabel : "Revenue",
    resourcingLabel: setup && setup.advancedCostLabel !== undefined ? setup.advancedCostLabel : "Resourcing",
    profitLabel: setup && setup.netImpactLabel !== undefined ? setup.netImpactLabel : "Profit",
    unitLabel: unitLabel,
    unitLabelShort: isCurrencyUnitType ? unitLabel : "",
    isCurrencyUnitType: isCurrencyUnitType,
    currentOptimalProfit: "TBD",

    activeSegment: getActiveSegment(state, id),
    innerSegment: innerSegment,
    isBusinessReality: runReducer.getIsBusinessReality(state, innerId),

    isRealityHammer: setupReducer.getIsRealityHammer(state, innerId),

    isCreatingSplit: getIsCreatingSplit(state, id),

    hasSameScenario: getHasSameScenario(state, id),

    overEstimate: isContinuous ? getOverEstimate(state, id, runId) : undefined,
    underEstimate: isContinuous ? getUnderEstimate(state, id, runId) : undefined,
    overestimateLabel: setup && setup.overestimateLabel !== undefined ? setup.overestimateLabel : "Over Estimate",
    underestimateLabel: setup && setup.underestimateLabel !== undefined ? setup.underestimateLabel : "Under Estimate",

    continuousCount: isContinuous ? setupReducer.getBaselineCountContinuous(state, innerId) : undefined,
    continuousValue: isContinuous ? setupReducer.getBaselineValueContinuous(state, innerId) : undefined,

    overallContinuousCount: isContinuous ? getOverallContinuousCount(state, id) : undefined,
    overallContinuousValue: isContinuous ? getOverallContinuousValue(state, id) : undefined,
    overallExpectedContinuousCount: isContinuous ? getOverallExpectedContinuousCount(state, id) : undefined,
    overallExpectedContinuousValue: isContinuous ? getOverallExpectedContinuousValue(state, id) : undefined,
    overallCurrentProfit: isContinuous ? getOverallCurrentProfit(state, id) : undefined,
    overallContinuousPercent: isContinuous ? getOverallContinuousPercent(state, id) : undefined,

    overallSegmentId: getOverallSegmentId(state, id),

    /*
      source: 0,
      target: 3,
      value: Math.round(optimized.first.totalWon)
    },
    {
      source: 0,
      target: 2,
      value: Math.round(optimized.first.totalPursued - optimized.first.totalWon)
    },
    {
      source: 1,
      target: 1,
      value:
        isHide === true ? 0 : Math.round(optimized.first.totalProspects - optimized.first.totalPursued)
    },

    { source: 3, target: 4, value: Math.round(optimized.second.totalWon) },
    {
      source: 3,
      target: 5,
      value: Math.round(optimized.second.totalPursued - optimized.second.totalWon)
    },
    {
      source: 3,
      target: 6,
      value: Math.round(optimized.first.totalWon - optimized.second.totalPursued)
    }
    */

    first: first,
    second: {
      totalPursued: getTotalPursued(state, id, runId),
      totalWon: getTotalWon(state, id, runId),
      setup: setup
    },
    hasLinkedProject: getHasLinkedProject(state, id),
    isLinkedProjectLoading: getIsLinkedProjectLoading(state, id)
  };
}

export function getOverallSegmentId(state, id) {
  const project = getProject(state, id);

  return (
    project &&
    project.segments &&
    project.segments.length &&
    (project.segments.length === 1
      ? project.segments[0].id
      : (project.segments.find((elem) => elem.conditions && !elem.conditions.length) || {}).id)
  );
}

export function getOverallExpectedContinuousCount(state, id) {
  const overallRunId = getOverallRunId(state, id);
  return setupReducer.getExpectedCountContinuous(state, overallRunId, id);
}

export function getOverallExpectedContinuousValue(state, id) {
  const overallRunId = getOverallRunId(state, id);
  return setupReducer.getExpectedValueContinuous(state, overallRunId, id);
}

export function getOverallCurrentProfit(state, id) {
  const overallRunId = getOverallRunId(state, id);
  return setupReducer.getCurrentContinuousProfit(state, overallRunId, id);
}

export function getOverallContinuousPercent(state, id) {
  const overallRunId = getOverallRunId(state, id);
  return setupReducer.getCurrentContinuousPercent(state, overallRunId, id);
}

export function getOverallContinuousCount(state, id) {
  const overallRunId = getOverallRunId(state, id);
  return setupReducer.getBaselineCountContinuous(state, overallRunId, id);
}

export function getOverallContinuousValue(state, id) {
  const overallRunId = getOverallRunId(state, id);
  return setupReducer.getBaselineValueContinuous(state, overallRunId, id);
}

export function getOverEstimate(state, id, runId) {
  const project = getProject(state, id);

  const isOverall = getIsOverallSegment(state, id, runId);

  let overEstimate = 0;

  if (
    project &&
    project.segments.length === 1 &&
    project.segments[0].job &&
    project.segments[0].job.job_id !== undefined
  ) {
    const bestModel = runReducer.getFirstModel(state, project.segments[0].job.job_id);

    overEstimate += bestModel && bestModel.metrics ? bestModel.metrics.overImpact : 0;
  } else if (project && project.segments.length > 1 && isOverall) {
    project.segments
      .filter((s) => s.conditions.length > 0)
      .forEach((s) => {
        if (s.job && s.job.job_id !== undefined) {
          const bestModel = runReducer.getFirstModel(state, s.job.job_id);
          overEstimate += bestModel && bestModel.metrics ? bestModel.metrics.overImpact : 0;
        }
      });
  } else if (project && project.segments.length > 1) {
    const bestModel = runReducer.getFirstModel(state, runId);
    overEstimate += bestModel && bestModel.metrics ? bestModel.metrics.overImpact : 0;
  }

  return overEstimate;
}

export function getUnderEstimate(state, id, runId) {
  const project = getProject(state, id);

  const isOverall = getIsOverallSegment(state, id, runId);

  let underEstimate = 0;

  if (
    project &&
    project.segments.length === 1 &&
    project.segments[0].job &&
    project.segments[0].job.job_id !== undefined
  ) {
    const bestModel = runReducer.getFirstModel(state, project.segments[0].job.job_id);

    underEstimate += bestModel && bestModel.metrics ? bestModel.metrics.underImpact : 0;
  } else if (project && project.segments.length > 1 && isOverall) {
    project.segments
      .filter((s) => s.conditions.length > 0)
      .forEach((s) => {
        if (s.job && s.job.job_id !== undefined) {
          const bestModel = runReducer.getFirstModel(state, s.job.job_id);
          underEstimate += bestModel && bestModel.metrics ? bestModel.metrics.underImpact : 0;
        }
      });
  } else if (project && project.segments.length > 1) {
    const bestModel = runReducer.getFirstModel(state, runId);
    underEstimate += bestModel && bestModel.metrics ? bestModel.metrics.underImpact : 0;
  }

  return underEstimate;
}

export function getIsCreatingSplit(state, id) {
  let isCreatingSplit = false;

  const project = getProject(state, id);

  if (project && project.segments && project.segments.length > 1) {
    project.segments.forEach((s) => {
      if (
        !s.job ||
        !s.job.job_id ||
        (runReducer.getModelsArr(state, s.job.job_id).length <= 1 &&
          runReducer.getStatus(state, s.job.job_id) !== "DONE" &&
          runReducer.getStatus(state, s.job.job_id) !== "ERROR")
      ) {
        isCreatingSplit = true;
      }
    });
  }

  return false;
}

export function getChildRunIdsFromRunId(state, runId) {
  let runIds = [];

  const projectId = getProjectIdFromRunId(state, runId);

  if (projectId !== undefined && getOverallRunId(state, projectId) === runId) {
    const project = getProject(state, projectId);

    if (project && project.segments && project.segments.filter((s) => s.conditions.length > 0).length > 0) {
      project.segments
        .filter((s) => s.conditions.length > 0)
        .forEach((s) => s.job && s.job.job_id !== undefined && runIds.push(s.job.job_id));
    }
  }

  return runIds;
}

export function getProjectIdFromRunId(state, runId) {
  const projects = getProjectsArr(state);

  const retProjects = projects.filter(
    (f) =>
      f &&
      f.segments &&
      f.segments.length > 0 &&
      f.segments.reduce(
        (a, b) => a || (b && b.job && b.job.job_id !== undefined && b.job.job_id === Number(runId)),
        false
      )
  );

  return retProjects.length > 0 ? retProjects[0].id : undefined;
}

export function getContinuousFrontier(state, id) {
  const project = getProject(state, id);

  return project && project.segments
    ? project.segments.map((s) => ({
        subgroup: s.conditions.length === 0 ? "Overall" : s.conditions[0].value,
        bestModel: s.job ? runReducer.getFirstModel(state, s.job.job_id) : {},
        frontier:
          s.job && s.job.job_id !== undefined
            ? runReducer.getModelsArr(state, s.job.job_id).map((m) => ({
                ...m,
                bias: m.metrics
                  ? (m.metrics.count_over_predict === 0
                      ? 0
                      : m.metrics.sum_over_predict / m.metrics.count_over_predict) -
                    (m.metrics.count_under_predict === 0
                      ? 0
                      : m.metrics.sum_under_predict / m.metrics.count_under_predict)
                  : 0
              }))
            : []
      }))
    : [];
}

export function getCurrentFrontier(state, projectId, segmentId, isFast) {
  const runId = getSegmentRunId(state, projectId, segmentId);
  const frontier = getFrontierModelListAndSimplify(state, projectId, segmentId, isFast);
  const positive = getTotalPursued(state, projectId, runId);

  const min = setupReducer.getFrontierPercentProcessedMin(state, runId);
  const max = setupReducer.getFrontierPercentProcessedMax(state, runId);

  const points = [];

  let hasDefault = false;

  for (let i = 0; i < frontier.length; i++) {
    const model = frontier[i];
    const point = {
      job_id: runId,
      model_name: model.id,
      is_default: false
    };

    const cm = model.maxIteration ? model.maxIteration.confusionMatrix : model.confusionMatrix;

    if (cm !== undefined) {
      if (!hasDefault && cm.getAllPositive() >= positive) {
        point.is_default = true;
        hasDefault = true;
      }

      point.threshold = cm.threshold;
      point.percent_processed = cm.getPositiveRate();
    }

    points.push(point);
  }

  if (!hasDefault && points.length > 0) {
    points[points.length - 1].is_default = true;
  }

  return points.filter((point) => {
    if (point.is_default) {
      return true;
    }

    return (
      (min === undefined || min <= point.percent_processed) && (max === undefined || point.percent_processed <= max)
    );
  });
}

export function getFrontierDeploymentPayload(state, projectId) {
  const segments = getSegments(state, projectId).map((s) => {
    const frontier = getCurrentFrontier(state, projectId, s.id);

    const defaultIndex = frontier.findIndex((x) => x.is_default);
    const models = frontier.map((f) => {
      const m = { view_id: f.job_id, model_name: f.model_name };

      if ("threshold" in f) {
        m.threshold = f.threshold;
      }

      if ("percent_processed" in f) {
        m.percent_processed = f.percent_processed;
      }

      return m;
    });

    return {
      id: s.id,
      conditions: s.conditions,
      default_frontier_index: defaultIndex,
      frontier_models: models
    };
  });

  return {
    id: projectId,
    segments: segments
  };
}

export function getIsDone(state, id) {
  const jobs = getAllJobIdsFromOptimizer(state, id, false);

  if (jobs === undefined || jobs.length === 0) {
    return false;
  }

  return jobs.every((jobId) => {
    const job = runReducer.getJob(state, jobId);

    return job && (job.status === "DONE" || job.status === "ERROR");
  });
}

/* Actions */

export function resetProjects() {
  return { type: RESET };
}

export function updateBase(base) {}

export function setOptimizer(id, optimizer) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_SUB_KEY,
      key: id,
      subKey: "settings",
      value: optimizer
    });
  };
}

export function initBudget(id) {
  return (dispatch, getState) => {
    const cms = getSegmentConfusionMatrices(getState(), id);

    if (cms === undefined) {
      dispatch(resetBudget(id, false, true));
    }
  };
}

export function resetBudget(id, isTimeout = false, skipLoadingChecks = false) {
  return (dispatch, getState) => {
    if (id && id !== "new") {
      skipLoadingChecks !== true &&
        dispatch({
          type: UPDATE_SETTINGS,
          key: id,
          subKey: "optimizerIsRunning",
          value: true
        });

      if (isTimeout === false) {
        const project = getProject(getState(), id);

        if (project && project.segments) {
          for (let segment of project.segments) {
            if (segment.conditions.length == 0) {
              dispatch(resetSegmentConfusionMatrix(id, segment.id));
            }
          }
        }

        skipLoadingChecks !== true &&
          dispatch({
            type: UPDATE_SETTINGS,
            key: id,
            subKey: "optimizerIsRunning",
            value: false
          });
      } else {
        setTimeout(() => {
          const project = getProject(getState(), id);

          if (project && project.segments) {
            for (let segment of project.segments) {
              if (segment.conditions.length == 0) {
                dispatch(resetSegmentConfusionMatrix(id, segment.id));
              }
            }
          }

          skipLoadingChecks !== true &&
            dispatch({
              type: UPDATE_SETTINGS,
              key: id,
              subKey: "optimizerIsRunning",
              value: false
            });
        }, 10);
      }
    }
  };
}

export function resetSegmentConfusionMatrix(id, segmentId) {
  return (dispatch, getState) => {
    const runId = getSegmentRunId(getState(), id, segmentId);
    const cm = getConfusionMatrix(getState(), runId);

    const isContinuous = runReducer.getIsContinuous(getState(), runId);

    if (isContinuous) {
      return Promise.resolve();
    } else {
      return dispatch(setSegmentConfusionMatrix(id, segmentId, cm));
    }
  };
}

export function updateSegmentConfusionMatrix(id, segmentId, confusionMatrix) {
  return (dispatch, getState) => {
    const current = getSegmentConfusionMatrices(getState(), id) || {};

    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "segmentConfusionMatrices",
      value: {
        ...current,
        [segmentId]: confusionMatrix
      }
    });
  };
}

export function getDeployedSegmentTimePeriod(state, projectId, segmentIdOverride) {
  const segmentId = segmentIdOverride ? segmentIdOverride : getOverallSegmentId(state, projectId);
  const projectSegment = getSegment(state, projectId, segmentId);

  const segmentTimePeriod =
    projectSegment &&
    projectSegment.assumption &&
    projectSegment.assumption.data &&
    projectSegment.assumption.data.timePeriod;
  return segmentTimePeriod || "Month";
}

export function setSegmentConfusionMatrix(id, segmentId, confusionMatrix, optimizeChildren = true) {
  return (dispatch, getState) => {
    const current = getSegmentConfusionMatrices(getState(), id) || {};

    /*
    dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "segmentConfusionMatrices",
      value: {
        ...current,
        [segmentId]: confusionMatrix
      }
    });
    */

    if (optimizeChildren) {
      dispatch(optimizeSegmentBudgets(id, segmentId));
    }
    return Promise.resolve();
  };
}

export function setSegmentMaxBudgetByRunId(id, runId, budget, skipUpdate = false) {
  return (dispatch, getState) => {
    const segmentId = getSegmentIdByRunId(getState(), id, runId);

    const segment = getSegment(getState(), id, segmentId);

    if (segment && segment.job && segment.job.job_id !== undefined && skipUpdate === false) {
      dispatch(setupReducer.updateMaxBudget(segment.job.job_id, budget));
    }

    return dispatch(setSegmentMaxBudget(id, segmentId, budget));
  };
}

export function setSegmentMaxBudget(id, segmentId, budget, forceReset = true) {
  return (dispatch, getState) => {
    const current = getSegmentMaxBudgets(getState(), id) || {};

    const segment = getSegment(getState(), id, segmentId);

    const shouldUpdate = current[segmentId] !== budget;

    if (shouldUpdate) {
      dispatch({
        type: UPDATE_SETTINGS,
        key: id,
        subKey: "segmentMaxBudgets",
        value: {
          ...current,
          [segmentId]: budget
        }
      });
    }

    if (forceReset === true && shouldUpdate) {
      dispatch(resetBudget(id));
    }
  };
}

export function setSegmentMinBudgetByRunId(id, runId, budget, skipUpdate = false) {
  return (dispatch, getState) => {
    const segmentId = getSegmentIdByRunId(getState(), id, runId);

    const segment = getSegment(getState(), id, segmentId);

    if (segment && segment.job && segment.job.job_id !== undefined && skipUpdate === false) {
      dispatch(setupReducer.updateMinBudget(segment.job.job_id, budget));
    }

    return dispatch(setSegmentMinBudget(id, segmentId, budget));
  };
}

export function setSegmentMinBudget(id, segmentId, budget, forceReset = true) {
  return (dispatch, getState) => {
    const current = getSegmentMinBudgets(getState(), id) || {};

    const shouldUpdate = current[segmentId] !== budget;

    if (shouldUpdate) {
      dispatch({
        type: UPDATE_SETTINGS,
        key: id,
        subKey: "segmentMinBudgets",
        value: {
          ...current,
          [segmentId]: budget
        }
      });
    }

    if (forceReset === true && shouldUpdate) {
      dispatch(resetBudget(id));
    }
  };
}

export function setRunId(id, runId) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "runId",
      value: runId
    });
  };
}

export function updateInnerId(id, innerId) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "innerId",
      value: innerId
    });
  };
}

export function updateProjectMode(id, projectMode) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "projectMode",
      value: projectMode
    });
  };
}

export function updateRetrainDatasetId(id, retrainDatasetId) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "retrainDatasetId",
      value: retrainDatasetId
    });
  };
}

export function updateIsRefreshing(id, isRefreshing) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "isRefreshing",
      value: isRefreshing
    });
  };
}

export function updateIsRetraining(id, isRetraining) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "isRetraining",
      value: isRetraining
    });
  };
}

export function updateIsLoadingLink(id, isLoadingLink) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "isLoadingLink",
      value: isLoadingLink
    });
  };
}

export function updateIsNewFrontier(id, isNewFrontier) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "isNewFrontier",
      value: isNewFrontier
    });
  };
}

export function setDrilldownValue(id, drilldownValue) {
  return {
    type: UPDATE_SETTINGS,
    key: id,
    subKey: "drilldownValue",
    value: drilldownValue
  };
}

export function setDrilldownColumn(id, drilldownColumn) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "drilldownColumn",
      value: drilldownColumn
    });

    dispatch(initOptimizerObject(id));
  };
}

export function setAibleViewType(id, viewType) {
  return (dispatch, getState) => {
    if (viewType === AIBLE_VIEW_TYPE.BUSINESS) {
      window.history.pushState("page2", "", "/project/" + id + "/b");
    } else if (viewType === AIBLE_VIEW_TYPE.ADVANCED) {
      window.history.pushState("page2", "", "/project/" + id);
    }

    dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "aibleViewType",
      value: viewType
    });
  };
}

export function setSalesDrilldown(id, salesDrilldown) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "salesDrilldown",
    value: salesDrilldown
  };
}

export function setMarketingDrilldown(id, marketingDrilldown) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "marketingDrilldown",
    value: marketingDrilldown
  };
}

export function setOverallScaleFromRowCount(id, rowCount) {
  return (dispatch, getState) => {
    const cm = getMarketingConfusionMatrix(getState(), id, true);

    const overallScale = Number(rowCount) / Number(cm.getTotal());

    return dispatch(setOverallScale(id, overallScale));
  };
}

/* Action Creators */
export function pollJobs(id) {
  return (dispatch, getState) => {
    const optimizer = getOptimizer(getState(), id);

    const jobKeys = [];

    Object.keys(optimizer.overallRuns).forEach((v) =>
      Object.keys(optimizer.overallRuns[v]).forEach((p) => jobKeys.push(optimizer.overallRuns[v][p]))
    );

    Object.keys(optimizer.combinedRuns).forEach((v) =>
      Object.keys(optimizer.combinedRuns[v]).forEach((p) => jobKeys.push(optimizer.combinedRuns[v][p]))
    );

    Object.keys(optimizer.marketingRuns).forEach((v) => jobKeys.push(optimizer.marketingRuns[v]));

    Object.keys(optimizer.salesRuns).forEach((v) => jobKeys.push(optimizer.salesRuns[v]));

    jobKeys.forEach((k) => {
      if (Object.keys(runReducer.getJob(getState(), k)).length === 0) {
        dispatch(runReducer.pollJob(k, true));
      }
    });
  };
}

export function createSplitsFromRunId(runId, column, nextId) {
  return (dispatch, getState) => {
    const setup = setupReducer.getSetup(getState(), runId);

    return dispatch(createProject(runId, setup, nextId)).then((r) => {
      dispatch(globalReducer.updateCurrentProjectId(r.id));
      if (nextId === undefined) {
        if (isEnabled("aible-business-simple")) {
          dispatch(push("/business/" + r.id));
        } else {
          dispatch(push("/project/" + r.id + "/b"));
        }
      }

      dispatch(createSegments(r.id, runId, [column]));

      return Promise.resolve(r.id);
    });
  };
}

export function createSplits(id, columnOne, columnTwo, interactColumns = false, createNewProject = false) {
  return (dispatch, getState) => {
    const runId = getRunId(getState(), id);

    const setup = setupReducer.getSetup(getState(), runId);

    const columnCombinations = [[columnOne], [columnTwo], ...(interactColumns ? [columnOne, columnTwo] : [])];

    //return dispatch(createSegments(id, runId, [columnOne]));

    if (createNewProject) {
      const blueprintId = setupReducer.getBlueprintId(getState(), runId);

      return dispatch(setupReducer.initJobFromWizard(runId, blueprintId, {})).then((r) => {
        const projectId = getProjectIdFromRunId(getState(), r);
        dispatch(createSegments(projectId, r, [columnOne]));
      });
    }
    dispatch(createProject(runId, setup)).then((r) => {
      dispatch(globalReducer.updateCurrentProjectId(r.id));
      if (isEnabled("aible-business-simple")) {
        dispatch(push("/business/" + r.id));
      } else {
        dispatch(push("/project/" + r.id + "/b"));
      }

      return dispatch(createSegments(r.id, runId, [columnOne]));
    });
  };
}

function createSegment(projectId, runId, conditions) {
  return (dispatch, getState) => {
    const segment = {
      conditions: conditions,
      jobs: [{ job_id: runId, status: "ACCEPTED" }],
      assumptions: [
        {
          data: { ...setupReducer.getSetup(getState(), runId) },
          status: "ACCEPTED"
        }
      ]
    };

    return ProjectAPI.createProjectSegment(projectId, segment);
  };
}

function createSegments(projectId, runId, columnNames) {
  return (dispatch, getState) => {
    switch (columnNames.length) {
      case 0:
        return [];

      case 1:
        return dispatch(createFirstOrderSegments(projectId, runId, columnNames[0]));

      case 2:
        return dispatch(createSecondOrderSegments(projectId, runId, columnNames[0], columnNames[1]));

      default:
        return []; // TODO: Handle this better
    }
  };
}

function createFirstOrderSegments(projectId, runId, columnName) {
  return (dispatch, getState) => {
    const dataset = setupReducer.getDatasetFromSetup(getState(), runId);

    const pSetup = setupReducer.getSetup(getState(), runId);
    dispatch(setupReducer.updateParentLabel(runId, pSetup.label));
    const setup = setupReducer.getSetup(getState(), runId);

    const blueprint =
      setup?.blueprintId === undefined ? undefined : blueprintReducer.getBlueprint(getState(), setup?.blueprintId);

    const ignoredValue = setupReducer.getIgnoredValue(getState(), runId);

    const columnFieldData = (Utils.get(dataset, ["fields", columnName, "data"]) || []).filter((v) =>
      ignoredValue &&
      ignoredValue[columnName] &&
      ignoredValue[columnName][v.value] &&
      ignoredValue[columnName][v.value] === true
        ? false
        : true
    );

    const isContinuous = getIsContinuous(getState(), projectId);

    const MIN_ROW_COUNT = MIN_SEGMENT_ROWS;
    const MAX_CHILD_COUNT = isContinuous === true ? MAX_NUM_SEGMENTS_CONTINUOUS : MAX_NUM_SEGMENTS;

    const retColumns = columnFieldData
      .filter((v) => v.value !== "{Other}" && v.value !== "{Missing}")
      .filter((v) => v.count > MIN_ROW_COUNT)
      .sort((a, b) => b.count - a.count)
      .filter((v, i) => i < MAX_CHILD_COUNT);

    const specialVals = columnFieldData
      .filter((v) => retColumns.filter((rV) => v.value === rV.value).length === 0)
      .map((v) => ({ column: columnName, value: v.value, operand: "=" }));

    if (specialVals.length > 0) {
      dispatch(setupReducer.createSetupFromSpecial(runId, specialVals));

      dispatch(
        setupReducer.initJobFromWizard(runId, setup?.blueprintId, {
          isSplitting: true,
          createProject: false
        })
      ).then((retJobId, i) => {
        let condition = {
          type: ConditionType.In,
          column: columnName,
          values: specialVals.map((x) => x.value)
        };

        if (specialVals.some(({ value }) => value == "{Other}" || value == "{Missing}")) {
          condition = {
            type: ConditionType.NotIn,
            column: columnName,
            values: retColumns.map((x) => x.value)
          };
        }
        const conditions = [condition];

        return dispatch(createSegment(projectId, retJobId, conditions, forceSetup)).then(() =>
          dispatch(fetchProject(projectId))
        );
      });
    }

    return retColumns.map(({ value }, i) => {
      // HACKY FIX FOR RACE CONDITION - RESET TO ORIGINAL SETUP
      dispatch(setupReducer.updateSetup(runId, setup));

      const hasForceSetup = blueprint?.wizard?.segmentSetups?.[value] !== undefined;
      let forceSetup = { ...(blueprint?.wizard?.segmentSetups?.[value] ?? {}) };

      const nSetup = setupReducer.getSetup(getState(), "new");
      const nDataset = homeReducer.getDataset(getState(), nSetup?.datasetId);

      if (nSetup?.datasetId !== undefined) {
        forceSetup.datasetId = nSetup?.datasetId;
        forceSetup.success = nSetup?.success;
      }

      return dispatch(
        createRunFromValue(
          runId,
          columnName,
          value,
          undefined,
          undefined,
          hasForceSetup ? forceSetup : nSetup,
          projectId
        )
      ).then((runJobId) => {
        const conditions = [{ column: columnName, value: value }];

        return dispatch(createSegment(projectId, runJobId, conditions, forceSetup)).then(() =>
          dispatch(fetchProject(projectId))
        );
      });
    });
  };
}

function createSecondOrderSegments(projectId, runId, firstColumnName, secondColumnName) {
  return (dispatch, getState) => {
    const dataset = setupReducer.getDatasetFromSetup(getState(), runId);

    const firstColumnFieldData = Utils.get(dataset, ["fields", firstColumnName, "data"]) || [];

    const secondColumnFieldData = Utils.get(dataset, ["fields", secondColumnName, "data"]) || [];

    return firstColumnFieldData.map((firstFieldData) =>
      secondColumnFieldData.flatMap((secondFieldData) =>
        dispatch(
          createRunFromValue(runId, firstColumnName, firstFieldData.value, secondColumnName, secondFieldData.value)
        ).then((runJobId) => {
          const conditions = [
            { column: firstColumnName, value: firstFieldData.value },
            { column: secondColumnName, value: secondFieldData.value }
          ];

          return dispatch(createSegment(projectId, runJobId, conditions)).then(() => dispatch(fetchProject(projectId)));
        })
      )
    );
  };
}

export function createRunFromValue(
  runId,
  column,
  value,
  columnTwo,
  valueTwo,
  forceSetup = undefined,
  projectId = undefined
) {
  return (dispatch, getState) => {
    const setup = setupReducer.getSetup(getState(), runId);

    const specialVals = [{ column: column, value: value, operand: "=" }];

    if (columnTwo !== undefined && valueTwo !== undefined) {
      specialVals.push({ column: columnTwo, value: valueTwo });
    }

    dispatch(setupReducer.createSetupFromSpecial(runId, specialVals));

    if (forceSetup !== undefined) {
      const retSetup = {
        ...forceSetup,
        success: setup?.success
      };

      dispatch(setupReducer.updateSetup(runId, retSetup));
    }

    //return new Promise((resolve, reject) => resolve(0));

    if (projectId !== undefined) {
      dispatch(setupReducer.updateAllBusinessConstraints(projectId, runId, []));
    }

    return dispatch(
      setupReducer.initJobFromWizard(runId, setup.blueprintId, {
        isSplitting: true,
        createProject: false
      })
    );
  };
}

export function hasLoadedAllJobs(state, id) {
  const jobKeys = getNonOverallJobsFromOptimizer(state, id);

  let loadedCount = 0;

  jobKeys.forEach((k) => {
    if (runReducer.hasModels(state, k)) {
      loadedCount++;
    }
  });

  return loadedCount === jobKeys.length;
}

export function getNonOverallJobsFromOptimizer(state, id) {
  return getAllJobIdsFromOptimizer(state, id, false);
}

export function getAllJobsIdsFromProject(state, id) {
  const project = getProject(state, id);

  let retIds = [];

  project?.segments.forEach((s) => {
    retIds.push(s?.job?.job_id);
  });

  return retIds;
}

export function getAllJobIdsFromOptimizer(state, id, includeOverall = true) {
  return getProjectSegments(state, id, includeOverall).flatMap((segment) => {
    const rawHistoryJobIds = (Utils.get(segment, ["history", "jobs"]) || [])
      .filter((job) => job && job.job_id !== undefined)
      .map(({ job_id }) => job_id);

    const historyJobIds = [...new Set(rawHistoryJobIds)];

    if (segment.job === undefined || segment.job === null || segment.job.job_id === undefined) {
      return historyJobIds;
    }

    return [segment.job.job_id, ...historyJobIds].filter((v, i, a) => a.indexOf(v) === i);
  });
}

export function getLoadedJobsFromOptimizer(state, id, includeOverall = true) {
  return getProjectSegments(state, id, includeOverall).flatMap((segment) => {
    const jobs = (Utils.get(segment, ["history", "jobs"]) || []).filter((job) => job?.job).map(({ job }) => job);

    if (segment?.job?.job) {
      const accepted = segment?.job?.job;

      if (jobs.find((x) => x.id == accepted.id) === undefined) {
        jobs.push(segment?.job?.job);
      }
    }

    return jobs;
  });
}

export function getAllJobsFromOptimizer(state, id, includeOverall = true) {
  return getAllJobIdsFromOptimizer(state, id, includeOverall).map((jobId) => runReducer.getJob(state, jobId));
}

export function optimizeSegmentBudgets(id, segmentId) {
  return (dispatch, getState) => {
    const project = getProject(getState(), id);

    let baseSegment = getSegment(getState(), id, segmentId);

    const segment = getActiveSegment(getState(), id);
    if (segment && segment.job && segment.job.job_id) {
      if (
        runReducer.getIsContinuous(getState(), segment.job.job_id) ||
        Object.keys(setupReducer.getSetup(getState(), segment.job.job_id)).length === 0
      ) {
        return;
      }
    }

    if (baseSegment === undefined) {
      return; // no-op without a base segment
    }

    const childrenByColumn = getChildSegmentsByColumn(getState(), id, segmentId);

    const hasChildren = Object.keys(childrenByColumn).length > 0;

    if (!hasChildren && baseSegment.conditions.length == 0) {
      childrenByColumn["Overall"] = [baseSegment];
    }

    let confusionMatrices = {};

    Object.values(childrenByColumn)
      .filter((v) => v)
      .flatMap((segments) => {
        let optimizerWithinBounds = getOptimizerWithinBounds(getState(), id);
        if (!optimizerWithinBounds) {
          dispatch({
            type: UPDATE_SETTINGS,
            key: id,
            subKey: "optimizerWithinBounds",
            value: true
          });
          optimizerWithinBounds = true;
        }

        const result = optimizeBudgetAcrossSegments(getState(), id, segments);

        if (!result.isWithinBounds && getOptimizerWithinBounds(getState(), id)) {
          dispatch({
            type: UPDATE_SETTINGS,
            key: id,
            subKey: "optimizerWithinBounds",
            value: result.isWithinBounds
          });
        }

        return result.segments;
      })
      .forEach(({ confusionMatrix, segmentId }) => {
        confusionMatrices[segmentId] = confusionMatrix;

        if (hasChildren) {
          dispatch(optimizeSegmentBudgets(id, segmentId));
        }
      });

    if (
      project &&
      project.segments &&
      Object.keys(confusionMatrices).length > 0 &&
      (!hasChildren || Object.keys(confusionMatrices).length === project.segments.length - 1)
    )
      dispatch({
        type: UPDATE_SETTINGS,
        key: id,
        subKey: "segmentConfusionMatrices",
        value: confusionMatrices
      });
  };
}

export function optimizeBudgetAcrossSegments(state, projectId, segments) {
  const runId = getOverallRunIdFromProject(getProject(state, projectId));

  const biasReduction = getBiasReduction(state, runId);

  let allocationMetric = AllocationMetric.Units;

  switch (biasReduction) {
    case BIAS_REDUCTION.IMPACT:
      allocationMetric = AllocationMetric.Profit;
      break;
    case BIAS_REDUCTION.EFFORT:
      allocationMetric = AllocationMetric.Cost;
      break;
    case BIAS_REDUCTION.COUNT:
    default:
      allocationMetric = AllocationMetric.Units;
      break;
  }

  const fairnessToleranceBound =
    getFairnessToleranceBound(state, runId) === undefined ? undefined : getFairnessToleranceBound(state, runId) / 100;

  const segmentConfigs = segments
    .map((segment) => {
      const jobId = segment.job && segment.job.job_id;

      if (jobId === undefined) {
        return false;
      }

      const models = getFrontierModelListAndSimplify(state, projectId, segment.id);

      if (!models || models.length === 0) {
        return false;
      }

      const firstModel = models[0];

      const costBenefitMatrix = new CostBenefitMatrix(
        setupReducer.getConversionGain(state, jobId),
        setupReducer.getConversionSaved(state, jobId),
        setupReducer.getConversionCost(state, jobId),
        setupReducer.getConversionMissed(state, jobId)
      );

      const segmentConstraints = getConvertedBusinessConstraints(state, projectId, jobId);

      const rawAllocation = setupReducer.getTargetPositiveAllocation(state, jobId);

      const allocation = rawAllocation === undefined ? undefined : Number(rawAllocation) / 100;

      const setup = setupReducer.getSetup(state, jobId);

      const defaultIndex = (setup.frontier || []).findIndex((x) => x.is_default);

      const defaultFrontier = (setup.frontier || [])[defaultIndex];
      const defaultModel = models[defaultIndex];

      const isValidDefaultIndex =
        false && // disable caching for now
        !(segment.history && segment.history.jobs && segment.history.jobs.length > 1) &&
        segment.assumption &&
        segment.assumption.data &&
        isSameSetup(segment.assumption.data, setup) &&
        defaultFrontier !== undefined &&
        defaultModel !== undefined &&
        defaultFrontier.job_id == jobId &&
        defaultFrontier.model_name == defaultModel.id &&
        defaultFrontier.percent_processed == defaultModel.confusionMatrix.getPositiveRate() &&
        defaultFrontier.threshold == defaultModel.confusionMatrix.threshold;

      const assumptions = {
        costBenefitMatrix: costBenefitMatrix,
        unitCost: getUnitCost(state, jobId),
        unitRevenue: getUnitRevenue(state, jobId),
        targetAllocation: allocation,
        allocationMetric: allocationMetric,
        defaultIndex: isValidDefaultIndex ? defaultIndex : undefined
      };

      return {
        segmentId: segment.id,
        constraints: segmentConstraints,
        assumptions: assumptions,
        model: {
          confusionMatrixFactory: firstModel.maxIteration.confusionMatrixFactory
        },
        confusionMatrices: models.map((model) => model.confusionMatrix),
        fairnessToleranceBound: fairnessToleranceBound
      };
    })
    .filter((s) => s !== false);

  const businessObjective = getBusinessObjective(state, runId);
  const fairnessObjective = getFairnessObjective(state, runId);

  let objective = ObjectiveMetric.Profit;

  switch (businessObjective) {
    case BUSINESS_OBJECTIVE.REVENUE:
      objective = ObjectiveMetric.Revenue;
      break;
    case BUSINESS_OBJECTIVE.MINIMIZE_RESOURCING:
      objective = ObjectiveMetric.Cost;
      break;
    case BUSINESS_OBJECTIVE.PROFIT:
    default:
      objective = ObjectiveMetric.Profit;
      break;
  }

  let strategy = FairnessStrategy.None;

  switch (fairnessObjective) {
    case FAIRNESS_OBJECTIVE.ALLOCATION:
      strategy = FairnessStrategy.TargetAllocation;
      break;
    case FAIRNESS_OBJECTIVE.ACCURACY:
      strategy = FairnessStrategy.EqualAccuracy;
      break;
    case FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE:
      strategy = FairnessStrategy.EqualTruePositiveRate;
      break;
    case FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE:
      strategy = FairnessStrategy.EqualFalsePositiveRate;
      break;
    case FAIRNESS_OBJECTIVE.ODDS:
      strategy = FairnessStrategy.EqualOdds;
      break;
    case FAIRNESS_OBJECTIVE.NONE:
    default:
      strategy = FairnessStrategy.None;
      break;
  }

  const constraints = getConvertedBusinessConstraints(state, projectId, runId);

  return ProjectOptimizer.create(segmentConfigs, objective, constraints, strategy).optimize();
}

export function getCheckBalance(state, projectId) {
  const linkedProjectId = getLinkedProjectId(state, projectId);

  const modelGroups = getModelGroups(state, linkedProjectId);

  // Function to take the individual subgroups and find the lowest value and compare to the average of the others.
  // Find how much to scale the lowest value subgroup to bring it in line with the average.
  const subgroupData = modelGroups?.drilldownValues?.filter((d) => d?.value !== "Overall" && d?.capValue !== 0) ?? [];

  if (!subgroupData?.length || subgroupData?.length === 1) {
    return undefined;
  }
  const lowestSubgroup = subgroupData.reduce((min, item) => (item.capValue < min.capValue ? item : min));
  const remainingGroups = subgroupData.filter((subgroup) => subgroup?.label !== lowestSubgroup?.label);
  const sumRemainingGroups = remainingGroups.reduce((acc, item) => acc + item?.capValue, 0);
  const aveGroupValue = sumRemainingGroups / remainingGroups?.length;

  if (aveGroupValue === lowestSubgroup?.capValue || lowestSubgroup?.capValue === 0) {
    return undefined;
  }

  const percentChangeScale = (aveGroupValue / lowestSubgroup?.capValue) * 100;

  return {
    highestSubgroup: lowestSubgroup,
    averageGroupValue: aveGroupValue,
    percentChange: percentChangeScale
  };
}

export function getConvertedBusinessConstraints(state, projectId, runId) {
  const constraints = setupReducer
    .getBusinessConstraints(state, projectId, runId)
    .map(getBusinessConstraintConverter(state, projectId, runId))
    .filter(({ min, max }) => isFinite(min) || isFinite(max));

  const segment = getSegmentByRunId(state, projectId, runId);

  // legacy
  const minBudget = getSegmentMinBudget(state, projectId, segment.id);
  const maxBudget = getSegmentMaxBudget(state, projectId, segment.id);

  if (isFinite(minBudget) || isFinite(maxBudget)) {
    let costConstraint = constraints.find((c) => c.type == SegmentMetric.Cost);

    if (costConstraint == undefined) {
      costConstraint = {
        type: SegmentMetric.Cost
      };

      constraints.push(costConstraint);
    }

    if (isFinite(minBudget)) {
      costConstraint.min = minBudget;
    }

    if (isFinite(maxBudget)) {
      costConstraint.max = maxBudget;
    }
  }

  return constraints;
}

export function getBusinessConstraintConverter(
  state,
  projectId,
  runId,
  mode = setupReducer.BusinessConstraintMode.Number,
  format = false
) {
  return (constraint) => {
    if (constraint === undefined || constraint.mode === mode) {
      return constraint;
    }

    let converter = (x) => x;
    let inverseConverter = (x) => x;

    const accessor = getCurrentSegmentMetricAccessor(constraint.type);

    switch (constraint.mode) {
      case setupReducer.BusinessConstraintMode.Percent: {
        converter = (x) => Utils.humanize(x * accessor(state, projectId, runId));
        break;
      }

      case setupReducer.BusinessConstraintMode.Number:
      default:
        converter = (x) => x;
    }

    switch (mode) {
      case setupReducer.BusinessConstraintMode.Percent: {
        inverseConverter = (x) => {
          const value = x / accessor(state, projectId, runId);

          if (!format) {
            return value;
          } else {
            return Utils.humanize(value);
          }
        };
        break;
      }

      case setupReducer.BusinessConstraintMode.Number:
      default:
        inverseConverter = (x) => (format ? Utils.humanize(Math.round(x * 100) / 100) : x);
    }

    const updated = {
      type: constraint.type,
      mode: mode
    };

    if (isFinite(constraint.min)) {
      updated.min = inverseConverter(converter(constraint.min));
    }

    if (isFinite(constraint.max)) {
      updated.max = inverseConverter(converter(constraint.max));
    }

    return updated;
  };
}

export function getCurrentSegmentMetricAccessor(type) {
  switch (type) {
    case SegmentMetric.Profit:
      return getCurrentProfit;

    case SegmentMetric.Revenue:
      return getCurrentRevenue;

    case SegmentMetric.Cost:
      return getCurrentSpend;

    default:
      return () => undefined;
  }
}

export function getFrontierByRunId(state, runId, projectIdOverride, breakpoints) {
  const projectId = isFinite(projectIdOverride) ? projectIdOverride : getProjectIdFromRunId(state, runId);

  if (projectId === undefined) {
    return {};
  }

  const segment = getSegmentByRunId(state, projectId, runId);

  return getSegmentFrontier(state, projectId, segment.id, undefined, breakpoints);
}

export function getSegmentFrontier(state, projectId, segmentId, isFast = true, breakpointOverride) {
  const jobId = getSegmentRunId(state, projectId, segmentId);

  const ignoredModels = setupReducer.getIgnoredModels(state, jobId);

  const isContinuous = runReducer.getIsContinuous(state, jobId);

  if (isContinuous) {
    return runReducer.getOptimalContinuousModel(state, jobId, ignoredModels);
  }

  if (isFast) {
    return runReducer.getOptimalModelsByCapacityFast(state, jobId, breakpointOverride, ignoredModels);
  } else {
    const breakpoints = breakpointOverride !== undefined ? breakpointOverride : runReducer.CAPACITY_BREAKPOINTS_ALL;
    return runReducer.getOptimalModelsByCapacity(state, jobId, breakpoints, ignoredModels);
  }
}

function getFrontierModelListAndSimplify(state, projectId, segmentId, isFast) {
  const maxModelList = runReducer.getFrontierList(state, undefined, {
    projectId,
    segmentId,
    isFast
  });

  const simple = [];

  let lastMax = undefined;

  for (let currentMax of maxModelList) {
    const lastCM = lastMax
      ? lastMax.maxIteration
        ? lastMax.maxIteration.confusionMatrix
        : lastMax.confusionMatrix
      : undefined;
    const currentCM = currentMax
      ? currentMax.maxIteration
        ? currentMax.maxIteration.confusionMatrix
        : currentMax.confusionMatrix
      : undefined;

    const lastT = lastMax
      ? lastMax.maxIteration
        ? lastMax.maxIteration.threshold
        : lastMax.confusionMatrix
          ? lastMax.confusionMatrix.threshold
          : 0
      : undefined;
    const currentT = currentMax
      ? currentMax.maxIteration
        ? currentMax.maxIteration.threshold
        : currentMax.confusionMatrix
          ? currentMax.confusionMatrix.threshold
          : 0
      : undefined;
    if (
      lastMax === undefined ||
      ((lastMax.id != currentMax.id || lastT != currentT) && lastCM && !lastCM.equals(currentCM))
    ) {
      simple.push(currentMax);
      lastMax = currentMax;
    }
  }

  return simple;
}

export function updateProject(project) {
  return { type: UPDATE_KEY, key: project.id, value: project };
}

export function updateProjects(projects) {
  return { type: INIT_OPTIMIZER, data: projects };
}

export function updateProjectMonitor(id, projectMonitor) {
  return (dispatch, getState) => {
    const current = getProjectMonitor(getState(), id);
    const update = {
      ...current,
      ...projectMonitor
    };

    return dispatch({
      type: UPDATE_SUB_KEY,
      key: id,
      subKey: "monitors",
      value: [update]
    });
  };
}

export function updateUniqueIdName(id, uniqueIdName) {
  return (dispatch, getState) => {
    dispatch(
      updateProjectMonitor(id, {
        unique_id_name: uniqueIdName
      })
    );
  };
}

export function updateBusinessPredictionName(id, businessPredictionName) {
  return (dispatch, getState) => {
    dispatch(
      updateProjectMonitor(id, {
        business_prediction_name: businessPredictionName
      })
    );
  };
}

export function updateTerminalConditions(id, terminalConditions) {
  return (dispatch, getState) => {
    dispatch(
      updateProjectMonitor(id, {
        terminal_conditions: terminalConditions
      })
    );
  };
}

export function updateActivityConditions(id, activityConditions) {
  return (dispatch, getState) => {
    dispatch(
      updateProjectMonitor(id, {
        activity_conditions: activityConditions
      })
    );
  };
}

export function getAlternateFrontiers(state, projectId) {
  const activeSegment = getActiveSegment(state, projectId);

  return activeSegment && activeSegment.history && activeSegment.history.jobs ? activeSegment.history.jobs : [];
}

export function getOverallRunIdFromProject(project) {
  let runId = undefined;

  if (project && project.segments && project.segments.length > 0) {
    for (let segment of project.segments) {
      if (segment.conditions.length == 0) {
        runId = segment?.job?.job_id;
        break;
      }
    }
  }

  return runId;
}

export function getOverallRunId(state, id) {
  const project = getProject(state, id);

  let runId = undefined;

  if (project && project.segments && project.segments.length > 0) {
    for (let segment of project.segments) {
      if (segment.conditions.length == 0) {
        runId = segment?.job?.job_id;
        break;
      }
    }
  }

  return runId;
}

export function getIsOverallSegment(state, id, runId = undefined, ignoreSplits = false) {
  const project = getProject(state, id);

  if (project === undefined) {
    return false;
  }

  if (project && project.segments && project.segments.length <= 1 && ignoreSplits === false) {
    return false;
  }

  const retRunId = runId ? runId : getRunId(state, id);
  const overallRunId = getOverallRunIdFromProject(getProject(state, id));

  return retRunId === overallRunId;
}

export function getDefaultSegmentBudgets(project) {
  const retProject = {
    segmentMinBudgets: {},
    segmentMaxBudgets: {}
  };

  project.segments &&
    project.segments
      .filter((s, i) => i > 0 || project.segments.length === 1)
      .forEach((s) => {
        if (s && s.id !== undefined && s.job && s.assumption) {
          const totalCount =
            Number(s.assumption.data.truePositive) +
            Number(s.assumption.data.falsePositive) +
            Number(s.assumption.data.trueNegative) +
            Number(s.assumption.data.falseNegative);

          retProject.segmentMinBudgets[s.id] = 0;
          retProject.segmentMaxBudgets[s.id] = Math.ceil(
            Math.abs(totalCount) *
              (s.assumption.data.advancedCost
                ? Math.abs(s.assumption.data.advancedCost)
                : Math.abs(s.assumption.data.conversionCost)) *
              10
          );
        }
      });

  if (project.segments && project.segments.length > 1) {
    retProject.segmentMinBudgets[project.segments[0].id] = 0;
    retProject.segmentMaxBudgets[project.segments[0].id] = 0;

    project.segments.forEach((s) => {
      retProject.segmentMinBudgets[project.segments[0].id] += retProject.segmentMinBudgets[s.id];
      retProject.segmentMaxBudgets[project.segments[0].id] += retProject.segmentMaxBudgets[s.id];
    });
  }

  return retProject;
}

export function getCapValue(state, id, runId, forceCalculate = false) {
  const runCapValue = runReducer.getCapValue(state, runId);

  if (runCapValue && forceCalculate === false) {
    return runCapValue;
  }

  const cm = getConstrainedConfusionMatrix(state, id, runId);

  if (cm === undefined) {
    return 0;
  }
  const capValue = cm.getPositiveRate() * 100;
  return isNaN(capValue) ? 0 : capValue;
}

export function getCapModel(state, id, runId, forceCalculate = false) {
  const capValue = Math.round(getCapValue(state, id, runId, true));

  if (!isFinite(capValue)) {
    return undefined;
  }

  const frontier = runReducer.getFrontier(state, runId, id);

  return frontier[capValue + "%"];
}

export function viewScenario(id, scenario, scenarioIndex) {
  return (dispatch, getState) => {
    let retSetups = {};

    console.log("view", id, scenario, scenarioIndex);

    Object.keys(scenario).forEach((k, i) => {
      const runId = scenario[k].runId;
      const currentSetup = setupReducer.getSetup(getState(), runId);

      retSetups[runId] = {
        ...scenario[k].setup.data,
        defaultSetup: {
          data:
            currentSetup && currentSetup.defaultSetup && currentSetup.defaultSetup.data
              ? { ...currentSetup.defaultSetup.data }
              : { ...currentSetup }
        },
        jobSetup: { ...currentSetup.jobSetup }
      };

      if (i === 0 && scenario[k].setup.data && scenario[k].setup.data.linkedProjectId) {
        if (
          scenario[k].setup.data.linkedProjectReportId &&
          reportReducer.getReport(getState(), scenario[k].setup.data.linkedProjectReportId) === undefined
        ) {
          dispatch(reportReducer.fetchReport(scenario[k].setup.data.linkedProjectReportId));
        }
        dispatch(
          init(
            scenario[k].setup.data.linkedProjectId,
            undefined,
            id,
            false,
            scenario[k].setup.data.linkedScenarioId,
            true
          )
        );
      }
    });

    dispatch(setupReducer.updateSetups(retSetups));

    Object.keys(scenario).forEach((k) => {
      const project = getProject(getState(), id);

      if (
        project &&
        (project.segmentMinBudgets === undefined ||
          project.segmentMinBudgets[scenario[k].project_segment_id] !== scenario[k].setup.data.minBudget)
      ) {
        dispatch(
          setSegmentMinBudget(
            id,
            scenario[k].project_segment_id,
            scenario[k].setup.data.minBudget || scenario[k].setup.data.minBudget === 0
              ? scenario[k].setup.data.minBudget
              : undefined,
            false
          )
        );
      }
      if (
        project &&
        (project.segmentMaxBudgets === undefined ||
          project.segmentMaxBudgets[scenario[k].project_segment_id] !== scenario[k].setup.data.maxBudget)
      ) {
        dispatch(
          setSegmentMaxBudget(
            id,
            scenario[k].project_segment_id,
            scenario[k].setup.data.maxBudget || scenario[k].setup.data.maxBudget === 0
              ? scenario[k].setup.data.maxBudget
              : undefined,
            false
          )
        );
      }
    });

    //dispatch(updateSetupFromLinked(id));
    if (id) {
      dispatch(reconciliateProjectRejectedModels(id));
    }

    dispatch(resetBudget(id, false, true));

    const currInnerId = getInnerId(getState(), id);
    const currDrilldown = getDrilldownValue(getState(), id);
    const overallRunId = getOverallRunId(getState(), id);
    if (currInnerId !== overallRunId) {
      dispatch(updateDrilldownValue(id, overallRunId, "Overall"));
    }
    const optimized = getOptimized(getState(), id);
    const modelGroups = getModelGroups(getState(), id);
    let project = JSON.parse(JSON.stringify(getProject(getState(), id)));

    let scenarioStats = { ...project.settings.scenarioStats };

    project.settings.scenarios = project.settings.scenarios.map((s, i) => {
      if (s[Object.keys(s)[0]].id === scenario[Object.keys(scenario)[0]].id) {
        let retScenario = { ...s };

        const allBusinessConstraints = setupReducer.getAllBusinessConstraints(getState(), id);
        let segmentTopModels;
        if (modelGroups && modelGroups.drilldownValues && modelGroups.drilldownValues.length > 0) {
          if (modelGroups.drilldownValues.length === 1) {
            segmentTopModels = runReducer.getTransformedSortedModels(getState(), modelGroups.drilldownValues[0].runId);
          } else {
            segmentTopModels = modelGroups.drilldownValues.map((segment) => {
              const models = runReducer.getTransformedSortedModels(getState(), segment.runId);
              return models && models.length > 0 ? models.slice(0, 10) : [];
            });
          }
        }

        scenarioStats[i] = {
          revenue: optimized.revenue,
          profit: optimized.profit,
          cost: optimized.totalCost,
          currentRevenue: optimized.currentRevenue,
          currentProfit: optimized.currentProfit,
          currentSpend: optimized.currentSpend,
          underEstimate: optimized.underEstimate,
          overEstimate: optimized.overEstimate,
          profitDelta: optimized.profitDelta,
          revenueDelta: optimized.revenueDelta,
          modelGroupSegments: modelGroups.drilldownValues,
          topDrivers: modelGroups.topDrivers,
          segmentTopModels: segmentTopModels,
          businessConstraints: allBusinessConstraints
        };

        Object.keys(retScenario).forEach((k) => {
          retScenario[k].capValue =
            modelGroups.drilldownValues.filter((v) => v.value === k).length > 0
              ? modelGroups.drilldownValues.filter((v) => v.value === k)[0].capValue
              : 0;

          const capValue = getCapValue(getState(), id, retScenario[k].runId, true);

          if (runReducer.getCapValue(getState(), retScenario[k].runId) !== capValue) {
            //dispatch(runReducer.setCapValue(retScenario[k].runId, capValue));
          }

          retScenario[k].profit = getProfit(getState(), id, retScenario[k].runId);
          retScenario[k].revenue = getTotalRevenue(getState(), id, retScenario[k].runId);
          retScenario[k].cost = getTotalCost(getState(), id, retScenario[k].runId);

          retScenario[k].model = runReducer.getFirstModel(getState(), retScenario[k].runId);
          retScenario[k].cValue = capValue;
          retScenario[k].topDrivers = getTopDriver(getState(), retScenario[k].runId);
          retScenario[k].columnDrivers = getColumnDrivers(getState(), retScenario[k].runId, id);
        });
        return retScenario;
      } else {
        return { ...s };
      }
    });

    if (currInnerId !== overallRunId) {
      dispatch(updateDrilldownValue(id, currInnerId, currDrilldown));
    }

    dispatch(
      updateSettingScenario(
        id,
        project.settings.scenarios,
        project.settings.originalScenarios,
        scenarioStats,
        scenarioIndex
      )
    );

    // Select the best model for the current capacity in Aible Advanced
    // const cScenario = getCurrentScenario(getState(), id);
    // if (cScenario && cScenario.data) {
    //   Object.keys(cScenario.data).forEach((sKey) => {
    //     const cRunId = cScenario.data[sKey].runId;
    //     const cCapModel = runReducer.getCapModel(getState(), cRunId);

    //     if (
    //       cCapModel &&
    //       cCapModel.id !== runReducer.getFeatureSelection(getState(), cRunId)
    //     ) {
    //       //dispatch(runReducer.updateFeatureSelection(cRunId, cCapModel.id));
    //     }
    //   });
    // }

    //dispatch(updateProject(project));
  };
}

export function updateSettingScenario(id, scenarios, originalScenarios, scenarioStats, scenarioIndex) {
  return {
    type: UPDATE_SETTING_SCENARIO,
    key: id,
    scenarios: scenarios,
    originalScenarios: originalScenarios,
    scenarioStats: scenarioStats,
    viewingIndex: scenarioIndex
  };
}

export function updateCompareScenarioIndex(id, compareScenarioIndex) {
  return (dispatch, getState) => {
    const project = getProject(getState(), id);

    const oldViewScenario = getViewingScenarioIndex(getState(), id);

    if (
      project &&
      project.settings &&
      project.settings.scenarioStats &&
      project.settings.scenarioStats[compareScenarioIndex] === undefined
    ) {
      dispatch(viewScenario(id, project.settings.scenarios[compareScenarioIndex], compareScenarioIndex));

      dispatch(viewScenario(id, project.settings.scenarios[oldViewScenario], oldViewScenario));
    }

    dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "compareScenarioIndex",
      value: compareScenarioIndex
    });
  };
}

export function getDefaultProjectSettings(state, project, aibleViewType = undefined) {
  let retProject = {
    aibleViewType: aibleViewType ? aibleViewType : getDefaultAibleViewType(state),
    drilldownValue: "Overall",
    runId: getOverallRunIdFromProject(project),
    innerId: getOverallRunIdFromProject(project),
    isLoading: false,
    sliderView: "Capacity",
    showPending: true,
    showShortListed: true,
    usePercentages: false
  };

  return retProject;
}

export function getDefaultAibleViewType(state) {
  const isBusinessMode = globalReducer.getIsBusinessMode(state);
  const defaultAibleViewType = isBusinessMode || true ? AIBLE_VIEW_TYPE.BUSINESS : AIBLE_VIEW_TYPE.ADVANCED;
  return defaultAibleViewType;
}

export function getAibleViewTypeOptions(state, projectId) {
  const notAllowed = {};
  if (globalReducer.getIsBusinessMode(state)) {
    notAllowed[AIBLE_VIEW_TYPE.ADVANCED] = true;
  }
  if (!monitoringReducer.getIsMonitoring()) {
    notAllowed[AIBLE_VIEW_TYPE.MONITORING] = true;
  }
  if (!deployReducer.getProjectDeployment(state, projectId)) {
    notAllowed[AIBLE_VIEW_TYPE.DEPLOYMENT] = true;
  }
  return Object.values(AIBLE_VIEW_TYPE)
    .filter((elem) => !notAllowed[elem])
    .map((key) => ({
      label: key,
      value: key
    }));
}

export function transformProjects(state, projects) {
  let retProjects = {};

  // Filter is for hack since projects < 30 can be broken and undeletable.
  projects
    .filter((p) => Number(p.id) > 60)
    .forEach((p) => {
      const currProject = getProject(state, p.id);
      if (!currProject || currProject.id !== p.id || currProject.updated_date !== p.updated_date) {
        retProjects[p.id] = {
          ...p,
          segment_count: p.segments ? p.segments.length : p.segment_count,
          segments: p.segments ? p.segments : [],
          settings: getDefaultProjectSettings(state, p)
        };
      }
    });

  return retProjects;
}

export function transformProject(state, response, aibleViewType = undefined, setIsLoading = false) {
  const project = getProject(state, response.id);

  const retProject = {
    ...response,
    settings:
      project && project.settings && project.settings.runId
        ? project.settings
        : getDefaultProjectSettings(state, response, aibleViewType)
  };

  if (setIsLoading) {
    if (!retProject.settings) {
      retProject.settings = {};
    }
    retProject.settings.isLoading = true;
  }

  return retProject;
}

export function fetchProject(id, aibleViewType = undefined, setIsLoading = false) {
  return (dispatch, getState) => {
    return ProjectAPI.getProject(id)
      .then((project) => {
        const segmentsPromise =
          project.segments !== undefined && project.segments.length
            ? Promise.resolve(project.segments)
            : ProjectAPI.getProjectSegments(id);

        return segmentsPromise.then((segments) => {
          let retSegments = segments.map((s) => ({
            ...s,
            conditions: s.conditions.map((c) => {
              if (c.value === "@@_@@Other@@_@@") {
                return { ...c, value: "Other" };
              } else if (c.type === ConditionType.In) {
                return {
                  column: c.column,
                  value: "Other"
                };
              } else if (c.type === ConditionType.NotIn && c.column !== undefined) {
                return {
                  column: c.column,
                  value: "Other"
                };
              } else if (
                c.type === ConditionType.Not &&
                c.condition !== undefined &&
                c.condition.column !== undefined
              ) {
                return {
                  column: c.condition.column,
                  value: "Other"
                };
              }

              return c;
            })
          }));

          dispatch(
            updateProject(
              transformProject(
                getState(),
                {
                  ...project,
                  segment_count: retSegments.length,
                  segments: retSegments
                },
                aibleViewType,
                setIsLoading
              )
            )
          );
          return dispatch(fetchScenarioFeedback(id));
        });
      })
      .catch((error) => {
        console.log(error);
        dispatch(unifiedProjectReducer.setProjectIsNotFound(id, true));
      });
  };
}

export function viewAssumption(projectId, setup, runId = undefined) {
  return (dispatch, getState) => {
    const retRunId = runId ? runId : getRunId(getState(), projectId);
    const currentSetup = setupReducer.getSetup(getState(), runId);

    dispatch(
      setupReducer.updateSetup(retRunId, {
        ...setup,
        defaultSetup: { data: { ...currentSetup } },
        jobSetup: { ...currentSetup.jobSetup }
      })
    );

    //return dispatch(resetBudget(projectId));
  };
}

export function checkSetup(projectId, runId) {
  return (dispatch, getState) => {
    const project = getProject(getState(), projectId);

    const segment =
      project && project.segments && project.segments.filter((s) => s.job && s.job.job_id === runId).length > 0
        ? project.segments.filter((s) => s.job && s.job.job_id === runId)[0]
        : undefined;

    if (segment && segment.assumption && Object.keys(segment.assumption).length > 0) {
      const actualSetup = setupReducer.getSetup(getState(), runId);

      dispatch(
        setupReducer.updateSetup(runId, {
          ...segment.assumption.data,

          defaultSetup: { data: { ...segment.assumption.data } },
          jobSetup: { ...actualSetup }
        })
      );
    }
    return Promise.resolve();
  };
}

export function fetchProjects(isConsumer = false) {
  return (dispatch, getState) => {
    const state = getState();
    let latestProjectId = paginationReducer.getProjectsPage(state);
    const folderId = foldersReducer.getSelectedFolder(state);
    dispatch(globalReducer.updateIsOptimizersLoading(true));
    return ProjectAPI.getProjects(
      isConsumer ? undefined : folderId,
      globalReducer.getProjectOffset(state),
      latestProjectId
    ).then((response) => {
      if (!isEnabled("aible-consumer") && folderId !== foldersReducer.getSelectedFolder(getState())) {
        return;
      }

      const retResponse = isConsumer ? response.filter((p) => p && p.segment_count) : response;

      dispatch(paginationReducer.storeLatestProject(retResponse));
      dispatch(updateProjects(transformProjects(getState(), retResponse)));
      dispatch(globalReducer.updateIsOptimizersLoading(false));

      if (isConsumer === true && retResponse && retResponse.length > 0) {
        if (isNaN(consumerReducer.getProjectId(getState()))) {
          const targetProject = retResponse
            .sort((a, b) => (b && b.id ? b.id : 0) - (a && a.id ? a.id : 0))
            .find((p) => p && p.segment_count);
          if (targetProject) {
            dispatch(consumerReducer.updateProjectId(targetProject.id));
          } else {
            toast("You have not been added to any projects. Please contact an admin for assistance.", {
              type: toast.TYPE.ERROR,
              position: toast.POSITION.TOP_CENTER,
              autoClose: false
            });
          }
        }
      }

      return Promise.resolve();
    });
  };
}

export function removeProject(projectId) {
  return (dispatch, getState) => {
    const projects = { ...getBase(getState()) };

    delete projects[projectId];

    return dispatch({ type: REPLACE_PROJECTS, data: projects });
  };
}

export function handleDelete(projectId, skipConfirm = false) {
  return (dispatch, getState) => {
    const shouldDelete = skipConfirm || confirm("Are you sure you want to delete this project?");

    if (shouldDelete) {
      return ProjectAPI.deleteProject(projectId).then((response) => {
        return dispatch(removeProject(projectId));
      });
    }
  };
}

export function createProject(runId, setup, nextId = undefined) {
  return (dispatch, getState) => {
    let promise;

    let existingProjectId = undefined;

    if (nextId !== undefined) {
      existingProjectId = nextReducer.getExploreProjectId(getState(), nextId);
    }

    if (existingProjectId !== undefined) {
      promise = PROJECT_CLIENT.getProject(existingProjectId).then((project) => {
        const global = project.segments.find((s) => s.conditions.length == 0);

        if (global !== undefined) {
          return ProjectAPI.createSegmentJob(global.id, {
            job_id: runId,
            status: "ACCEPTED"
          }).then(() => PROJECT_CLIENT.getProject(existingProjectId));
        } else {
          throw Error("missing overall segment");
        }
      });
    } else {
      const folderId = foldersReducer.getSelectedFolder(getState());

      const runAccountId = runReducer.getJobAccountId(getState(), runId);

      promise = PROJECT_CLIENT.createProject({
        account_id: runAccountId,
        name: setup.label,
        folder_id: folderId,
        segments: [
          {
            conditions: [],
            jobs: [
              {
                job_id: runId,
                status: "ACCEPTED"
              }
            ]
          }
        ]
      });
    }

    return promise.then((r) => {
      dispatch(updateProject(transformProject(getState(), r)));
      return r;
    });
  };
}

export function createEmptyProject(nextId) {
  return (dispatch, getState) => {
    const folderId = foldersReducer.getSelectedFolder(getState());
    let accountId = accountsReducer.getSelectedAccountId(getState());
    let name = "Test Project";

    if (nextId !== undefined) {
      const datasetId = nextReducer.getDatasetId(getState(), nextId);
      const dataset = homeReducer.getDataset(getState(), datasetId);
      accountId = dataset.account_id;
      name = dataset.name;
    }

    const projectJson = {
      account_id: accountId,
      name: name,
      folder_id: folderId,
      segments: [
        {
          conditions: []
        }
      ]
    };

    return ProjectAPI.createProject(projectJson).then((r) => {
      dispatch(updateProject(transformProject(getState(), r)));
      return r;
    });
  };
}

export function getProjectSubgroupUsers(state, projectId) {
  const users = {};
  const project = getProject(state, projectId) || {};
  const segments = project.segments || [];
  const overallSegmentId = getOverallSegmentId(state, projectId);
  segments.forEach((seg) => {
    if (!seg || seg.id === overallSegmentId) {
      return;
    }
    const roles = seg.roles ? seg.roles.filter((r) => r.value !== "VIEWER") : [];
    roles.forEach((role) => {
      const user = role.role_user;
      if (users[user.id] !== undefined) {
        users[user.id].count += 1;
      } else {
        users[user.id] = {
          ...user,
          count: 1
        };
      }
    });
  });
  return Object.values(users);
}

export function addUserRoleToSegment(projectId, segmentId, userId, role) {
  return (dispatch, getState) => {
    const project = getProject(getState(), projectId) || {};
    const segments = project.segments || [];
    const overallSegmentId = getOverallSegmentId(getState(), projectId);

    let otherSegmentsToUpdate = [];
    let otherSegmentsRole = "NONE";

    if (overallSegmentId == segmentId) {
      otherSegmentsToUpdate = segments.filter((segment) => segment.id !== segmentId);
      otherSegmentsRole = role;
    } else {
      otherSegmentsToUpdate = segments.filter(
        (segment) => segment.id !== segmentId && segment.roles.every((role) => role.role_user.id !== userId)
      );
      otherSegmentsRole = "VIEWER";
    }

    dispatch(updateIsLoadingSharing(projectId, true));

    const promise = ProjectAPI.addUserRoleToSegment(segmentId, userId, role).then(() =>
      Promise.all(
        otherSegmentsToUpdate.map((segment) => ProjectAPI.addUserRoleToSegment(segment.id, userId, otherSegmentsRole))
      )
    );

    return promise.then(() =>
      dispatch(fetchProject(projectId)).then(() => dispatch(updateIsLoadingSharing(projectId, false)))
    );
  };
}

export function deleteUserFromProject(projectId, userId, segmentId) {
  return (dispatch, getState) => {
    const project = getProject(getState(), projectId) || {};
    const segments = project.segments || [];

    const promises = segments
      .filter((segment) => (segmentId ? segment && segment.id === segmentId : segment))
      .flatMap((segment) => segment.roles)
      .filter((role) => role.role_user.id === userId)
      .map((role) => ProjectAPI.deleteUserRoleFromSegment(role.id));

    for (let role of project.roles || []) {
      if (role.role_user.id === userId) {
        promises.push(ProjectAPI.deleteProjectRole(role.id));
      }
    }

    return Promise.all(promises).then(() => dispatch(fetchProject(projectId)));
  };
}

export function deleteUserRoleFromSegment(projectId, segmentRoleId) {
  return (dispatch, getState) => {
    return ProjectAPI.deleteUserRoleFromSegment(segmentRoleId).then((r) => dispatch(fetchProject(projectId)));
  };
}

export function patchUserRoleSegment(projectId, segmentRoleId, role) {
  return (dispatch, getState) => {
    return ProjectAPI.patchUserRoleSegment(segmentRoleId, role).then((r) => dispatch(fetchProject(projectId)));
  };
}

export function fetchMonitorData(projectId) {
  return (dispatch, getState) => {
    const runId = getRunId(getState(), projectId);

    const originalDataset = setupReducer.getDatasetFromSetup(getState(), runId);

    return ScoreAPI.createDatasetFromMonitor(projectId).then((r) => {
      if (r && r.training_data_id) {
        const monitorDatasetId = r.training_data_id;

        dispatch(homeReducer.fetchDataset(monitorDatasetId));
        dispatch(updateRetrainDatasetId(projectId, monitorDatasetId));

        let retries = 0;
        return dispatch(homeReducer.pollProcessingDataset(monitorDatasetId))
          .then((nDataset) => {
            if (nDataset && nDataset.dataset_status !== "ERROR") {
              return dispatch(homeReducer.setDatasetTarget(monitorDatasetId, originalDataset.target));
            } else {
              dispatch(errorsReducer.updateError("There was an error processing your dataset."));
              return Promise.resolve();
            }
          })
          .then(() => {
            if (retries >= 2) {
              dispatch(errorsReducer.updateError("There was an error processing your dataset."));
              return Promise.resolve();
            } else {
              retries = retries + 1;
              return dispatch(homeReducer.pollProcessingDataset(monitorDatasetId));
            }
          });
      }

      return Promise.resolve();
    });
  };
}

export function createRunFromDataset(projectId, datasetId) {
  return (dispatch, getState) => {
    const state = getState();

    const segmentId = getSegmentId(state, projectId);
    const runId = getRunId(state, projectId);

    dispatch(setupReducer.updateDatasetId(runId, datasetId, true));
    dispatch(setupReducer.introspectCounts(runId));

    const setup = setupReducer.getSetup(getState(), runId);

    return dispatch(
      setupReducer.initJobFromWizard(runId, setup.blueprintId, {
        isUpdateDataset: true
      })
    ).then((retJobId) => {
      dispatch(setRunId(projectId, retJobId));
      dispatch(runReducer.pollJob(retJobId));

      return ProjectAPI.createJob(segmentId, retJobId).then((response) => {
        dispatch(fetchProject(projectId));
      });
    });
  };
}

function retrainRunHelper(projectId, segmentId, runId, setup) {
  return (dispatch, getState) => {
    return dispatch(
      setupReducer.initJobFromWizard(runId, setup.blueprintId, {
        isUpdateDataset: true,
        skipStrategyReset: true
      })
    ).then((retJobId) => {
      dispatch(runReducer.pollJob(retJobId, true));

      return ProjectAPI.createJob(segmentId, retJobId, "ACCEPTED").then(() => {
        return retJobId;
      });
    });
  };
}

function retrainRunCleanup(projectId, retJobId) {
  return (dispatch, getState) => {
    if (retJobId && isFinite(retJobId)) {
      dispatch(setRunId(projectId, retJobId));
      dispatch(updateInnerId(projectId, retJobId));
    }
    const currentScenarioIndex = getViewingScenarioIndex(getState(), projectId);
    return dispatch(fetchProject(projectId)).then(() => {
      if (!retJobId || !isFinite(retJobId)) {
        const overallJobId = getOverallRunId(getState(), projectId);
        dispatch(setRunId(projectId, overallJobId));
        dispatch(updateInnerId(projectId, overallJobId));
      }
      return dispatch(loadScenariosFromProject(projectId, false, false, true)).then(() => {
        const currScenario = getSelectedScenario(getState(), projectId, currentScenarioIndex);
        if (currScenario && currScenario.scenario) {
          dispatch(viewScenario(projectId, currScenario.scenario, currentScenarioIndex));
        }
        return dispatch(updateIsRetraining(projectId, false));
      });
    });
  };
}

export function retrainRun(projectId) {
  return (dispatch, getState) => {
    const state = getState();

    const segmentId = getSegmentId(state, projectId);
    const runId = getRunId(state, projectId);

    const setup = setupReducer.getSetup(getState(), runId) || {};

    dispatch(updateIsRetraining(projectId, true));

    if (getIsOverallSegment(getState(), projectId, runId)) {
      const segments = getProjectSegments(getState(), projectId, true);
      const retrainPromises = segments.map((segment) => {
        const currSegmentId = segment.id;
        const currRunId = getSegmentRunId(getState(), projectId, currSegmentId);

        const currSetup = setupReducer.getSetup(getState(), currRunId);
        return dispatch(retrainRunHelper(projectId, currSegmentId, currRunId, currSetup));
      });
      return Promise.all(retrainPromises).then((responses) => {
        return dispatch(retrainRunCleanup(projectId));
      });
    } else {
      return dispatch(retrainRunHelper(projectId, segmentId, runId, setup)).then((retJobId) => {
        return dispatch(retrainRunCleanup(projectId, retJobId));
      });
    }
  };
}

export function retrainProjectFromDataset(projectId, datasetId) {
  return (dispatch, getState) => {
    const segments = (getProject(getState(), projectId) || {}).segments || [];

    if (segments.some((s) => !s.permissions.can_edit)) {
      return; // no-op
    }

    const overallSegmentId = getOverallSegmentId(getState(), projectId);
    const overallRunId = getSegmentRunId(getState(), projectId, overallSegmentId);

    const originalSetup = setupReducer.getSetup(getState(), overallRunId);

    dispatch(setupReducer.updateDatasetId(overallRunId, datasetId, true));
    dispatch(setupReducer.introspectCounts(overallRunId));

    const setup = setupReducer.getSetup(getState(), overallRunId);
    const dataset = setupReducer.getDatasetFromSetup(getState(), overallRunId);

    const promises = [];

    const overallPromise = dispatch(
      setupReducer.initJobFromWizard(overallRunId, setup.blueprintId, {
        isSplitting: true,
        createProject: false
      })
    ).then((runJobId) => ProjectAPI.createJob(overallSegmentId, runJobId, "ACCEPTED"));

    promises.push(overallPromise);

    const firstOrderSegments = segments.filter((s) => s.conditions.length == 1);

    const segmentColumns = firstOrderSegments.map((s) => s.conditions).map((c) => c[0].column);

    const segmentValues = firstOrderSegments.map((s) => s.conditions).map((c) => c[0].value);

    const uniqueSegmentColumns = [...new Set(segmentColumns)];

    if (
      segmentColumns.some((c) => c == undefined) ||
      uniqueSegmentColumns.length != 1 ||
      segmentValues.some((v) => v == undefined)
    ) {
      dispatch(setupReducer.updateSetup(overallRunId, originalSetup));

      return Promise.all(promises).then(() => dispatch(fetchProject(projectId)));
    }

    const columnName = uniqueSegmentColumns[0];

    const columnFieldData = Utils.get(dataset, ["fields", columnName, "data"]) || [];

    const isContinuous = getIsContinuous(getState(), projectId);

    const MIN_ROW_COUNT = MIN_SEGMENT_ROWS;
    const MAX_CHILD_COUNT = isContinuous === true ? MAX_NUM_SEGMENTS_CONTINUOUS : MAX_NUM_SEGMENTS;

    const retColumns = columnFieldData
      .filter((v) =>
        setup &&
        setup.ignoredValue &&
        setup.ignoredValue[columnName] &&
        setup.ignoredValue[columnName][v.value] &&
        setup.ignoredValue[columnName][v.value] === true
          ? false
          : true
      )
      .sort((a, b) => b.count - a.count)
      .filter((v) => v.count > MIN_ROW_COUNT)
      .filter((v, i) => i < MAX_CHILD_COUNT);

    for (let segment of firstOrderSegments) {
      if (segment.conditions[0].value == "@@_@@Other@@_@@") {
        const specialVals = columnFieldData
          .filter((v) => segmentValues.every((sv) => sv != v))
          .map((v) => ({ column: columnName, value: v.value, operand: "=" }));

        if (specialVals.length > 0) {
          dispatch(setupReducer.createSetupFromSpecial(overallRunId, specialVals));

          const promise = dispatch(
            setupReducer.initJobFromWizard(overallRunId, setup.blueprintId, {
              isSplitting: true,
              createProject: false
            })
          ).then((runJobId) => ProjectAPI.createJob(segment.id, runJobId, "ACCEPTED"));

          dispatch(setupReducer.updateSetup(overallRunId, setup));

          promises.push(promise);
        }
      } else {
        for (let col of retColumns) {
          if (col.value == segment.conditions[0].value) {
            const promise = dispatch(createRunFromValue(overallRunId, columnFieldData, col.value)).then((runJobId) =>
              ProjectAPI.createJob(segment.id, runJobId, "ACCEPTED")
            );

            dispatch(setupReducer.updateSetup(overallRunId, setup));

            promises.push(promise);
          }
        }
      }
    }

    dispatch(setupReducer.updateSetup(overallRunId, originalSetup));

    return Promise.all(promises).then(() => dispatch(fetchProject(projectId)));
  };
}

export function restoreAssumption(projectId, assumptionId) {
  return (dispatch, getState) => {
    return ProjectAPI.updateAssumptionStatus(assumptionId, "DRAFT").then((r) =>
      dispatch(approveAssumption(projectId, assumptionId))
    );
  };
}

export function deploy(projectId, createDefaultScenario = false, forceCreateScenario = false) {
  return (dispatch, getState) => {
    return (() => {
      if (createDefaultScenario || forceCreateScenario) {
        const allScenarios = getAllScenarios(getState(), projectId);
        if (!allScenarios || allScenarios.length < 1 || forceCreateScenario) {
          return dispatch(
            saveScenario(projectId, forceCreateScenario === true ? "New Deployed Scenario" : "Default Scenario")
          );
        }
      }
      return Promise.resolve();
    })().then(() => {
      const deployment = deployReducer.getProjectDeployment(getState(), projectId);

      if (deployment === undefined) {
        dispatch(deployReducer.handleRestfulProjectDeploy(projectId));
      }

      const projectMonitor = getProjectMonitor(getState(), projectId) || {};

      const monitorPromise =
        projectMonitor.id === undefined
          ? ProjectAPI.createProjectMonitor(projectId, projectMonitor)
          : ProjectAPI.updateProjectMonitor(projectMonitor);

      const scenario = getSelectedScenario(getState(), projectId);

      if (scenario && scenario.originalScenario && scenario.originalScenario.elements) {
        return dispatch(deployScenario(projectId)).then(() => {
          const scenario = getSelectedScenario(getState(), projectId);
          const promises = scenario.originalScenario.elements.map(({ assumption_id }) =>
            ProjectAPI.approveAssumption(assumption_id)
          );

          promises.push(monitorPromise);

          return Promise.all(promises).then(() =>
            dispatch(fetchProject(projectId)).then(() => {
              dispatch(monitoringReducer.fetchMonitor(projectId));
              return dispatch(checkSetup(projectId, getRunId(getState(), projectId)));
            })
          );
        });
      }

      return Promise.resolve();
    });
  };
}

export function approveAssumption(projectId, assumptionId) {
  return (dispatch, getState) => {
    return ProjectAPI.approveAssumption(assumptionId).then((r) =>
      dispatch(fetchProject(projectId)).then((p) => dispatch(checkSetup(projectId, getRunId(getState(), projectId))))
    );
  };
}

export function rerunSegment(projectId, segmentId) {
  return (dispatch, getState) => {
    const segment = getSegment(getState(), projectId, segmentId);

    const setup = segment && segment.assumption && segment.assumption.data ? segment.assumption.data : undefined;

    dispatch(setupReducer.updateSetup("new", { ...setup }));

    return dispatch(
      setupReducer.initJobFromWizard("new", setup.blueprintId, {
        isSplitting: true,
        createProject: false
      })
    ).then((runId) =>
      ProjectAPI.createSegmentJob(segmentId, {
        job_id: runId,
        status: "ACCEPTED"
      }).then(() => dispatch(fetchProject(projectId)).then(() => dispatch(setRunId(projectId, runId))))
    );
  };
}

export function deleteAssumption(projectId, assumptionId) {
  return (dispatch, getState) => {
    return ProjectAPI.deleteAssumption(assumptionId).then((r) => dispatch(fetchProject(projectId)));
  };
}

export function getAssumptionById(state, projectId, assumptionId) {
  const project = getProject(state, projectId);

  let retAssumption = undefined;

  project &&
    project.segments &&
    project.segments.forEach(
      (s) =>
        s.history &&
        s.history.assumptions &&
        s.history.assumptions.forEach((a) => {
          if (a.id === assumptionId) {
            retAssumption = a;
          }
        })
    );

  return retAssumption;
}

export function getSetupDifference(fSetup, nSetup, fId, nId) {
  let dataset = [];

  if (!fSetup || !nSetup) {
    return {
      dataset: [{ Setup: "Missing Setup" }],
      ["SELECT_DATA"]: [{ Setup: "Missing Setup" }],
      ["REALITY_CHECK"]: [{ Setup: "Missing Setup" }],
      ["OBJECTIVE"]: [{ Setup: "Missing Setup" }],
      ["OPTIMIZE"]: [{ Setup: "Missing Setup" }],
      ["TRAIN"]: [{ Setup: "Missing Setup" }],
      ["DEPLOY"]: [{ Setup: "Missing Setup" }],
      ["FAIRNESS"]: [{ Setup: "Missing Setup" }]
    };
  }

  if (fSetup.datasetId !== nSetup.datasetId) {
    dataset.push({ "Dataset ID": nSetup.datasetId });
  }

  let blueprint = [];
  if (fSetup.blueprintId !== nSetup.blueprintId) {
    blueprint.push({ "Blueprint ID": nSetup.blueprintId });
    dataset.push({ "Blueprint ID": nSetup.blueprintId });
  }

  let behavior = [];
  const behaviorFields = [
    {
      value: "transactionLabel",
      label: "Transaction Label",
      default: "Opportunities"
    },
    { value: "outcomeLabel", label: "Outcome Label" },
    { value: "netImpactLabel", label: "Net Impact Label" },
    { value: "overPredictAction", label: "Over Predict Label" },
    { value: "underPredictAction", label: "Under Predict Label" },
    { value: "numberPredicted", label: "Number Predicted Label" },
    { value: "trueAction", label: "True Action", default: "Pursue" },
    { value: "falseAction", label: "False Action", default: "Ignore" },
    { value: "notPursuedTransactionLabel", label: "Not Pursued Transaction" },
    { value: "unitType", label: "Unit Type", default: "$" },
    { value: "truePositive", label: nSetup.truePositiveLabel },
    { value: "falsePositive", label: nSetup.falsePositiveLabel },
    { value: "trueNegative", label: nSetup.trueNegativeLabel },
    { value: "falseNegative", label: nSetup.falseNegativeLabel },
    { value: "pursuedRatio", label: "Pursued Ratio" },
    { value: "notPursuedRatio", label: "Not Pursued Ratio" },
    { value: "baselineValueContinuous", label: "Baseline Average Price" },
    { value: "baselineCountContinuous", label: "Baseline Transaction Count" },
    { value: "expectedValueContinuous", label: "Average Price" },
    { value: "expectedCountContinuous", label: "Transaction Count" },
    { value: "continuousPercent", label: "Best Case Impact" },
    { value: "currentProfit", label: "Current Total Impact" },
    { value: "timePeriod", label: "Prediction Time Period" },
    { value: "showImpact", label: "Has Business Drivers" },
    { value: "splitColumn", label: "Subgroup" },
    {
      value: "businessObjective",
      label: "Business Objective",
      default: BUSINESS_OBJECTIVE.PROFIT
    },
    {
      value: "fairnessObjective",
      label: "Fairness Objective",
      default: FAIRNESS_OBJECTIVE.NONE
    },
    {
      value: "fairnessToleranceBound",
      label: "Fairness Tolerance Bound",
      default: 1
    },
    { value: "targetPositiveAllocation", label: "Fairness Target Allocation" },
    {
      value: "biasReduction",
      label: "Fairness Target Allocation Metric",
      default: BIAS_REDUCTION.COUNT
    }
  ];

  behaviorFields.forEach((f) => {
    if (
      fSetup[f.value] !== nSetup[f.value] &&
      (fSetup[f.value] !== f.default || nSetup[f.value] !== undefined) &&
      (nSetup[f.value] !== f.default || fSetup[f.value] !== undefined)
    ) {
      behavior.push({ [f.label]: nSetup[f.value] });
    }
  });

  let objective = [];
  const overallStrategyFields = [
    {
      value: "businessConstraints",
      label: "Primary Strategy Goal"
    },
    {
      value: "businessConstraints",
      label: "Secondary Strategy Goal"
    },
    {
      value: "businessConstraints",
      label: "Other Strategy Goals"
    }
  ];

  const defaultBusinessConstraints = [
    {
      mode: setupReducer.BusinessConstraintMode.Percent,
      type: SegmentMetric.Profit
    }
  ];

  const fBusinessConstraints = fSetup.businessConstraints || defaultBusinessConstraints;

  const nBusinessConstraints = nSetup.businessConstraints || defaultBusinessConstraints;

  if (fBusinessConstraints.length !== nBusinessConstraints.length) {
    objective.push({
      "Overall Strategy": "businessConstraints"
    });
  } else if (fBusinessConstraints && nBusinessConstraints) {
    for (let i = 0; i < fBusinessConstraints.length; i++) {
      const fConstraint = fBusinessConstraints[i];
      const nConstraint = nBusinessConstraints[i];

      const fKeys = Object.keys(fConstraint).sort();
      const nKeys = Object.keys(nConstraint).sort();

      if (
        fKeys.length !== nKeys.length ||
        JSON.stringify(fKeys) !== JSON.stringify(nKeys) ||
        !fKeys.every((fk, fIndex) => fConstraint[fKeys[fIndex]] === nConstraint[nKeys[fIndex]])
      ) {
        objective.push(overallStrategyFields[Math.max(i, overallStrategyFields.length)]);
      }
    }
  }

  const fIgnoredModels = fSetup.ignoredModels || {};
  const nIgnoredModels = nSetup.ignoredModels || {};

  if (Object.keys(fIgnoredModels).length !== Object.keys(nIgnoredModels).length) {
    behavior.push({ ["Ignored Models"]: nIgnoredModels });
  } else if (fIgnoredModels && nIgnoredModels) {
    if (JSON.stringify(fIgnoredModels) !== JSON.stringify(nIgnoredModels)) {
      behavior.push({ ["Ignored Models"]: nIgnoredModels });
    }
  }

  let outcome = [];
  const outcomeFields = [
    { value: "conversionGain", label: nSetup.truePositiveLabel + " Benefit" },
    { value: "conversionCost", label: nSetup.falsePositiveLabel + " Cost" },
    { value: "conversionSaved", label: nSetup.trueNegativeLabel + " Benefit" },
    { value: "conversionMissed", label: nSetup.falseNegativeLabel + " Cost" },
    {
      value: "baselineConversionGain",
      label: "Baseline " + nSetup.truePositiveLabel + " Benefit"
    },
    {
      value: "baselineConversionCost",
      label: "Baseline " + nSetup.falsePositiveLabel + " Cost"
    },
    {
      value: "baselineConversionSaved",
      label: "Baseline " + nSetup.trueNegativeLabel + " Benefit"
    },
    {
      value: "baselineConversionMissed",
      label: "Baseline " + nSetup.falseNegativeLabel + " Cost"
    },
    { value: "minBudget", label: "Min Budget" },
    { value: "maxBudget", label: "Max Budget" },
    { value: "isRealityHammer", label: "Reality Scaling" },
    { value: "scaledTotal", label: "Scaled Total" },
    { value: "outcomePercentage", label: "Outcome Percentage" },
    { value: "linkedProjectId", label: "Linked Project Id" },
    { value: "hasLinkedProject", label: "Has Linked Project" },
    { value: "linkedProjectOutputMode", label: "Linked Project Output" },
    { value: "linkedScenarioId", label: "Linked Scenario Id" }
    // { value: "optimize_by", label: "Field to Optimize By" },
    // { value: "optimize_goal", label: "Optimization Goal" },
    // { value: "n_opts", label: "Number of Optimization Results" },
  ];

  outcomeFields.forEach((f) => {
    if (
      isNaN(fSetup[f.value]) || isNaN(nSetup[f.value])
        ? fSetup[f.value] !== nSetup[f.value]
        : Number(fSetup[f.value]) !== Number(nSetup[f.value])
    ) {
      outcome.push({ [f.label]: nSetup[f.value] });
    }
  });

  const outcomeCustomValues = [{ value: "customValues", label: "Custom Values" }];

  outcomeCustomValues.forEach((f) => {
    const fField = fSetup[f.value];
    const nField = nSetup[f.value];
    if (Boolean(fField) !== Boolean(nField)) {
      outcome.push({ [f.label]: "Missing" });
    } else if (fField && nField) {
      const retFields = {
        ...fField,
        ...nField
      };
      Object.keys(retFields).forEach((cValue) => {
        if (fField[cValue] !== nField[cValue]) {
          outcome.push({ [f.label]: cValue });
        }
      });
    }
  });

  let models = [];

  let fairness = [];
  const fairnessFields = [
    { value: "fairnessObjective", label: "Fairness Objective" },
    { value: "fairnessToleranceBound", label: "Fairness Tolerance Bound" },
    { value: "targetPositiveAllocation", label: "Target Positive Allocation" },
    { value: "biasReduction", label: "Bias Reduction" }
  ];

  fairnessFields.forEach((f) => {
    if (
      isNaN(fSetup[f.value]) || isNaN(nSetup[f.value])
        ? fSetup[f.value] !== nSetup[f.value]
        : Number(fSetup[f.value]) !== Number(nSetup[f.value])
    ) {
      outcome.push({ [f.label]: nSetup[f.value] });
    }
  });

  /*
  if (fId !== undefined && nId !== undefined) {
    const fRun = runReducer.getJob(getState(), fId);
    const nRun = runReducer.getJob(getState(), nId);

    if (
      nRum &&
      nRun.models &&
      fRun &&
      fRun.models &&
      nRun.models.length !== fRun.models.length
    ) {
      models.push({ "Number of Models": fRun.models.length });
    }

    console.log(nRun);
  }

  if(fId === undefined || nId !== undefined) {
    const nRun = runReducer.getJob(getState(), fId);

    if (
      nRum &&
      nRun.models
    ) {
      models.push({ "Number of Models": nRun.models.length });
    }

  }
  */

  let deployment = [];

  return {
    dataset: dataset,
    ["SELECT_DATA"]: dataset,
    ["REALITY_CHECK"]: behavior,
    ["OBJECTIVE"]: objective,
    ["OPTIMIZE"]: outcome,
    ["TRAIN"]: models,
    ["DEPLOY"]: deployment,
    ["FAIRNESS"]: fairness
  };
}

export function isSameSetup(sOne, sTwo, skipFrontier = true) {
  if (sOne === undefined || sTwo === undefined) {
    return true;
  }

  const diffItems = getSetupDifference(sOne, sTwo);

  let isSame = true;

  Object.keys(diffItems).forEach((i) => {
    if (diffItems[i].length > 0) {
      isSame = false;
    }
  });

  return isSame;

  /*
  return (
    Number(sOne.conversionGain) === Number(sTwo.conversionGain) &&
    Number(sOne.conversionCost) === Number(sTwo.conversionCost) &&
    Number(sOne.conversionMissed) === Number(sTwo.conversionMissed) &&
    Number(sOne.conversionSaved) === Number(sTwo.conversionSaved) &&
    Number(sOne.truePositive) === Number(sTwo.truePositive) &&
    Number(sOne.falsePositive) === Number(sTwo.falsePositive) &&
    Number(sOne.trueNegative) === Number(sTwo.trueNegative) &&
    Number(sOne.falseNegative) === Number(sTwo.falseNegative) &&
    sOne.advancedRevenueLabel === sTwo.advancedRevenueLabel &&
    sOne.trueAction === sTwo.trueAction &&
    sOne.falseAction === sTwo.falseAction &&
    sOne.minBudget === sTwo.minBudget &&
    sOne.maxBudget === sTwo.maxBudget &&
    sOne.expectedCountContinuous === sTwo.expectedCountContinuous &&
    JSON.stringify(sOne.linkedRuns) === JSON.stringify(sTwo.linkedRuns) &&
    (skipFrontier ||
      JSON.stringify(sOne.frontier) === JSON.stringify(sTwo.frontier))
  );
  */
}

export function getViewingScenarioIndex(state, id) {
  const project = getProject(state, id);

  return project && project.settings ? project.settings.viewingIndex : undefined;
}

export function getSelectedScenario(state, id, index = undefined) {
  const project = getProject(state, id);
  const viewingScenarioIndex = index !== undefined ? index : getViewingScenarioIndex(state, id);

  let retScenario =
    viewingScenarioIndex !== undefined &&
    project &&
    project.settings &&
    project.settings.scenarios &&
    project.settings.scenarios[viewingScenarioIndex] &&
    project.settings.originalScenarios &&
    project.settings.originalScenarios[viewingScenarioIndex]
      ? {
          scenario: project.settings.scenarios[viewingScenarioIndex],
          scenarioStats: project.settings.scenarioStats[viewingScenarioIndex],
          originalScenario: project.settings.originalScenarios[viewingScenarioIndex],
          index: viewingScenarioIndex,
          isApproved: viewingScenarioIndex == getDeployedScenarioIndex(state, id)
        }
      : undefined;

  if (retScenario !== undefined) {
    Object.keys(retScenario.scenario).forEach((k) => {
      retScenario.scenario[k].bestModel = runReducer.getFirstModel(state, retScenario.scenario[k].runId);

      retScenario.scenario[k].totalWorked =
        retScenario.scenario[k].bestModel &&
        retScenario.scenario[k].bestModel.maxIteration &&
        retScenario.scenario[k].bestModel.maxIteration.confusionMatrix
          ? retScenario.scenario[k].bestModel.maxIteration.confusionMatrix.tp +
            retScenario.scenario[k].bestModel.maxIteration.confusionMatrix.fp
          : 0;

      retScenario.scenario[k].total =
        retScenario.scenario[k].bestModel &&
        retScenario.scenario[k].bestModel.maxIteration &&
        retScenario.scenario[k].bestModel.maxIteration.confusionMatrix
          ? retScenario.scenario[k].bestModel.maxIteration.confusionMatrix.tp +
            retScenario.scenario[k].bestModel.maxIteration.confusionMatrix.fp +
            retScenario.scenario[k].bestModel.maxIteration.confusionMatrix.tn +
            retScenario.scenario[k].bestModel.maxIteration.confusionMatrix.fn
          : 0;
    });
  }

  return retScenario;
}

export function getScenarioWithConfusionMatrix(state, id, cm) {
  const project = getProject(state, id);
  const scenarios = project && project.settings && project.settings.scenarios;
  if (!scenarios) {
    return [];
  }
  const retScenarios = [];
  const viewingScenarioIndex = getViewingScenarioIndex(state, id);
}

export function getUnderImpactFromSetup(setup, underSum) {
  const conversionCost = setup && setup.data && setup.data.conversionCost;
  return Math.abs(underSum * Number(conversionCost));
}

export function getOverImpactFromSetup(setup, overSum) {
  const conversionGain = setup && setup.data && setup.data.conversionGain;
  return Math.abs(overSum * Number(conversionGain));
}

export function getErrorCostFromSetup(setup, underSum, overSum) {
  return getUnderImpactFromSetup(setup, underSum) + getOverImpactFromSetup(setup, overSum);
}

export function getApprovedScenario(state, id, runId) {
  const segment = getSegmentByRunId(state, id, runId);

  return segment && segment.assumption ? segment.assumption : undefined;
}

export function getScenarioCount(state, id) {
  const project = getProject(state, id);

  return project && project.settings && project.settings.scenarios ? project.settings.scenarios.length : 0;
}

export function getScenarios(state, id) {
  const project = getProject(state, id);

  return project && project.settings && project.settings.scenarios ? project.settings.scenarios : [];
}

export function getAllScenarios(state, id) {
  const project = getProject(state, id);

  const bestModel = getBestModel(state, id);

  const isContinuous =
    bestModel &&
    bestModel.metrics &&
    bestModel.metrics.overImpact !== undefined &&
    bestModel.metrics.underImpact !== undefined;

  const underPredictSum = bestModel && bestModel.metrics && bestModel.metrics.sum_under_predict;
  const overPredictSum = bestModel && bestModel.metrics && bestModel.metrics.sum_over_predict;

  const retScenarios = [];

  const selectedScenario = getSelectedScenario(state, id);

  project &&
    project.settings &&
    project.settings.scenarios &&
    project.settings.scenarios.map((s, i) => {
      const retScenario = {
        ...getSelectedScenario(state, id, i)
      };

      retScenario.scenario &&
        Object.keys(retScenario.scenario).forEach((k) => {
          if (isContinuous) {
            retScenario.scenario[k].transformed = selectedScenario
              ? {
                  underPredict: getUnderImpactFromSetup(retScenario.scenario[k].setup, underPredictSum),
                  overPredict: getOverImpactFromSetup(retScenario.scenario[k].setup, overPredictSum)
                }
              : {
                  underPredict: 0,
                  overPredict: 0
                };
          } else {
            retScenario.scenario[k].transformed = selectedScenario
              ? {
                  profit: getProfitFromMatrix(
                    state,
                    getConstrainedConfusionMatrix(state, id, selectedScenario.scenario[k].runId),
                    retScenario.scenario[k].runId,
                    retScenario.scenario[k].setup
                  ),
                  revenue: getRevenueFromMatrix(
                    state,
                    getConstrainedConfusionMatrix(state, id, selectedScenario.scenario[k].runId),
                    retScenario.scenario[k].runId
                  ),
                  cost: getCostFromMatrix(
                    state,
                    getConstrainedConfusionMatrix(state, id, selectedScenario.scenario[k].runId),
                    retScenario.scenario[k].runId
                  )
                }
              : {
                  profit: 0,
                  revenue: 0,
                  cost: 0
                };
          }
        });

      retScenarios.push(retScenario);
    });

  retScenarios.forEach((s, i) => {
    if (retScenarios[i].scenario !== undefined) {
      const filteredScenarios = Object.keys(retScenarios[i].scenario).filter(
        (k, i, sList) => sList.length === 1 || k !== "Overall"
      );
      if (isContinuous) {
        retScenarios[i].overPredict = filteredScenarios.reduce(
          (a, b) => a + retScenarios[i].scenario[b].transformed.overPredict,
          0
        );
        retScenarios[i].underPredict = filteredScenarios.reduce(
          (a, b) => a + retScenarios[i].scenario[b].transformed.underPredict,
          0
        );
      } else {
        retScenarios[i].profit = filteredScenarios.reduce(
          (a, b) => a + retScenarios[i].scenario[b].transformed.profit,
          0
        );
        retScenarios[i].revenue = filteredScenarios.reduce(
          (a, b) => a + retScenarios[i].scenario[b].transformed.revenue,
          0
        );
        retScenarios[i].cost = filteredScenarios.reduce((a, b) => a + retScenarios[i].scenario[b].transformed.cost, 0);
      }
    }
  });

  return retScenarios;
}

export function hasDeployedScenario(state, id) {
  const segments = getSegments(state, id);
  const optimized = getOptimizer(state, id);

  return (
    (deployReducer.getProjectDeployment(state, id) &&
      optimized &&
      optimized.originalScenarios &&
      optimized.originalScenarios.find((scen) => scen.status === SCENARIO_STATUS.ACCEPTED)) ||
    (segments !== undefined &&
      segments.length > 0 &&
      segments.every((segment) => Utils.get(segment, ["assumption", "data", "frontier"])))
  );
}

export function getCompareScenarioIndex(state, id) {
  const project = getProject(state, id);

  return project && project.settings && project.settings.compareScenarioIndex !== undefined
    ? project.settings.compareScenarioIndex
    : 0;
}

export function getCurrentScenario(state, id, indexOverride) {
  const project = getProject(state, id);

  const scenarioIndex = indexOverride !== undefined ? indexOverride : getViewingScenarioIndex(state, id);

  return project &&
    project.settings &&
    project.settings.scenarios &&
    project.settings.scenarioStats &&
    project.settings.originalScenarios &&
    scenarioIndex !== undefined &&
    project.settings.scenarios.length > scenarioIndex &&
    project.settings.originalScenarios.length > scenarioIndex &&
    project.settings.scenarioStats[scenarioIndex] !== undefined
    ? {
        stats: project.settings.scenarioStats[scenarioIndex],
        original: project.settings.originalScenarios[scenarioIndex],
        data: project.settings.scenarios[scenarioIndex]
      }
    : undefined;
}

export function deleteScenario(id) {
  return (dispatch, getState) => {
    const state = getState();
    const currentScenario = getCurrentScenario(state, id);
    const scenarioIndex = getViewingScenarioIndex(state, id);

    if (currentScenario && currentScenario.original && currentScenario.original.id !== undefined) {
      return ProjectAPI.deleteScenario(currentScenario.original.id).then((r) => {
        const projectSettings = getOptimizer(state, id);
        const originalScenarios = [...projectSettings.originalScenarios];
        const scenarioStats = {};

        Object.keys(projectSettings.scenarioStats).forEach((statsKey, i) => {
          let currKey = parseInt(statsKey);
          if (currKey < scenarioIndex) {
            scenarioStats[currKey] = projectSettings.scenarioStats[currKey];
          } else if (currKey > scenarioIndex) {
            scenarioStats[currKey - 1] = projectSettings.scenarioStats[currKey];
          }
        });

        let scenarios = [...projectSettings.scenarios];

        originalScenarios.splice(scenarioIndex, 1);
        scenarios.splice(scenarioIndex, 1);

        scenarios = scenarios.map((scen, i) => {
          const retScen = {};
          Object.keys(scen).forEach((sKey) => {
            retScen[sKey] = {
              ...scen[sKey],
              id: i + 1
            };
          });
          return retScen;
        });

        dispatch(updateSettingScenario(id, scenarios, originalScenarios, scenarioStats, 0));
        return dispatch(setScenario(id));
      });
    }

    return Promise.resolve();
  };
}

export function getDeployedScenarioIndex(state, projectId) {
  const project = getProject(state, projectId);
  const settings = project && project.settings;
  const segments = project && project.segments;
  let selectedScenario = {
    updated: null,
    index: undefined
  };
  if (settings && settings.originalScenarios && segments) {
    //list of current assumption ids
    const assumptionIds = segments.map((segment) => segment.assumption && segment.assumption.id);
    //assumption ids from each saved scenario
    const originalScenarioElementIds = settings.originalScenarios.map((scenario, index) => ({
      scenarioIndex: index,
      element: scenario.elements.map((element) => element.assumption_id)
    }));

    if (originalScenarioElementIds.every((s) => s.element.length == 0)) {
      return settings.originalScenarios
        .map((scenario, index) => ({ scenario, index }))
        .filter((x) => x.scenario.status == SCENARIO_STATUS.ACCEPTED)
        .sort((a, b) => {
          if (b.scenario.updated_date < a.scenario.updated_date) {
            return -1;
          } else if (b.scenario.updated_date > a.scenario.updated_date) {
            return 1;
          }
          return 0;
        })?.[0]?.index;
    }

    const deployedScenario = originalScenarioElementIds.find((scenario) => {
      const elementIds = scenario.element;
      let numMatches = 0;
      assumptionIds.forEach((assumption) => {
        if (elementIds.includes(assumption)) {
          numMatches += 1;
        }
      });
      return numMatches > 0;
    });
    if (deployedScenario) {
      selectedScenario.index = deployedScenario.scenarioIndex;
    }
  }
  return selectedScenario.index;
}

export function updateCurrentScenarioStatusAndSave(id, status) {
  return (dispatch, getState) => {
    const currentScenario = getCurrentScenario(getState(), id);

    if (currentScenario && currentScenario.original && currentScenario.original.id !== undefined) {
      const update = {
        status: status
      };

      return ProjectAPI.patchScenario(currentScenario.original.id, update).then((r) => dispatch(updateScenario(r)));
    }

    return Promise.resolve(currentScenario);
  };
}

export function updateAllScenariosWithLinkedRuns(id) {
  return async (dispatch, getState) => {
    const linkedProjectId = getLinkedProjectId(getState(), id);
    const runIds = getAllJobIdsFromOptimizer(getState(), id);

    const allScenarios = getAllScenarios(getState(), id);
    const project = getProject(getState(), id);

    const patchPromises = (allScenarios ?? []).map((s) => {
      const update = {
        segments: (s?.originalScenario?.segments ?? []).map((seg) => {
          const runId = project?.segments.find((pSeg) => seg.id === pSeg.id)?.job?.job_id;

          const linkedRuns = setupReducer.getLinkedRuns(getState(), runId);
          const currentSetup = setupReducer.getSetup(getState(), runId);

          const assumptions = [...(seg?.assumptions ?? [])];
          assumptions[0] = {
            type: "ALL",
            data: {
              ...(assumptions[0]?.data ?? {}),
              linkedProjectId,
              linkedRuns,
              linkedProjectReportId: currentSetup?.linkedProjectReportId,
              linkedScenarioId: currentSetup?.linkedScenarioId,
              hasLinkedProject: linkedProjectId !== undefined ? true : false
            }
          };

          const { data, ...segWithoutData } = seg;
          return {
            ...segWithoutData,
            assumptions
          };
        })
      };

      return ProjectAPI.patchScenario(s.originalScenario.id, update).then((r) => dispatch(updateScenario(r)));
    });

    await Promise.all(patchPromises);

    return dispatch(fetchProject(id));
  };
}

export function updateScenario(scenario) {
  return (dispatch, getState) => {
    const project = getProject(getState(), scenario.project_id);
    // const segments = project.segments ? project.segments : [];

    const oScenarios = [...project.settings.originalScenarios];
    // const scenarios = [...project.settings.scenarios];

    let index = undefined;

    for (let i = 0; i < oScenarios.length; i++) {
      if (oScenarios[i].id == scenario.id) {
        oScenarios[i] = scenario;
        index = i;
        break;
      }
    }

    if (index === undefined) {
      oScenarios.push(scenario);
    }

    dispatch({
      type: UPDATE_SETTINGS,
      key: scenario.project_id,
      subKey: "originalScenarios",
      value: oScenarios
    });

    return scenario;
  };
}

export function deployScenario(id) {
  return (dispatch, getState) => {
    if (isEnabled("aible-next-api")) {
      const project = getProject(getState(), id);

      const clientFactory = AibleClientFactory.getInstance();

      clientFactory
        .getAccountClient()
        .getCustomerClientFactory(project.account_id)
        .then((factory) => factory.getProjectClient().deploy(getFrontierDeploymentPayload(getState(), id)));
    }

    return dispatch(updateCurrentScenarioStatusAndSave(id, SCENARIO_STATUS.PENDING)).then(() => {
      return dispatch(updateCurrentScenarioStatusAndSave(id, SCENARIO_STATUS.ACCEPTED));
    });
  };
}

export function shortlistScenario(id) {
  return (dispatch, getState) => {
    return dispatch(updateCurrentScenarioStatusAndSave(id, SCENARIO_STATUS.PENDING));
  };
}

export function removeShortlist(id) {
  return (dispatch, getState) => {
    return dispatch(updateCurrentScenarioStatusAndSave(id, SCENARIO_STATUS.DRAFT));
  };
}

export function saveScenario(id, name, description, skipProjectUpdate) {
  return (dispatch, getState) => {
    const project = getProject(getState(), id);

    const scenarioSegments = project.segments
      .filter((s) => s.permissions.can_draft)
      .map((s) => {
        let rSetup = s.job && s.job.job_id !== undefined ? setupReducer.getSetup(getState(), s.job.job_id) : undefined;

        const frontier = getCurrentFrontier(getState(), id, s.id);

        let setup = {
          ...JSON.parse(JSON.stringify(rSetup)),
          frontier: frontier
        };

        setup.ignored = setupReducer.getIgnored(getState(), s.job && s.job.job_id);
        setup.ignoredValue = setupReducer.getIgnoredValue(getState(), s.job && s.job.job_id);

        const { defaultSetup, ...rSetupNoDefault } = rSetup;

        setup.defaultSetup = { data: rSetupNoDefault };

        const assumptions = [];

        //test
        if (
          s.assumption === undefined ||
          rSetup.fronter === undefined ||
          !isSameSetup(s.assumption.data, setup, false)
        ) {
          assumptions.push({
            type: "ALL",
            data: setup
          });
        }

        return {
          id: s.id,
          assumptions: assumptions
        };
      })
      .filter((x) => x.assumptions.length > 0);

    const retScenario = {
      name: name,
      status: "DRAFT",
      segments: scenarioSegments
    };

    if (description) {
      retScenario.description = description;
    } else {
      const optimized = getOptimized(getState(), id);
      const totalCost = !isNaN(optimized?.totalCost) ? optimized?.totalCost : 0;
      const profit = !isNaN(optimized?.profit) ? optimized?.profit : 0;
      const revenue = !isNaN(optimized?.revenue) ? optimized?.revenue : 0;

      const smartRound = (val) => {
        if (val > 1000) {
          return Math.round(val);
        }
        if (val > 10) {
          return Number(val.toFixed(2));
        }
        return val;
      };

      const retDescription = `${optimized?.profitLabel} is ${smartRound(profit)}, ${optimized?.resourcingLabel} is ${smartRound(totalCost)}, and ${optimized?.revenueLabel} is ${smartRound(revenue)}`;

      retScenario.description = retDescription;
    }

    let scenarios = project && project.settings && project.settings.scenarios ? [...project.settings.scenarios] : [];

    let rScenario = {};

    project.segments.forEach((s) => {
      const scenarioSegment = scenarioSegments.find((x) => x.id == s.id) ?? {
        assumptions: [{ data: s.assumption.data }]
      };

      const mergedSetup = scenarioSegment.assumptions.map((a) => a.data).reduce((x, y) => ({ ...x, ...y }), {});

      rScenario[s.conditions.length > 0 ? s.conditions[0].value : "Overall"] = {
        project_segment_id: s.id,
        setup: { data: mergedSetup },
        runId: s.job.job_id,
        id: scenarios.length + 1
      };
    });

    scenarios.push(rScenario);

    dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "scenarios",
      value: scenarios
    });

    return ProjectAPI.createScenario(id, retScenario).then((s) => {
      if (skipProjectUpdate) {
        return Promise.resolve();
      }
      return dispatch(fetchProject(id))
        .then(() => {
          dispatch(updateScenario(s));
          dispatch(setScenario(id, s.id));
          return Promise.resolve();
        })
        .catch((e) => {
          // toast(<div>{"Your scenario saved successfully, but there was an issue fetching your updated project. Please refresh the page."}</div>, {
          //   type: toast.TYPE.ERROR,
          //   position: toast.POSITION.TOP_CENTER,
          //   autoClose: 7500,
          // });
        });
    });
  };
}

export function viewAllScenarios(id) {
  return (dispatch, getState) => {
    dispatch(updateIsLoadingCompare(id, true));
    return dispatch(loadScenariosFromProject(id));
  };
}

export function loadScenariosFromProject(id, forceView = true, skipIfExists = false, skipViewEach = false) {
  return (dispatch, getState) => {
    const projectSettings = getOptimizer(getState(), id);
    // Skips scenario loading if argument is passed and scenarios already exist
    // Defaults to off due to our scenario deletion logic

    if (
      skipIfExists &&
      projectSettings.originalScenarios !== undefined &&
      projectSettings.scenarioStats !== undefined &&
      projectSettings.scenarios !== undefined
    ) {
      if (forceView) {
        dispatch(setScenario(id));
      }
      dispatch(updateIsLoadingProject(id, false));
      return Promise.resolve();
    }

    const project = getProject(getState(), id) || {};

    return Promise.resolve(project.scenarios).then((response) => {
      const project = getProject(getState(), id) || {};

      const segments = project.segments ? project.segments : [];

      let retScenarios = [];
      let originalScenarios = [];

      response.forEach((scenario, sIndex) => {
        let retScenario = {};
        originalScenarios.push(scenario);

        segments.forEach((s, i) => {
          retScenario[s.conditions.length > 0 ? s.conditions[0].value : "Overall"] = {
            project_segment_id: s.id,
            setup: s.assumption,
            runId: s.job.job_id,
            id: sIndex + 1
          };
        });

        scenario.elements.forEach((e) => {
          const assumption = getAssumptionById(getState(), id, e.assumption_id);

          Object.keys(retScenario).forEach((s) => {
            if (assumption && retScenario[s].project_segment_id === assumption.project_segment_id) {
              retScenario[s].setup = assumption;
              retScenario[s].assumption_id = e.assumption_id;
            }
          });
        });

        scenario.segments.forEach((segment) => {
          Object.keys(retScenario).forEach((s) => {
            if (retScenario[s].project_segment_id === segment.id) {
              retScenario[s].setup = { data: segment.data };
            }
          });
        });

        retScenarios.push(retScenario);
      });

      const viewingIndex = getViewingScenarioIndex(getState(), id);

      const scenarioStats = { ...(project.settings || {}).scenarioStats };

      dispatch(updateSettingScenario(id, retScenarios, originalScenarios, scenarioStats, viewingIndex));

      if (forceView) {
        if (skipIfExists) {
          dispatch(setScenario(id));
        } else {
          for (let i = retScenarios.length; i-- > 0; ) {
            if (retScenarios[i] && Object.keys(retScenarios[i]).length > 0) {
              const innerScenario = retScenarios[i][Object.keys(retScenarios[i])[0]];
            }
            if (skipViewEach === false) {
              dispatch(viewScenario(id, retScenarios[i], i));
            }
          }
        }
      }

      const deployedScenarioIndex = getDeployedScenarioIndex(getState(), id);
      if (deployedScenarioIndex !== undefined) {
        const deployedScenario = retScenarios[deployedScenarioIndex];
        dispatch(viewScenario(id, deployedScenario, deployedScenarioIndex));
      } else if (skipViewEach !== false && retScenarios.length > 0) {
        const scenIndex = Math.max(retScenarios.length - 1, 0);
        dispatch(viewScenario(id, retScenarios[scenIndex], scenIndex));
      }

      dispatch(updateIsLoadingProject(id, false));

      if (skipViewEach === false) {
        dispatch(updateIsLoadingCompare(id, false));
      }

      //dispatch(updateCompareScenarioIndex(id, retScenarios.length - 1));
      return Promise.resolve();
    });
  };
}

export function createSegmentTask(
  projectSegmentId,
  assigned_to,
  type,
  name,
  status = undefined,
  description = undefined,
  data = undefined
) {
  return (dispatch, getState) => {
    const task = { assigned_to, type, name, status, description, data };

    return PROJECT_CLIENT.createProjectSegmentTask(projectSegmentId, task).then((segment) => {
      dispatch(fetchProject(segment.project_id));

      const retTask = segment?.tasks && segment.tasks.length > 0 ? segment.tasks[segment.tasks.length - 1] : undefined;

      dispatch(tasksReducer.updateTask(retTask));

      return retTask;
    });
  };
}

export function addTaskToSegment(projectSegmentId, taskId) {
  return (dispatch, getState) => {
    return PROJECT_CLIENT.addProjectSegmentTask(projectSegmentId, taskId).then((segment) =>
      dispatch(fetchProject(segment.project_id))
    );
  };
}

export function removeTaskFromSegment(projectSegmentId, taskId) {
  return (dispatch, getState) => {
    return PROJECT_CLIENT.removeProjectSegmentTask(projectSegmentId, taskId).then((segment) =>
      dispatch(fetchProject(segment.project_id))
    );
  };
}

export function getProjectTaskComments(taskId) {
  return (dispatch, getState) => {
    return PROJECT_CLIENT.getProjectTaskComments(taskId);
  };
}

export function addProjectTaskComment(taskId, comment) {
  return (dispatch, getState) => {
    return PROJECT_CLIENT.addProjectTaskComment(taskId, {
      message: comment
    }).then((comment) => {
      return getTaskProjects(getState(), taskId).map((project) => dispatch(fetchProject(project.id)));
    });
  };
}

export function createTaskVersion(taskId, data) {
  return (dispatch, getState) => {
    return PROJECT_CLIENT.createProjectTaskVersion(taskId, { data }).then((task) => {
      const oTask = tasksReducer.getTask(getState(), taskId) || {};
      dispatch(tasksReducer.updateTask({ ...oTask, ...task }));

      toast(<div>{"Task successfully updated!"}</div>, {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });

      return getTaskProjects(getState(), task.id).map((project) => dispatch(fetchProject(project.id)));
    });
  };
}

export function reassignTask(taskId, assigned_to) {
  return (dispatch, getState) => {
    dispatch(updateTask(taskId, { assigned_to }));
  };
}

export function resetTaskStatus(taskId) {
  return (dispatch, getState) => {
    dispatch(updateTask(taskId, { status: SCENARIO_STATUS.DRAFT }));
  };
}

export function stageTask(taskId) {
  return (dispatch, getState) => {
    dispatch(updateTask(taskId, { status: SCENARIO_STATUS.PENDING }));
  };
}

export function approveTask(taskId) {
  return (dispatch, getState) => {
    dispatch(updateTask(taskId, { status: SCENARIO_STATUS.ACCEPTED }));
  };
}

export function rejectTask(taskId) {
  return (dispatch, getState) => {
    dispatch(updateTask(taskId, { status: SCENARIO_STATUS.REJECTED }));
  };
}

export function deleteTask(taskId) {
  return (dispatch, getState) => {
    const shouldDelete = confirm("Are you sure you want to delete this task?");

    if (shouldDelete) {
      return PROJECT_CLIENT.deleteProjectTask(taskId).then((task) => {
        dispatch(tasksReducer.updateTask(task));
      });
    }
  };
}

export function updateTask(taskId, { assigned_to, status, name, description, deleted }) {
  return (dispatch, getState) => {
    const update = { assigned_to, status, name, description, deleted };

    return PROJECT_CLIENT.updateProjectTask(taskId, update).then((task) => {
      const oTask = tasksReducer.getTask(getState(), taskId) || {};

      dispatch(tasksReducer.updateTask({ ...oTask, ...task }));
      //dispatch(tasksReducer.fetchComments(task?.id));
      getTaskProjects(getState(), task.id).map((project) => dispatch(fetchProject(project.id)));
    });
  };
}

export function updateScenarioWithLatestTaskVersion(scenarioId, taskId) {
  return (dispatch, getState) => {
    const scenario = getProjectScenario(getState(), scenarioId);

    const project = getProject(scenario.project_id);

    const segments = [...(scenario.segments ?? [])];

    for (let { id, tasks } of project.segment) {
      const task = tasks?.find((t) => t.id == taskId);

      if (task !== undefined && task.versions.length > 0) {
        const assumption = { id: task.versions[0].id };

        const segment = segments?.find((s) => s.id == id);

        if (segment === undefined) {
          segments.push({
            id: id,
            assumptions: [assumption]
          });
        } else {
          (segment.assumptions ??= []).push(assumption);
        }
      }
    }

    return PROJECT_CLIENT.updateProjectScenario(scenarioId, {
      segments
    }).then((scenario) => dispatch(refresh(scenario.project_id)));
  };
}

export function loadJobsFromProject(id, skipScenarioForceView = false, skipLoadedScenarioFetch = false) {
  return (dispatch, getState) => {
    const loadedJobs = getLoadedJobsFromOptimizer(getState(), id);

    const jobKeys = getAllJobIdsFromOptimizer(getState(), id).filter(
      (jobId) => loadedJobs.find((job) => job.id === jobId) === undefined
    );

    const wrappedJobs = loadedJobs.map((job) => ({ id: job.id, job: job }));

    for (let key of jobKeys) {
      wrappedJobs.push({ id: key });
    }

    const project = getProject(getState(), id);

    if (project === undefined) {
      const timeoutId = setTimeout(
        () => dispatch(loadJobsFromProject(id, skipScenarioForceView, skipLoadedScenarioFetch)),
        2000
      );
      return Promise.resolve(timeoutId);
    }

    const setDefaultSetupHelper = (k) => {
      const actualSetup = setupReducer.getSetup(getState(), k);
      let retSetup = actualSetup;
      if (!(actualSetup && actualSetup.defaultSetup && actualSetup.defaultSetup.data)) {
        const segment =
          project && project.segments && project.segments.filter((s) => s.job && s.job.job_id === k).length > 0
            ? project.segments.filter((s) => s.job && s.job.job_id === k)[0]
            : undefined;

        if (segment && segment.assumption && Object.keys(segment.assumption).length > 0) {
          retSetup = {
            ...actualSetup,
            ...segment.assumption.data, // This is where the assumption setup overwrites the one saved in the run
            defaultSetup: {
              data: JSON.parse(JSON.stringify(segment.assumption.data))
            },
            jobSetup: { ...actualSetup }
          };
        } else {
          const defaultSetup = JSON.parse(JSON.stringify(actualSetup));
          delete defaultSetup.defaultSetup;

          retSetup = {
            ...actualSetup,
            defaultSetup: { data: defaultSetup }
          };
        }

        //dispatch(setupReducer.updateSetup(k, retSetup));
      }
      return retSetup;
    };

    const jobPromises = wrappedJobs.map((job, i) => {
      if (Object.keys(runReducer.getJob(getState(), job.id)).length === 0) {
        const jobPromise =
          job.job === undefined || (job.job.status !== "ERROR" && job.job.status !== "DONE")
            ? dispatch(runReducer.pollJob(job.id, true))
            : loadJobMetadata(job.job).then((job) => dispatch(runReducer.handleJobResponse(job, true)));

        return jobPromise.then(() => {
          // Uses the segement assumption as the default setup or uses the current setup as it
          let retSetup = setDefaultSetupHelper(job.id);

          if (i === 0 && retSetup && retSetup.linkedProjectId) {
            dispatch(init(retSetup.linkedProjectId, undefined, id, false, retSetup.linkedScenarioId, true));
          }

          dispatch(runReducer.saveFrontier(job.id));

          return Promise.resolve();
        });
      } else {
        setDefaultSetupHelper(job.id);
      }

      return Promise.resolve();
    });

    return Promise.all(jobPromises).then(() =>
      dispatch(loadScenariosFromProject(id, !skipScenarioForceView, skipLoadedScenarioFetch, true))
    );
  };
}

export function openProject(projectId) {
  return (dispatch, getState) => {
    if (projectId) {
      return dispatch(push("/project/" + projectId + "/one"));
    }
  };
}

export function refresh(id) {
  return (dispatch, getState) => {
    dispatch(updateIsRefreshing(id, true));
    return dispatch(fetchProject(id)).then(() => {
      return dispatch(loadScenariosFromProject(id)).then(() => {
        dispatch(updateIsRefreshing(id, false));
        dispatch(checkNotify(id));
        return Promise.resolve();
      });
    });
  };
}

export function checkNotify(id) {
  return (dispatch, getState) => {
    const project = getProject(getState(), id);

    let hasUpdate = false;
    let updateCount = 0;

    const loginDate = new Date(loginReducer.getItem(getState(), "last_project_view"));

    dispatch(loginReducer.storeItem("last_project_view", new Date()));

    project &&
      project.settings &&
      project.settings.originalScenarios &&
      project.settings.originalScenarios.forEach((s) => {
        const updateDate = new Date(moment.utc(s.updated_date).local());

        if (loginDate < updateDate) {
          hasUpdate = true;
          updateCount++;
        }
      });

    if (hasUpdate) {
      dispatch(updateIsNewFrontier(id, true));

      toast(
        "This project has been updated since last time you have viewed it. " +
          updateCount +
          " new scenario(s) have been added.",
        {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        }
      );
    }
  };
}

export function resetDatasetOutcome(id) {
  return (dispatch, getState) => {
    const jobIds = getAllJobIdsFromOptimizer(getState(), id);
    let latestJob = null;
    jobIds.forEach((id) => {
      const currJob = runReducer.getJob(getState(), id);
      if (currJob && currJob.id) {
        if (!latestJob || moment(latestJob.updated_date).isBefore(moment(currJob.updated_date))) {
          latestJob = currJob;
        }
      }
    });
    if (latestJob) {
      const datasetId = latestJob.datasetId;
      const outcome = homeReducer.getOutcome(getState(), datasetId);
      const originalOutcome = homeReducer.getOriginalOutcome(getState(), datasetId);
      if (outcome !== originalOutcome) {
        dispatch(homeReducer.changeOutcome(datasetId, originalOutcome));
      }
    }
  };
}

export function updateDatasetOutcomeToJobOutcome(id) {
  return (dispatch, getState) => {
    const jobIds = getAllJobIdsFromOptimizer(getState(), id);
    let latestJob = null;
    jobIds.forEach((id) => {
      const currJob = runReducer.getJob(getState(), id);
      if (currJob && currJob.id) {
        if (!latestJob || moment(latestJob.updated_date).isBefore(moment(currJob.updated_date))) {
          latestJob = currJob;
        }
      }
    });
    if (latestJob) {
      const outcome = latestJob.outcome;
      const datasetId = latestJob.datasetId;
      if (outcome !== homeReducer.getOutcome(getState(), datasetId)) {
        dispatch(homeReducer.changeOutcome(datasetId, outcome));
      }
    }
  };
}

export function getTargetedFromRun(state, projectId, runId) {
  const project = getProject(state, projectId);

  const activeSegment =
    project && project.segments && project.segments.filter((s) => s.job && s.job.job_id === runId).length > 0
      ? project.segments.filter((s) => s.job && s.job.job_id === runId)[0]
      : undefined;

  if (activeSegment !== undefined) {
    const cm =
      project && project.settings && project.settings.segmentConfusionMatrices
        ? project.settings.segmentConfusionMatrices[activeSegment.id]
        : undefined;

    if (cm !== undefined) {
      return cm.tp + cm.fp;
    }
  }
}

export function setScenario(id, scenarioId) {
  return (dispatch, getState) => {
    let linkedIndex = -1;

    const project = getProject(getState(), id);

    project &&
      project.settings &&
      project.settings.originalScenarios &&
      project.settings.originalScenarios.forEach((s, i) => {
        if (s.id === scenarioId) {
          linkedIndex = i;
        }
      });

    if (linkedIndex !== -1) {
      const linkedScenario = project.settings.scenarios[linkedIndex];

      dispatch(viewScenario(project.id, linkedScenario, linkedIndex));
    } else if (project && project.settings && project.settings.scenarios && project.settings.scenarios.length > 0) {
      dispatch(viewScenario(project.id, project.settings.scenarios[0], 0));
    }
  };
}

export function fetchTaskComments(id) {
  return (dispatch, getState) => {
    const project = getProject(getState(), id);

    if (project?.segments) {
      let tasks = [];
      for (let segment of project?.segments) {
        segment.tasks &&
          segment.tasks.forEach((t) => {
            tasks.push(t);
          });
      }

      tasks.forEach((task) => {
        const rTask = tasksReducer.getTask(getState(), task?.id);

        if (rTask === undefined) {
          dispatch(tasksReducer.updateTask(task));
        }

        dispatch(tasksReducer.fetchComments(task?.id));
      });
    }
  };
}

export function init(
  id,
  aibleViewType = undefined,
  linkedProjectId = undefined,
  isFirstLinkSetup = false,
  linkedScenarioId = undefined,
  skipLoadedScenarioFetch = false
) {
  return (dispatch, getState) => {
    if (unifiedProjectReducer.getIsInit(getState(), id) === true) {
      if (linkedScenarioId !== undefined) {
        dispatch(setScenario(id, linkedScenarioId));
      }
      dispatch(updateDatasetOutcomeToJobOutcome(id));

      return Promise.resolve();
    }

    const projectSettings = getOptimizer(getState(), id);
    if (projectSettings && !projectSettings.isLoading) {
      dispatch(updateIsLoadingProject(id, true));
    }

    dispatch(unifiedProjectReducer.updateIsInit(id, true));

    const project = getProject(getState(), id);

    if (project?.segments) {
      let tasks = [];
      for (let segment in project?.segments) {
        segment.tasks && segment.tasks.forEach((t) => tasks.push(t));
      }
    }

    if (project !== undefined && project.segments && project.segments.length > 0) {
      dispatch(fetchTaskComments(id));

      if (isFirstLinkSetup === true) {
        dispatch(updateFirstLinkSetup(linkedProjectId, id));
      }
      dispatch(resetBudget(id, false, true));
      if (isFirstLinkSetup === true) {
        if (getIsLoadingProject(getState(), id)) {
          dispatch(updateIsLoadingProject(id, false));
        }
        return Promise.resolve();
      }
      return dispatch(loadJobsFromProject(id, linkedScenarioId !== undefined, skipLoadedScenarioFetch)).then(() => {
        dispatch(updateDatasetOutcomeToJobOutcome(id));
        if (linkedProjectId === undefined) {
          dispatch(initMonitoring(id));
          dispatch(globalReducer.updateCurrentProjectId(id));
        } else {
          if (linkedScenarioId !== undefined) {
            dispatch(setScenario(id, linkedScenarioId));
          }
          dispatch(updateSetupFromLinked(linkedProjectId));
        }

        dispatch(resetBudget(id, false, true));

        if (getIsLoadingProject(getState(), id)) {
          dispatch(updateIsLoadingProject(id, false));
        }

        if (linkedProjectId !== undefined) {
          dispatch(loadScenariosFromProject(linkedProjectId, false, false, true));
        }
        return Promise.resolve();
      });
      //dispatch(loadScenariosFromProject(id));
    } else {
      if (linkedProjectId !== undefined) {
        dispatch(updateIsLoadingLink(linkedProjectId, true));
      } else {
        dispatch(globalReducer.updateCurrentProjectId(id));
      }
      return dispatch(fetchProject(id, aibleViewType, true)).then(() => {
        const nProject = getProject(getState(), id);

        dispatch(fetchTaskComments(id));

        if (!isEnabled("aible-consumer") && nProject && nProject.folder_id) {
          const selectedFolder = foldersReducer.getSelectedFolder(getState());
          if (selectedFolder !== nProject.folder_id) {
            dispatch(foldersReducer.selectFolder(nProject.folder_id));
          }
        }
        return dispatch(loadJobsFromProject(id, linkedScenarioId !== undefined)).then(() => {
          dispatch(updateDatasetOutcomeToJobOutcome(id));
          dispatch(initMonitoring(id));
          dispatch(checkNotify(id));

          dispatch(initBudget(id));

          if (linkedProjectId !== undefined) {
            if (linkedScenarioId !== undefined) {
              dispatch(setScenario(id, linkedScenarioId));
            }

            if (isFirstLinkSetup === false) {
              dispatch(updateSetupFromLinked(linkedProjectId));
            } else {
              dispatch(updateFirstLinkSetup(linkedProjectId, id));
            }

            dispatch(updateIsLoadingLink(linkedProjectId, false));
          }

          if (getIsForecastProject(getState(), id)) {
            const runIds = getAllJobIdsFromOptimizer(getState(), id);
            runIds.map((runId) => {
              return dispatch(runReducer.fetchForecast(runId));
            });
          }

          if (getIsLoadingProject(getState(), id)) {
            dispatch(updateIsLoadingProject(id, false));
          }

          if (linkedProjectId !== undefined) {
            dispatch(loadScenariosFromProject(linkedProjectId, false, false, true));
          }
          return Promise.resolve();
        });
        //dispatch(loadScenariosFromProject(id));
      });
    }
  };
}

export function updateMonitorSettings(projectId) {
  return (dispatch, getState) => {
    let projectMonitor = getProjectMonitor(getState(), projectId) || {};

    projectMonitor = {
      ...projectMonitor,
      terminal_conditions: ConditionUtils.removeInvalid(projectMonitor.terminal_conditions),
      activity_conditions: ConditionUtils.removeInvalid(projectMonitor.activity_conditions)
    };

    const promise =
      projectMonitor.id === undefined
        ? ProjectAPI.createProjectMonitor(projectId, projectMonitor)
        : ProjectAPI.updateProjectMonitor(projectMonitor);

    return promise.then((monitor) => {
      dispatch(updateProjectMonitor(projectId, monitor));

      return monitor;
    });
  };
}

function isObjectsEqual(obj1, obj2) {
  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    return obj1 === obj2;
  }

  const keys1 = Object.keys(obj1).filter((currKey) => obj1[currKey] !== undefined);
  const keys2 = Object.keys(obj2).filter((currKey) => obj2[currKey] !== undefined);

  if (keys1.length !== keys2.length) {
    return false;
  }

  if (keys1.length === 0) {
    return true;
  }

  return keys1.every((key) => isObjectsEqual(obj1[key], obj2[key]));
}

function getIsUpdateNeeded(updates, setup) {
  return !Object.keys(updates).every((key) => isObjectsEqual(updates[key], setup[key]));
}

// getUpdateObject is a function that takes in the state, projectId, and a runId
// it returns an object that contains the values for the current segment we want
// to update.
// Also updates the deployed scenario last so it stays deployed.
export function updateProjectAssumptions(projectId, getUpdateObject) {
  return (dispatch, getState) => {
    const segments = getProjectSegments(getState(), projectId);
    const assumptionUpdatePromises = [];
    const deployedAssumptionUpdates = [];

    const deployedScenarioIndex = getDeployedScenarioIndex(getState(), projectId);
    const deployedScenario = getCurrentScenario(getState(), projectId, deployedScenarioIndex);

    let needsUpdate = false;

    const segmentUpdates = segments.map((segment) => {
      const runId = getSegmentRunId(getState(), projectId, segment.id);

      const projectUpdates = getUpdateObject(getState(), projectId, runId, segment.id);

      if (!needsUpdate) {
        needsUpdate = getIsUpdateNeeded(projectUpdates, segment.assumption.data);
      }

      return {
        id: segment.id,
        assumptions: [
          {
            type: "ALL",
            data: {
              ...segment.assumption.data,
              ...projectUpdates
            }
          }
        ]
      };
    });

    if (needsUpdate) {
      assumptionUpdatePromises.push(
        ProjectAPI.patchScenario(deployedScenario.original.id, {
          segments: segmentUpdates
        })
      );
    }

    const currentScenarioIndex = getViewingScenarioIndex(getState(), projectId);

    if (!assumptionUpdatePromises.length && !deployedAssumptionUpdates.length) {
      return Promise.resolve();
    }

    return Promise.all(assumptionUpdatePromises).then(() => {
      const deployedAssumptionPromises = deployedAssumptionUpdates.map((updateFunc) => updateFunc());
      return Promise.all(deployedAssumptionPromises).then(() => {
        return dispatch(fetchProject(projectId)).then(() => {
          return dispatch(loadScenariosFromProject(projectId, false, false, true)).then(() => {
            const currScenario = getSelectedScenario(getState(), projectId, currentScenarioIndex);
            if (currScenario && currScenario.scenario) {
              return dispatch(viewScenario(projectId, currScenario.scenario, currentScenarioIndex));
            }
          });
        });
      });
    });
  };
}

export function updateProjectMonitorSettingsAssumptions(projectId) {
  return (dispatch, getState) => {
    const getOptimizeByUpdates = (state, pId, rId) => {
      const predictionConstraints = setupReducer.getPredictionConstraints(getState(), rId);

      const optimizedByUpdates = {
        optimize_by: setupReducer.getOptimizeBy(getState(), rId),
        optimize_goal: setupReducer.getOptimizeGoal(getState(), rId),
        n_opts: setupReducer.getNOpts(getState(), rId),
        predictionConstraints: predictionConstraints
      };

      return optimizedByUpdates;
    };

    return dispatch(updateProjectAssumptions(projectId, getOptimizeByUpdates));
  };
}

export function updateAssumptionSetup(projectId, assumptionId, setup, skipFetch = false) {
  return (dispatch, getState) => {
    return ProjectAPI.updateAssumption(assumptionId, {
      data: setup
    }).then((response) => {
      if (!skipFetch) {
        return dispatch(fetchProject(projectId));
      } else {
        return Promise.resolve();
      }
    });
  };
}

export function handleUpdateMonitorSettings(projectId) {
  return (dispatch, getState) => {
    const promises = [
      dispatch(updateProjectMonitorSettingsAssumptions(projectId)),
      dispatch(updateMonitorSettings(projectId))
    ];

    return Promise.all(promises).then((responses) => {
      return dispatch(updateProjectMonitor(projectId, responses[1]));
    });
  };
}

export const exportToPowerpoint = (id) => {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_SETTINGS,
      key: id,
      subKey: "powerpointExporterIsRunning",
      value: true
    });
    dispatch(loadScenariosFromProject(id, false, true)).then(() => {
      try {
        dispatch(generatePowerpoint(id));
      } catch (error) {
        console.log(error);

        dispatch({
          type: UPDATE_SETTINGS,
          key: id,
          subKey: "powerpointExporterIsRunning",
          value: false
        });

        toast("Something went wrong with the exporter. Please try again later or contact Aible support.", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
        throw error;
      }

      dispatch({
        type: UPDATE_SETTINGS,
        key: id,
        subKey: "powerpointExporterIsRunning",
        value: false
      });
    });
  };
};

const generatePowerpoint = (id) => {
  return (dispatch, getState) => {
    let pres = new PptxGenJS();
    const modelGroups = getModelGroups(getState(), id);
    let optimized = getOptimized(getState(), id);

    let runId =
      optimized && optimized.innerId !== undefined ? optimized.innerId : isEditBlueprint ? "editBlueprint" : id;

    if (modelGroups?.drilldownValues && modelGroups.drilldownValues.length > 1) {
      dispatch(updateDrilldownValue(id, modelGroups?.drilldownValues?.[0]?.runId, "Overall"));

      optimized = getOptimized(getState(), id);
      runId = modelGroups?.drilldownValues?.[0]?.runId;
    }

    const isContinuous = getIsContinuous(getState(), id);

    const setup = setupReducer.getSetup(getState(), runId);
    const dataset = optimized && optimized.dataset;
    const datasetWarnings = dataset && dataset.warnings;
    const groupedUpdates = parseValidation(datasetWarnings);
    const project = getProject(getState(), id);
    const correlations = setupReducer.getCorrelationMetricsFromDataset(getState(), runId) || {};

    const renderConstraintText = (constraint, unitType) => {
      const direction = constraint.primary.direction === "max" ? "no more than " : "at least ";
      const value =
        constraint.primary.mode === "percent"
          ? Math.round(constraint.primary.value * 100) + "%"
          : unitType + numeral(constraint.primary.value).format("0.0a").toUpperCase();
      const convertedValue =
        constraint.primary.mode === "percent" ? ` (${unitType + constraint.primary.convertedValue})` : "";
      const message = constraint.primary.typeLabel + " of " + direction + value + convertedValue;
      return message;
    };

    function handlePlurals(count, value = "", noPlural = false) {
      return value + (noPlural || count === 1 ? "" : "s");
    }

    function checkMessage(message, checkArr) {
      for (let i = 0; i < checkArr.length; i++) {
        if (!message.toLowerCase().includes(checkArr[i].toLowerCase())) {
          return false;
        }
      }
      return true;
    }

    function parseValidation(updatedColumns) {
      const VALIDATION_MESSAGES = [
        {
          message: ["too many values", "It would be discarded."],
          type: "high cardinality field",
          action: "were ignored"
        },
        {
          message: ["It would be discarded."],
          type: "column",
          action: "were ignored"
        },
        {
          message: ["missing values", "(", ")"],
          type: "column",
          count: 0,
          action: "with missing values were updated"
        },
        {
          message: ["too many values", "most informative values", "flagged and included in the modeling step"],
          type: "high cardinality field",
          action: "were updated"
        }
      ];
      const retValidation = VALIDATION_MESSAGES.map((validation) => ({
        ...validation,
        value: []
      }));
      const extra = {
        type: "column",
        action: "were updated for other reasons",
        value: []
      };
      updatedColumns.forEach((col) => {
        let found = false;
        const message = col.metadata && col.metadata.message ? col.metadata.message : col.message;

        for (let i = 0; i < retValidation.length; i++) {
          if (!found && checkMessage(message, retValidation[i].message)) {
            retValidation[i].value.push(message);
            found = true;
            if (retValidation) break;
          }
        }
        if (!found) {
          extra.value.push(message);
        }
      });
      if (retValidation.length < 1) {
        extra.action = "would be updated";
      }
      retValidation.push(extra);
      return retValidation.filter((elem) => elem.value.length);
    }

    //end startup stuff
    //end startup stuff
    //end startup stuff

    if (setup) {
      let titleSlide = pres.addSlide();
      titleSlide.background = { color: "512f77" };
      titleSlide.addImage({
        path: aibleHelix,
        x: 0,
        y: 1.09,
        w: 10,
        h: 4.5,
        sizing: { type: "contain" }
      });
      titleSlide.addImage({
        path: aibleTransparentLogo,
        x: 0.9,
        y: 0.92,
        w: 1.87,
        h: 0.41,
        sizing: { type: "contain" }
      });
      titleSlide.addText("AI for Business Impact", {
        color: "FFFFFF",
        x: 0.78,
        y: 1.6
      });

      let blueprintSlide = pres.addSlide();
      blueprintSlide.addText("AI Opportunity Evaluation: Executive Summary", {
        x: "0%",
        y: "0%",
        w: "100%",
        h: "15%",
        color: "FFFFFF",
        fill: { color: "512f77" },
        fontSize: 24,
        fontFace: "Raleway"
      });
      const summaryText = [
        {
          text: "Project Focus",
          options: {
            bold: false,
            breakLine: true,
            fontSize: 16,
            color: "512F77",
            paraSpaceAfter: 5
          }
        },
        { text: "Optimize your ", options: { bullet: true } },
        { text: setup.outcomeLabel, options: { bold: true } },
        { text: " percentage by recommending which " },
        { text: setup.transactionLabel, options: { bold: true } },
        { text: " to " },
        { text: setup.trueAction, options: { bold: true } },
        { text: isContinuous ? "" : " from the list of available " },
        {
          text: isContinuous ? "." : setup.notPursuedTransactionLabel + ".",
          options: { bold: true }
        },
        { text: " Also analyze how to allocate your " },
        {
          text: setup.advancedCostLabel ? setup.advancedCostLabel : "Cost",
          options: { bold: true }
        },
        { text: " in a way that optimizes " },
        {
          text: setup.advancedRevenueLabel ? setup.advancedRevenueLabel : "Revenue",
          options: { bold: true }
        },
        { text: " and " },
        {
          text: setup.netImpactLabel ? setup.netImpactLabel : "Impact",
          options: { bold: true, breakLine: true }
        }
      ];
      const modelGroupSegments = modelGroups && modelGroups.drilldownValues;
      if (modelGroupSegments && modelGroupSegments.length > 1) {
        let subgroupList;
        modelGroups.drilldownValues
          .filter((group) => group.label !== "Overall")
          .forEach((group) => {
            if (subgroupList) {
              subgroupList = subgroupList + ", " + group.label;
            } else {
              subgroupList = group.label;
            }
          });
        const subgroupLineArray = [
          {
            text: "Separate analyses were conducted for the subgroups of ",
            options: { bullet: true }
          },
          { text: setup.splitColumn + ": ", options: { bold: true } },
          {
            text: subgroupList,
            options: { bold: true, breakLine: true, paraSpaceAfter: 10 }
          }
        ];
        summaryText.push(...subgroupLineArray);
      }
      //line for impact info
      const determinedTimePeriod =
        setup && setup.timePeriod && setup.timePeriod === "Month"
          ? "Monthly"
          : setup.timePeriod === "Day"
            ? "Daily"
            : setup.timePeriod === "Week"
              ? "Weekly"
              : setup.timePeriod === "Quarter"
                ? "Quarterly"
                : setup.timePeriod === "Year"
                  ? "Yearly"
                  : undefined;
      const timePeriod = determinedTimePeriod ? " on a " + determinedTimePeriod + " basis" : "";
      const impactLine = [
        {
          text: "Value Identified",
          options: {
            bold: false,
            breakLine: true,
            fontSize: 16,
            color: "512F77",
            paraSpaceAfter: 5
          }
        },
        { text: "Aible identified ", options: { bullet: true } },
        {
          text: isContinuous
            ? `an Optimized Error Cost of $${numeral(optimized.overEstimate + optimized.underEstimate).format("0.0a")}`
            : "$" +
              numeral(optimized.profitDelta).format("0.0a") +
              ` of total impact value${setup.timePeriod ? timePeriod : ""}`,
          options: { bold: true }
        },
        {
          text: " (assuming complete implementation and appropriate recommendation adoption) compared to the baseline period",
          options: { breakLine: true }
        }
      ];
      summaryText.push(...impactLine);

      //line strategy
      const allBusinessConstraints = setupReducer.getAllBusinessConstraints(getState(), id);

      if (allBusinessConstraints && allBusinessConstraints.length > 0) {
        const overallConstraint = allBusinessConstraints.find((segment) => segment.subgroup === "Overall");
        const segmentConstraints = allBusinessConstraints.filter(
          (segment) => segment.subgroup !== "Overall" && segment.primary !== undefined
        );
        const unitType = setup.unitType ? setup.unitType : "$";
        const profitLabel = setup.netImpactLabel ? setup.netImpactLabel : "Impact";
        const overallGoal =
          overallConstraint && (overallConstraint.primary === undefined || overallConstraint.primary.type === "profit")
            ? "The primary goal is to maximize " + profitLabel
            : "The primary goal is to reach " + renderConstraintText(overallConstraint, unitType);

        const strategyLine = [
          {
            text: overallGoal,
            options: { bullet: true, breakLine: true, paraSpaceAfter: 10 }
          }
        ];
        summaryText.push(...strategyLine);
      }

      if (dataset) {
        const datasetInfoLine = [
          {
            text: "AI Modeling",
            options: {
              breakLine: true,
              bold: false,
              fontSize: 16,
              color: "512F77",
              paraSpaceAfter: 5
            }
          },
          {
            text:
              "Dataset Used: " +
              optimized.dataset.label +
              " [# Rows: " +
              dataset.rowCount +
              ", # Columns: " +
              Object.keys(dataset.fields).length +
              "]",
            options: { bullet: true, breakLine: true }
          }
        ];
        summaryText.push(...datasetInfoLine);
      }

      //num models line
      let numDriverVariables;
      let numModels;
      let multipleSegmentsText;

      const numModelsLine = [
        { text: "300,000+", options: { bullet: true, bold: true } },
        { text: " model settings were evaluated and " }
      ];

      if (modelGroupSegments && modelGroupSegments.length > 1) {
        const segments = modelGroupSegments.filter((group) => group.label !== "Overall" && group.bestModel);

        numDriverVariables = segments.reduce((acc, seg) => {
          let currNum = seg?.bestModel?.featureInfo?.length ?? 0;
          return Math.max(currNum, acc);
        }, 0);
        // numDriverVariables = segments[0].bestModel.featureInfo.length;
        //num models section

        multipleSegmentsText = [
          { text: "across " },
          { text: segments.length + " ", options: { bold: true } },
          { text: "subgroups " }
        ];
      } else {
        numDriverVariables = modelGroupSegments[0].bestModel?.featureInfo?.length ?? 0;
        //numModels = modelGroupSegments[0].numModels;
      }
      numModels = getTotalModelCount(getState(), id);
      numModelsLine.push({ text: numModels, options: { bold: true } }, { text: " individual models " });
      if (multipleSegmentsText) {
        numModelsLine.push(...multipleSegmentsText);
      }
      summaryText.push(...numModelsLine);

      const driverVariablesLine = [
        {
          text: "were selected allowing for dynamic and instantaneous scenario planning at the subgroup level (assuming full implementation).",
          options: { breakLine: true }
        },
        {
          text: `${numDriverVariables} `,
          options: { bullet: true, bold: true }
        },
        {
          text: "unique model driver variables were ordered by significance and analyzed at the attribute level to determine which traits were more likely to increase or decrease"
        },
        {
          text: isContinuous
            ? setup.netImpactLabel
              ? " " + setup.netImpactLabel
              : " " + "Impact"
            : ` ${setup.outcomeLabel} percentage.`,
          options: { bold: true }
        }
      ];

      summaryText.push(...driverVariablesLine);
      blueprintSlide.addText(summaryText, {
        x: 0.2,
        y: 1,
        w: 9.5,
        h: 4,
        color: "363636",
        fontSize: 12
      });
      blueprintSlide.addImage({
        path: Logo,
        x: "5%",
        y: "90%",
        w: 1,
        h: 0.5,
        sizing: { type: "contain" }
      });
      //end blueprint page

      //ai check slide
      //ai check slide
      //ai check slide
      //
      //
      let validationSlide = pres.addSlide();
      validationSlide.addText(`Data Validation Results`, {
        x: "0%",
        y: "0%",
        w: "100%",
        h: "15%",
        color: "FFFFFF",
        fill: { color: "512f77" },
        fontSize: 24,
        fontFace: "Raleway"
      });

      let numTotalErrors = 0;
      const errorsList = [];
      if (groupedUpdates && groupedUpdates.length > 0) {
        groupedUpdates.forEach((update) => {
          const numErrors = update.value.length;
          numTotalErrors += numErrors;

          const message = `${numErrors} ${handlePlurals(
            update.value.length,
            update.type,
            update.noPlural
          )} ${update.action}`;
          const warningMessage = {
            text: message,
            options: {
              breakLine: true,
              bullet: { characterCode: "2713" }
            }
          };
          errorsList.push(warningMessage);
        });
      }

      const validationHeader = {
        text: `Your data passed all validation checks after ${numTotalErrors} common errors were automatically updated by Aible`,
        options: {
          breakLine: true,
          bold: true,
          bullet: { characterCode: "2713" }
        }
      };

      validationSlide.addText([validationHeader, ...errorsList], {
        x: 0.17,
        y: 1.13,
        w: 9.6,
        h: "20%",
        color: "000000",
        fill: { color: "b9faa5" },
        fontSize: 14,
        line: { width: "0", color: "A9A9A9" }
      });
      validationSlide.addText(
        [
          {
            text: "Dataset Details",
            options: {
              bold: false,
              breakLine: true,
              fontSize: 16,
              color: "512F77",
              paraSpaceAfter: 5
            }
          },
          {
            text: "Name: " + optimized.dataset.label,
            options: {
              breakLine: true,
              bullet: { indent: 15 },
              indentLevel: 1
            }
          },
          {
            text: "# of Records: " + dataset.rowCount,
            options: {
              breakLine: true,
              bullet: { indent: 15 },
              indentLevel: 1
            }
          },
          {
            text: "# of Variables: " + Object.keys(dataset.fields).length,
            options: {
              breakLine: true,
              bullet: { indent: 15 },
              indentLevel: 1
            }
          },
          // {
          //   text: "Each Record represents a historical transaction",
          //   options: { breakLine: true, bullet: true },
          // },
          {
            text: "Filters Applied: None required",
            options: {
              breakLine: true,
              bullet: { indent: 15 },
              indentLevel: 1
            }
          }
        ],
        {
          x: "5%",
          y: "45%",
          w: "30%",
          h: "40%",
          color: "000000",
          fontSize: 14
        }
      );
      let arrTabRows1 = [
        [
          {
            text: "Data Validation Checks",
            options: { fill: { color: "512f77" }, color: "ffffff" }
          },
          {
            text: "Status",
            options: { fill: { color: "512f77" }, color: "ffffff" }
          }
        ],
        [
          {
            text: "Outlier Detection",
            options: { color: "000000", fill: { color: "c7bbd3" } }
          },
          {
            text: "Passed",
            options: {
              bullet: { characterCode: "2713" },
              color: "333333",
              fill: { color: "E6E6FC" }
            }
          }
        ],
        [
          {
            text: "Missing Values",
            options: { color: "000000", fill: { color: "c7bbd3" } }
          },
          {
            text: "Passed",
            options: {
              bullet: { characterCode: "2713" },
              color: "333333",
              fill: { color: "E6E6FC" }
            }
          }
        ],
        [
          {
            text: "Rare Values",
            options: { color: "000000", fill: { color: "c7bbd3" } }
          },
          {
            text: "Passed",
            options: {
              bullet: { characterCode: "2713" },
              color: "333333",
              fill: { color: "E6E6FC" }
            }
          }
        ],
        [
          {
            text: "Unique Values",
            options: { color: "000000", fill: { color: "c7bbd3" } }
          },
          {
            text: "Passed",
            options: {
              bullet: { characterCode: "2713" },
              color: "333333",
              fill: { color: "E6E6FC" }
            }
          }
        ],
        [
          {
            text: "Duplicate Headers",
            options: { color: "000000", fill: { color: "c7bbd3" } }
          },
          {
            text: "Passed",
            options: {
              bullet: { characterCode: "2713" },
              color: "333333",
              fill: { color: "E6E6FC" }
            }
          }
        ],
        [
          {
            text: "Empty Rows",
            options: { color: "000000", fill: { color: "c7bbd3" } }
          },
          {
            text: "Passed",
            options: {
              bullet: { characterCode: "2713" },
              color: "333333",
              fill: { color: "E6E6FC" }
            }
          }
        ],
        [
          {
            text: "Extra Values",
            options: { color: "000000", fill: { color: "c7bbd3" } }
          },
          {
            text: "Passed",
            options: {
              bullet: { characterCode: "2713" },
              color: "333333",
              fill: { color: "E6E6FC" }
            }
          }
        ]
      ];
      validationSlide.addTable(arrTabRows1, {
        x: 3.81,
        y: 2.46,
        w: 5.7,
        h: 2.23,
        border: { pt: "1", color: "BBCCDD" },
        fontSize: 14,
        color: "363636"
      });
    }
    //ai readiness
    //ai readiness
    //ai readiness
    let aiReadinessSlide = pres.addSlide();
    aiReadinessSlide.addText(`AI Readiness Validation Results`, {
      x: "0%",
      y: "0%",
      w: "100%",
      h: "15%",
      color: "FFFFFF",
      fill: { color: "512f77" },
      fontSize: 24,
      fontFace: "Raleway"
    });
    const success = setup && setup.success ? "is " + setup.success.label : "";
    const readinessHeader = [
      {
        text: `Outcome to Predict: ${optimized.dataset.outcome} ${success}`,
        options: {
          breakLine: true,
          bold: true,
          bullet: { characterCode: "2713" }
        }
      },
      {
        text: `Aible scanned the dataset for AI readiness and found that your outcome is appropriate for AutoML modeling`,
        options: {
          breakLine: true,
          bullet: { characterCode: "2713" }
        }
      }
    ];
    const topPredictors =
      correlations &&
      correlations.predictors &&
      correlations.predictors.length > 0 &&
      correlations.predictors.slice(0, 7).map((predictor) => ({
        text: predictor.label.replace("__c", ""),
        options: { breakLine: true, bullet: { indent: 15 }, indentLevel: 1 }
      }));

    const numPredictors =
      correlations && correlations.predictors && correlations.predictors.length > 0 && correlations.predictors.length;

    const topPredictorsText = [
      {
        text: `Aible found ${numPredictors} variables that are predictive of your outcome`,
        options: {
          breakLine: true,
          fontFace: "Calibri",
          fontSize: 16,
          color: "512f77"
        }
      },
      ...(topPredictors !== undefined && topPredictors !== false ? topPredictors : [])
    ];

    if (numPredictors > 7) {
      topPredictorsText.push({
        text: "...",
        options: { bullet: { indent: 15 }, indentLevel: 1 }
      });
    }
    if (topPredictors && topPredictors?.length > 0) {
      aiReadinessSlide.addText(topPredictorsText, {
        x: 0.4,
        y: 2.58,
        w: 3.85,
        h: 2.25,
        color: "000000",
        fontSize: 14
      });
    }

    aiReadinessSlide.addText(readinessHeader, {
      x: 0.17,
      y: 1.13,
      w: 9.6,
      h: "15%",
      color: "000000",
      fill: { color: "b9faa5" },
      fontSize: 14,
      line: { width: "0", color: "A9A9A9" }
    });

    const validationChecksTable = [
      [
        {
          text: "Data Validation Checks",
          options: { fill: { color: "512f77" }, color: "ffffff" }
        },
        {
          text: "User Selections",
          options: { fill: { color: "512f77" }, color: "ffffff" }
        }
      ]
    ];

    if (groupedUpdates && groupedUpdates.length > 0) {
      let predictors = "";
      groupedUpdates.forEach((update) => {
        const message = `${update.value.length} ${update.action}`;
        if (predictors === "") {
          predictors = message;
        } else {
          predictors = predictors + ", " + message;
        }
      });
      validationChecksTable.push([
        {
          text: "Problematic Predictors",
          options: { color: "000000", fill: { color: "c7bbd3" } }
        },
        {
          text: predictors,
          options: { color: "333333", fill: { color: "E6E6FC" } }
        }
      ]);
    }

    const ignored = setupReducer.getIgnored(getState(), runId, undefined, true, true);
    if (ignored && Object.keys(ignored).length > 0) {
      const numManuallyIgnored = Object.values(ignored).filter((value) => value === true).length;
      validationChecksTable.push([
        {
          text: "Manually Excluded Columns",
          options: { color: "000000", fill: { color: "c7bbd3" } }
        },
        {
          text: numManuallyIgnored,
          options: { color: "333333", fill: { color: "E6E6FC" } }
        }
      ]);
    } else {
      validationChecksTable.push([
        {
          text: "Manually Excluded Columns",
          options: { color: "000000", fill: { color: "c7bbd3" } }
        },
        { text: "0", options: { color: "333333", fill: { color: "E6E6FC" } } }
      ]);
    }

    let calculatedFields = "None";
    const transformations = transformReducer.getTransformArr(getState(), runId);
    if (transformations && transformations.length > 0) {
      calculatedFields = transformations.length;
    }
    validationChecksTable.push([
      {
        text: "Custom Calculated Field",
        options: { color: "000000", fill: { color: "c7bbd3" } }
      },
      {
        text: `${calculatedFields} Required`,
        options: { color: "333333", fill: { color: "E6E6FC" } }
      }
    ]);

    if (modelGroups && modelGroups.drilldownValues && modelGroups.drilldownValues.length > 1) {
      const segments = modelGroups.drilldownValues.filter((group) => group.value !== "Overall").length;
      const row = [
        {
          text: "Subgroup Evaluation",
          options: { color: "000000", fill: { color: "c7bbd3" } }
        },
        {
          text: `Aible split the analysis into ${segments} subgroups based on ${setup.splitColumn}`,
          options: { color: "333333", fill: { color: "E6E6FC" } }
        }
      ];
      validationChecksTable.push(row);
    }

    aiReadinessSlide.addTable(validationChecksTable, {
      x: 4.02,
      y: 2.49,
      h: 2.42,
      w: 5.75,
      border: { pt: "1", color: "BBCCDD" },
      fontSize: 12,
      color: "363636",
      colW: [2, 3.75]
    });
    //strategy slides
    //
    //
    const generateStrategySlide = (scenarioStats, originalScenario, scenarioNum) => {
      let cText = undefined;
      if (false && scenarioStats && scenarioStats.businessConstraints && scenarioStats.businessConstraints.length > 0) {
        let behaviorExpectationsSlide = pres.addSlide();
        behaviorExpectationsSlide.addText(`Scenario ${scenarioNum}: ${originalScenario.name} Expectations`, {
          x: "0%",
          y: "0%",
          w: "100%",
          h: "15%",
          color: "FFFFFF",
          fill: { color: "512f77" },
          fontSize: 18
        });
        const unitType = setup.unitType ? setup.unitType : "$";
        const profitLabel = setup.netImpactLabel ? setup.netImpactLabel : "Impact";
        const constraints = scenarioStats.businessConstraints;
        const overallConstraint = constraints.find((segment) => segment.subgroup === "Overall");
        const segmentConstraints = constraints.filter(
          (segment) => segment.subgroup !== "Overall" && segment.primary !== undefined
        );
        const constraintsText = [
          {
            text: "Your Overall Strategy",
            options: { breakLine: true, bold: true }
          }
        ];
        const overallGoal =
          overallConstraint && (overallConstraint.primary === undefined || overallConstraint.primary.type === "profit")
            ? "The primary goal is to maximize " + profitLabel
            : "The primary goal is to reach " + renderConstraintText(overallConstraint, unitType);
        const additionalSegmentText = segmentConstraints.length > 0 ? " while maintaining these specific goals:" : "";
        constraintsText.push({
          text: overallGoal,
          options: { breakLine: true }
        });
        segmentConstraints.forEach((segment) => {
          const segmentObjective =
            segment.primary === undefined || segment.primary.type === "profit"
              ? "The primary goal is to maximize " + profitLabel
              : "The primary goal is to reach " + renderConstraintText(segment, unitType);

          constraintsText.push({
            text: segment.subgroup + ": " + segmentObjective,
            options: { breakLine: true, bullet: true }
          });
        });

        behaviorExpectationsSlide.addText(constraintsText, {
          x: 1.0,
          y: 2.0,
          fontSize: 16
        });
      }

      let strategySlide = pres.addSlide();
      strategySlide.addText(`Scenario ${scenarioNum}: ${originalScenario.name} Overview`, {
        x: "0%",
        y: "0%",
        w: "100%",
        h: "15%",
        color: "FFFFFF",
        fill: { color: "512f77" },
        fontSize: 24,
        fontFace: "Raleway"
      });

      const topDrivers = scenarioStats.topDrivers;

      const renderProfitTriangle = (value) => {
        return value > 0
          ? {
              text: "▲",
              options: { valign: "center", color: "a3e635", fontSize: 12 }
            }
          : {
              text: "▼",
              options: { valign: "center", color: "ff3232", fontSize: 12 }
            };
      };

      const profitSection = isContinuous
        ? [
            {
              text: "UNDERESTIMATE",
              options: {
                breakLine: true,
                fontSize: 12,
                color: "999999",
                lineSpacing: 0
              }
            },
            {
              text: numeral(scenarioStats.underEstimate).format("0.0a"),
              options: {
                breakLine: true,
                fontSize: 26,
                color: "512f77",
                bold: true,
                paraSpaceAfter: 7
              }
            }
          ]
        : [
            renderProfitTriangle(optimized.profitDelta),
            {
              text: setup?.netImpactLabel || "PROFIT",
              options: {
                breakLine: true,
                fontSize: 12,
                color: "999999",
                lineSpacing: 0
              }
            },
            {
              text: "$" + numeral(optimized.profitDelta).format("0.0a").toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 26,
                color: "512f77",
                bold: true,
                paraSpaceAfter: 7
              }
            },
            {
              text: "Current State: $",
              options: { fontSize: 12 }
            },
            {
              text: numeral(optimized.currentProfit).format("0.0a").toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 12,
                bold: true
              }
            },
            {
              text: "AIBLE Optimized: $",
              options: { fontSize: 12 }
            },
            {
              text: numeral(optimized.profit).format("0.0a").toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 12,
                bold: true
              }
            }
          ];
      strategySlide.addText(profitSection, {
        x: 1.6,
        y: 0.7,
        w: 2.4,
        h: 1.6,
        fontFace: "Calibri",
        fontSize: 14
      });

      const revenueSection = isContinuous
        ? [
            {
              text: "OVERESTIMATE",
              options: {
                breakLine: true,
                fontSize: 12,
                color: "999999",
                lineSpacing: 0
              }
            },
            {
              text: numeral(scenarioStats.overEstimate).format("0.0a"),
              options: {
                breakLine: true,
                fontSize: 26,
                color: "512f77",
                bold: true,
                paraSpaceAfter: 7
              }
            }
          ]
        : [
            renderProfitTriangle(scenarioStats.revenueDelta),
            {
              text: setup?.advancedRevenueLabel || "REVENUE",
              options: { breakLine: true, fontSize: 12, color: "999999" }
            },
            {
              text: numeral(optimized.revenueDelta).format("0.0a").toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 26,
                color: "512f77",
                bold: true,
                paraSpaceAfter: 7,
                lineSpacing: 0
              }
            },
            {
              text: "Current State: $",
              options: { fontSize: 12 }
            },
            {
              text: numeral(optimized.currentRevenue).format("0.0a").toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 12,
                bold: true
              }
            },
            {
              text: "AIBLE Optimized: $",
              options: { fontSize: 12 }
            },
            {
              text: numeral(optimized.revenue).format("0.0a").toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 12,
                bold: true
              }
            }
          ];
      strategySlide.addText(revenueSection, {
        x: 4.2,
        y: 0.7,
        w: 2.4,
        h: 1.6,
        fontFace: "Calibri",
        fontSize: 14
      });

      const costSection = isContinuous
        ? [
            {
              text: "ERROR COST",
              options: { breakLine: true, fontSize: 12, color: "999999" }
            },
            {
              text: numeral(scenarioStats.overEstimate + scenarioStats.underEstimate)
                .format("0.0a")
                .toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 26,
                color: "512f77",
                bold: true,
                paraSpaceAfter: 7
              }
            }
          ]
        : [
            renderProfitTriangle(scenarioStats.cost - scenarioStats.currentSpend),
            {
              text: setup?.advancedCostLabel || "COST",
              options: { breakLine: true, fontSize: 12, color: "999999" }
            },
            {
              text: numeral(optimized.totalCost - optimized.currentSpend)
                .format("0.0a")
                .toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 26,
                color: "512f77",
                bold: true,
                paraSpaceAfter: 7
              }
            },
            {
              text: "Current State: $",
              options: { fontSize: 12 }
            },
            {
              text: numeral(optimized.currentSpend).format("0.0a").toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 12,
                bold: true
              }
            },
            {
              text: "AIBLE Optimized: $",
              options: { fontSize: 12 }
            },
            {
              text: numeral(optimized.totalCost).format("0.0a").toUpperCase(),
              options: {
                breakLine: true,
                fontSize: 12,
                bold: true
              }
            }
          ];
      strategySlide.addText(costSection, {
        x: 6.6,
        y: 0.7,
        w: 2.4,
        h: 1.6,
        fontFace: "Calibri",
        fontSize: 14
      });

      let strategyGoalLine = undefined;

      //line strategy
      const allBusinessConstraints = setupReducer.getAllBusinessConstraints(getState(), id);

      if (allBusinessConstraints && allBusinessConstraints.length > 0) {
        const overallConstraint = allBusinessConstraints.find((segment) => segment.subgroup === "Overall");
        const segmentConstraints = allBusinessConstraints.filter(
          (segment) => segment.subgroup !== "Overall" && segment.primary !== undefined
        );
        const unitType = setup.unitType ? setup.unitType : "$";
        const profitLabel = setup.netImpactLabel ? setup.netImpactLabel : "Impact";
        const overallGoal =
          overallConstraint && (overallConstraint.primary === undefined || overallConstraint.primary.type === "profit")
            ? "The primary goal is to maximize " + profitLabel
            : "The primary goal is to reach " + renderConstraintText(overallConstraint, unitType);

        const strategyLine = [
          {
            text: overallGoal,
            options: { bullet: true, breakLine: true, paraSpaceAfter: 4 }
          }
        ];

        strategyGoalLine = strategyLine;
      }

      const oSetup = setupReducer.getSetup(getState(), topDrivers?.[0]?.runId);

      const fairnessText =
        oSetup?.fairnessObjective === "None"
          ? undefined
          : "I would like to ensure all subgroups have " +
            oSetup?.fairnessObjective +
            " (Target Max Difference: " +
            oSetup?.fairnessToleranceBound +
            "%)";

      const fairnessLine =
        fairnessText === undefined
          ? undefined
          : [
              {
                text: fairnessText,
                options: { bullet: true, breakLine: true, paraSpaceAfter: 4 }
              }
            ];

      strategyGoalLine !== undefined &&
        strategySlide.addText(strategyGoalLine, {
          x: 1.6,
          y: fairnessLine === undefined ? 1.4 : 1.3,
          w: 15,
          h: 2,
          color: "363636",
          fontSize: 12
        });

      fairnessLine !== undefined &&
        strategySlide.addText(fairnessLine, {
          x: 1.6,
          y: 1.6,
          w: 15,
          h: 2,
          color: "363636",
          fontSize: 12
        });

      //subgroup summary
      if (scenarioStats.modelGroupSegments.length > 1) {
        const resourceAllocationSection = [
          {
            text: "Resource Allocation",
            options: {
              breakLine: true,
              fontFace: "Raleway",
              color: "512f77",
              fontSize: 16
            }
          },
          {
            text: "Based on the business segmentation, Aible recommends the following overall customer base mix",
            options: { fontSize: 10.5, fontFace: "Calibri" }
          }
        ];
        strategySlide.addText(resourceAllocationSection, {
          x: isContinuous ? 3 : 0.5,
          y: 2.9,
          w: 4.5,
          h: 0.8
        });
        if (!isContinuous) {
          const opportunitiesSection = [
            {
              text: "Total Opportunities To Pursue",
              options: {
                breakLine: true,
                fontFace: "Raleway",
                color: "512f77",
                fontSize: 16
              }
            },
            {
              text: "The Aible Strategy suggests the following allocations for each segment",
              options: { fontSize: 10.5, fontFace: "Calibri" }
            }
          ];

          strategySlide.addText(opportunitiesSection, {
            x: 5,
            y: 2.9,
            w: 5,
            h: 0.6
          });
        }
        const pieData = scenarioStats.modelGroupSegments
          ? scenarioStats.modelGroupSegments
              .filter((segment) => segment.label !== "Overall")
              .map((s) => ({
                name: s.value,
                runId: s.runId,
                value: isContinuous
                  ? setupReducer.getBaselineCountContinuous(getState(), s.runId)
                  : Math.round(s.optimalPct),
                color: s.color,
                capValue: isContinuous ? setupReducer.getBaselineValueContinuous(getState(), s.runId) : s.capValue
              }))
          : [];

        const formattedPieData = [
          {
            name: "Resource Allocation",
            labels: pieData.map((slice) => slice.name),
            values: pieData.map((slice) => slice.value)
          }
        ];
        const pieColors = pieData.map((slice) => slice.color.replace("#", ""));

        strategySlide.addChart(pres.charts.DOUGHNUT, formattedPieData, {
          x: isContinuous ? 3 : 0.5,
          y: 3.42,
          w: 4,
          h: 2.18,
          legendPos: "left",
          showLegend: true,
          showLeaderLines: true,
          showPercent: true,
          chartColors: pieColors,
          dataLabelColor: "000000",
          dataLabelFontSize: 14,
          dataLabelposition: "bestFit",
          legendFontFace: "Calibri",
          titleFontFace: "Calibri"
        });

        const formattedBarData = [
          {
            name: "Opportunities",
            labels: pieData.map((slice) => slice.name),
            values: pieData.map((slice) => Math.round(slice.capValue))
          }
        ];

        if (!isContinuous) {
          strategySlide.addChart(pres.charts.BAR, formattedBarData, {
            x: 5.0,
            y: 3.4,
            w: 4,
            h: 2.1,
            valAxisMajorTickMark: "cross",
            valAxisMinorTickMark: "out",
            catAxisLabelFontFace: "Calibri",
            valAxisLabelFontFace: "Calibri",
            barDir: "bar",
            chartColors: pieColors
          });
        }

        strategySlide.addShape(pres.ShapeType.line, {
          x: 1,
          y: 2.9,
          w: 8,
          h: 0,
          line: { color: "999999", width: 0.5 }
        });
      }

      //drivers
      let driversSlide = pres.addSlide();
      driversSlide.addText(`Scenario ${scenarioNum}: ${originalScenario.name} - Top Drivers`, {
        x: "0%",
        y: "0%",
        w: "100%",
        h: "15%",
        color: "FFFFFF",
        fill: { color: "512f77" },
        fontSize: 24,
        fontFace: "Raleway"
      });

      const headerRow = [];
      const driversTableDataRows = [];
      if (topDrivers && topDrivers.length > 0) {
        topDrivers
          .filter((td) => td?.drilldownValue !== "Overall" && td.bestModel)
          .forEach((subgroup, subgroupIndex) => {
            const subgroupHeader = {
              text: [
                {
                  text: subgroup.drilldownValue,
                  options: { breakLine: true, color: "333333", bold: true }
                },
                {
                  text: `Optimal Model: ${subgroup.bestModel.label}`,
                  options: { color: "333333", bold: true }
                }
              ],
              options: { fill: subgroup.color.replace("#", "") }
            };
            headerRow.push(subgroupHeader);
            subgroup.columnDrivers.slice(0, 10).forEach((row, index) => {
              const data = {
                text: `${index + 1}.   ${row.field.replace("__c", "")}`,
                options: {
                  breakLine: true,
                  fontSize: 12,
                  color: "333333",
                  fill: subgroup.color.replace("#", "")
                }
              };
              if (subgroupIndex === 0) {
                driversTableDataRows.push([data]);
              } else {
                if (data) {
                  if (driversTableDataRows[index] === undefined) {
                    driversTableDataRows[index] = [];
                  }
                  driversTableDataRows[index].push(data);
                }
              }
            });
          });
      }

      if (headerRow.length && driversTableDataRows.length) {
        driversSlide.addText("Ranking Results", {
          x: 0.2,
          y: 1.07,
          h: 0.43,
          w: 3
        });

        driversSlide.addTable([headerRow, ...driversTableDataRows], {
          x: 0.3,
          y: 1.63,
          fontSize: 16,
          w: "90%",
          border: { type: "solid", color: "ffffff", pt: 1 }
        });
      }

      let showAll = false;
      //. Overall, show all subgroup
      if (topDrivers.length > 1 && topDrivers?.[0].runId === runId) {
        showAll = true;
      }

      topDrivers.forEach((slice, sliceIndex) => {
        if (!slice?.bestModel?.id) {
          return;
        }
        if ((showAll === true && sliceIndex > 0) || slice?.runId === runId) {
          const lineData = [];
          const modelList =
            scenarioStats.modelGroupSegments.length > 1
              ? scenarioStats.segmentTopModels[sliceIndex]
              : scenarioStats.segmentTopModels.slice(0, 10);

          slice.columnDrivers &&
            slice.columnDrivers.slice(0, 10).forEach((driver) => {
              const currentDriver = driver.field;

              const driverScores = modelList.slice(0, 10).map((model) => {
                const score = model.featureInfo.find((feature) => feature.label === currentDriver).score;

                return {
                  name: currentDriver,
                  model: model.label,
                  score: score
                };
              });
              lineData.push(driverScores);
            });
          const modelListTransposed = modelList.map((model) => {
            const currentModel = model.label;
            return slice.columnDrivers.slice(0, 10).map((driver) => {
              const currentDriver = driver.field;
              const feature = model.featureInfo.find((feature) => feature.label === currentDriver);
              return {
                name: currentModel,
                feature: currentDriver,
                score: feature.score
              };
            });
          });
          const topModelsData = [
            [
              {
                text: "Rank",
                options: { color: "ffffff", fill: { color: "512f77" } }
              },
              {
                text: "Model",
                options: { color: "ffffff", fill: { color: "512f77" } }
              },
              {
                text: "Impact",
                options: { color: "ffffff", fill: { color: "512f77" } }
              }
            ]
          ];

          const locale = navigator.language;

          modelList.forEach((model, index) => {
            topModelsData.push([
              {
                text: index + 1,
                options: {
                  color: "000000",
                  fill: { color: "c7bbd3" },
                  align: "right"
                }
              },
              {
                text: model.label,
                options: { fill: { color: "E6E6FC" } }
              },
              {
                text: "$" + Number(model.roi.toFixed(2)).toLocaleString(locale),
                options: { fill: { color: "E6E6FC" } }
              }
            ]);
          });

          if (lineData.length > 0) {
            const interpolateColor = (c0, c1, f) => {
              c0 = c0.match(/.{1,2}/g).map((oct) => parseInt(oct, 16) * (1 - f));
              c1 = c1.match(/.{1,2}/g).map((oct) => parseInt(oct, 16) * f);
              let ci = [0, 1, 2].map((i) => Math.min(Math.round(c0[i] + c1[i]), 255));
              return ci
                .reduce((a, v) => (a << 8) + v, 0)
                .toString(16)
                .padStart(6, "0");
            };

            const maxScore = Math.max(...lineData.map((driver) => Math.max(...driver.map((slice) => slice.score))));

            if (modelList && modelList.length > 0) {
              let driverModelComparisonSlide = pres.addSlide();
              driverModelComparisonSlide.addText("Ranking Results", {
                x: 0.3,
                y: 1.07,
                h: 0.43,
                w: 3
              });
              driverModelComparisonSlide.addText(
                `Scenario ${scenarioNum}: ${originalScenario.name} - ${slice.drilldownValue} Top Models`,
                {
                  x: "0%",
                  y: "0%",
                  w: "100%",
                  h: "15%",
                  color: "FFFFFF",
                  fill: { color: "512f77" },
                  fontSize: 24,
                  fontFace: "Raleway"
                }
              );

              driverModelComparisonSlide.addTable(topModelsData, {
                x: 0.4,
                y: 1.63,
                fontSize: 11,
                colW: [0.5, 3.5, 1.4]
              });
            }

            const pModels = (prepModels(getState(), id, slice?.runId) || []).slice(0, 10);

            if (pModels && pModels.length > 0) {
              let modelMetricsTable = pres.addSlide();
              modelMetricsTable.addText(
                `Scenario ${scenarioNum}: ${originalScenario.name} - ${slice.drilldownValue} Model Metrics`,
                {
                  x: "0%",
                  y: "0%",
                  w: "100%",
                  h: "15%",
                  color: "FFFFFF",
                  fill: { color: "512f77" },
                  fontSize: 24,
                  fontFace: "Raleway"
                }
              );

              let modelMetricData = isContinuous
                ? [
                    [
                      {
                        text: "Rank",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Model",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Impact",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "TPC",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Threshold",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Accuracy",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Precision",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Recall",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "AUC",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "F1 Score",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Log Loss",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "MCC",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      }
                    ]
                  ]
                : [
                    [
                      {
                        text: "Rank",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Model",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Impact",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "TPC",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Threshold",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Accuracy",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Precision",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Recall",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "AUC",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "F1 Score",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Log Loss",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "MCC",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Success",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      },
                      {
                        text: "Effort",
                        options: { color: "ffffff", fill: { color: "512f77" } }
                      }
                    ]
                  ];

              pModels.forEach((model, index) => {
                modelMetricData.push([
                  {
                    text: index + 1,
                    options: {
                      color: "000000",
                      fill: { color: "c7bbd3" },
                      align: "right"
                    }
                  },
                  {
                    text: model.label,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: "$" + model?.BusinessImpact,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.allPositives,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.threshold,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.accuracy,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.precision,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.recall,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.auc,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.f1_score,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.log_loss,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.MCC,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.revenue,
                    options: { fill: { color: "E6E6FC" } }
                  },
                  {
                    text: model?.cost,
                    options: { fill: { color: "E6E6FC" } }
                  }
                ]);
              });

              modelMetricsTable.addTable(modelMetricData, {
                x: 0,
                y: 0.8,
                fontSize: 8,
                colW: [0.4, 1.4, 1.2, 0.6, 1, 0.6, 0.6, 0.6, 0.6, 0.6, 0.6, 0.6, 0.6, 0.6]
              });
            }

            const heatMapData = modelListTransposed.map((model) => {
              const currentName = model[0].name;
              const tableRow = [{ text: currentName, options: { fontSize: 8, align: "right" } }];
              model.forEach((driver) => {
                const cellColor = interpolateColor("FFFFFF", "512F77", driver.score / maxScore);
                tableRow.push({
                  //text: Number(driver.score).toFixed(2) + "%",
                  text: "",
                  options: { fill: { color: cellColor } }
                });
              });
              return tableRow;
            });

            const columnLabelRow = [
              { text: "" },
              ...topDrivers[sliceIndex].columnDrivers.slice(0, 10).map((column) => ({
                text: column.field.replace("__c", ""),
                options: { fontSize: 8, rotate: 90 }
              }))
            ];

            heatMapData.push(columnLabelRow);

            let heatMapSlide = pres.addSlide();
            heatMapSlide.addText(
              `Scenario ${scenarioNum}: ${originalScenario.name} - ${slice.drilldownValue} Heat Map`,
              {
                x: "0%",
                y: "0%",
                w: "100%",
                h: "15%",
                color: "FFFFFF",
                fill: { color: "512f77" },
                fontSize: 24,
                fontFace: "Raleway"
              }
            );
            heatMapSlide.addTable(heatMapData, {
              x: 0.08,
              y: 0.88,
              w: 9.85,
              h: 4.75,
              color: "000000",
              fontSize: 8,
              valign: "center",
              align: "center",
              border: { pt: "1", color: "FFFFFF" },
              fontFace: "Calibri"
            });

            slice.columnDrivers.slice(0, 30).forEach((driver) => {
              const featureValues = runReducer.getModelFeatureInfo(
                getState(),
                slice.runId,
                slice.bestModel.id,
                driver.field
              ).values;
              const formattedFeatureValues = Object.keys(featureValues);
              const values = formattedFeatureValues.map((key) => featureValues[key]);
              const shortenedFeatureValues = formattedFeatureValues
                .map((feature) => feature.replace("more than", ">").replace("less than", "<"))
                .map((val) => (val.length > 30 ? val.slice(0, 30) + "..." : val));

              if (values && values.length > 0 && Math.max(...values) > 0) {
                const barInfo = [
                  {
                    name: driver.field,
                    labels: shortenedFeatureValues,
                    values: values
                  }
                ];
                const barColors = formattedFeatureValues.map((key) => (featureValues[key] >= 0 ? "7E69D7" : "0ea8bd"));
                const bestChoice = Object.keys(featureValues)
                  .find((key) => featureValues[key] === Math.max(...values))
                  .replace("more than", ">")
                  .replace("less than", "<");

                const worstChoice = Object.keys(featureValues)
                  .find((key) => featureValues[key] === Math.min(...values))
                  .replace("more than", ">")
                  .replace("less than", "<");

                let driverAnalysisSlide = pres.addSlide();
                driverAnalysisSlide.addText(
                  [
                    {
                      text: `How ${driver.field} Impacted the Model`,
                      options: {
                        breakLine: true,
                        fontSize: 24,
                        fontFace: "Raleway"
                      }
                    },
                    {
                      text: `Scenario ${scenarioNum}: ${originalScenario.name} - Subgroup: ${slice.drilldownValue} - Model: ${slice.bestModel.label}`,
                      options: { fontSize: 14, fontFace: "Raleway" }
                    }
                  ],
                  {
                    x: "0%",
                    y: "0%",
                    w: "100%",
                    h: "15%",
                    color: "FFFFFF",
                    fill: { color: "512f77" },
                    fontSize: 18
                  }
                );

                driverAnalysisSlide.addChart(pres.charts.BAR, barInfo, {
                  x: 0.15,
                  y: 1.0,
                  h: 4.2,
                  w: 6.8,
                  catAxisLabelPos: "low",
                  catAxisLineShow: false,
                  catAxisLabelFontFace: "Calibri",
                  valAxisLabelFontFace: "Calibri",
                  valAxisLineShow: false,
                  barDir: "bar",
                  chartColors: barColors
                });
                driverAnalysisSlide.addText(
                  `"${bestChoice}" is the most positive attribute and "${worstChoice}" is the most negative attribute for ${driver.field}`,
                  {
                    x: 7.1,
                    y: 1.6,
                    w: 2.8,
                    h: 3,
                    fontSize: 16,
                    fontFace: "Calibri"
                  }
                );
              }
            });
          }
        } else {
          return;
        }
      });
    };

    if (
      project &&
      project.settings &&
      project.settings.scenarioStats &&
      Object.keys(project.settings.scenarioStats).length > 0
    ) {
      Object.keys(project.settings.scenarioStats).forEach((scenario, index) =>
        generateStrategySlide(
          project.settings.scenarioStats[scenario],
          project.settings.originalScenarios[index],
          index + 1
        )
      );
    }

    let endSlide = pres.addSlide();
    endSlide.background = { color: "512f77" };
    endSlide.addImage({
      path: aibleSmallTransparentLogo,
      x: 8.8,
      y: 5.22,
      w: 1,
      h: 0.21,
      sizing: { type: "contain" }
    });
    endSlide.addImage({
      path: aibleCenterTransparentLogo,
      x: 3.5,
      y: 1.3,
      w: 3,
      h: 3,
      sizing: { type: "contain" }
    });
    const currYear = new Date().getFullYear();
    endSlide.addText(`© ${currYear ?? 2022}, Aible, Inc. All Rights Reserved`, {
      color: "FFFFFF",
      x: 3.8,
      y: 5.15,
      w: 2.4,
      h: 0.35,
      fontSize: 10
    });

    // pres.writeFile({
    //   fileName: `AIBLE Project ${id} - ${
    //     setup.parentLabel ? setup.parentLabel : setup.label
    //   }.pptx`,
    // });

    pres.writeFile({
      fileName: `AIBLE Project ${id}.pptx`
    });
  };
};

export function prepModels(state, projectId, runId) {
  const project = getProject(state, projectId);
  const setup = undefined;
  const models = runReducer.getTransformedSortedModels(state, runId);
  const unitRevenue = getUnitRevenue(state, projectId);

  const isContinuous = setupReducer.getIsContinuous(state, runId);

  let returnModels = [];
  let tempObj = {};

  const locale = navigator.language;

  for (let datum of models) {
    if (!datum?.isPlaceholder) {
      if (isContinuous) {
        tempObj.sum_over_predict = Number(datum?.metrics?.sum_over_predict?.toFixed(2)).toLocaleString(locale);
        tempObj.sum_under_predict = Number(datum?.metrics?.sum_under_predict?.toFixed(2)).toLocaleString(locale);
        tempObj.count_over_predict = Number(datum?.metrics?.count_over_predict?.toFixed(2)).toLocaleString(locale);
        tempObj.count_under_predict = Number(datum?.metrics?.count_under_predict?.toFixed(2)).toLocaleString(locale);
        tempObj.totalCost = Number(Math.abs(datum?.checkMax)).toLocaleString(locale);
        tempObj.mean_absolute_error = Number(datum?.metadata?.mean_absolute_error?.toFixed(5)).toLocaleString(locale);
        tempObj.mean_squared_error = Number(datum?.metadata?.mean_squared_error?.toFixed(5)).toLocaleString(locale);
        tempObj.r2_score = Number(datum.metadata?.r2_score?.toFixed(5)).toLocaleString(locale);
      } else {
        tempObj.accuracy = Number(datum?.maxIteration?.accuracy?.toFixed(5)).toLocaleString(locale);
        tempObj.precision = Number(datum?.maxIteration?.precision?.toFixed(5)).toLocaleString(locale);
        tempObj.recall = Number(datum?.maxIteration?.recall?.toFixed(5)).toLocaleString(locale);
        tempObj.MCC = Number(
          datum?.maxIteration?.confusionMatrix?.getMatthewsCorrelationCoefficient()?.toFixed(5)
        ).toLocaleString(locale);
        tempObj.threshold = Number(datum?.maxIteration?.confusionMatrix?.threshold).toString();
        tempObj.tp = Math.round(
          Number(datum?.maxIteration?.confusionMatrix?.tp ?? datum?.maxIteration?.truePositive)
        ).toLocaleString(locale);
        tempObj.fp = Math.round(
          Number(datum?.maxIteration?.confusionMatrix?.fp ?? datum?.maxIteration?.falsePositive)
        ).toLocaleString(locale);
        tempObj.fn = Math.round(
          Number(datum?.maxIteration?.confusionMatrix?.fn ?? datum?.maxIteration?.falseNegative)
        ).toLocaleString(locale);
        tempObj.tn = Math.round(
          Number(datum?.maxIteration?.confusionMatrix?.tn ?? datum?.maxIteration?.trueNegative)
        ).toLocaleString(locale);
        tempObj.allPositives = Math.round(
          Number(datum?.maxIteration?.confusionMatrix?.getAllPositive())
        ).toLocaleString(locale);
        tempObj.auc = Number(datum?.metadata?.auc?.toFixed(5)).toLocaleString(locale);
        tempObj.f1_score = Number(datum?.maxIteration?.confusionMatrix?.getF1Score().toFixed(5)).toLocaleString(locale);
        tempObj.log_loss = Number(datum?.metadata?.log_loss?.toFixed(5)).toLocaleString(locale);
        if (setup) {
          tempObj.BusinessImpact = Number(datum?.checkMax).toLocaleString(locale);
          const uRevenue = isNaN(unitRevenue)
            ? Number(setup.conversionGain) + Math.abs(Number(setup.conversionCost))
            : unitRevenue;
          tempObj.revenue = numeral(Number(datum?.maxIteration?.totalWins) * uRevenue).format("0.0a");
          tempObj.cost = numeral(
            Math.abs(
              (Number(datum?.maxIteration?.truePositive) + Number(datum?.maxIteration?.falsePositive)) *
                Number(setup.conversionCost)
            )
          ).format("0.0a");
        } else {
          tempObj.BusinessImpact =
            datum.maxIteration && !isNaN(datum?.maxIteration?.roi)
              ? Number(datum?.maxIteration?.roi)?.toLocaleString(locale)
              : Number(datum?.checkMax)?.toLocaleString(locale);
          tempObj.revenue = numeral(datum?.maxIteration?.revenue).format("0.0a");
          tempObj.cost = numeral(Math.abs(datum?.maxIteration?.cost)).format("0.0a");
        }
      }
    }

    tempObj.id = datum.id;
    tempObj.isFrontier = false; //fids.has(datum.id) ? "FRONTIER" : "";
    if (tempObj.isFrontier === "" && datum.id.includes("external-classifier-")) {
      tempObj.isFrontier = "EXTERNAL";
    }
    tempObj.label = datum.label;
    returnModels.push(tempObj);
    tempObj = {};
  }

  return returnModels;
}

export default projectReducer;
